export * from '@udecode/slate-plugins-core';
import { cloneDeep } from 'lodash';
import { Editor, Element, Node as Node$1, Range, Path, Point, Transforms, Text } from 'slate';
import React__default, { useRef, createElement, useDebugValue, useContext, forwardRef, useState, useMemo, useEffect, useCallback } from 'react';
import { ReactEditor, useEditor, useReadOnly, useSlate, useFocused, useSelected } from 'slate-react';
import useMergedRef from '@react-hook/merged-ref';
import Tippy from '@tippyjs/react';
import { mergeStyles, concatStyleSets } from '@uifabric/styling';
import { classNamesFunction, styled as styled$1, memoizeFunction } from '@uifabric/utilities';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useDrag, useDrop } from 'react-dnd';
import isHotkey from 'is-hotkey';
import { createPortal } from 'react-dom';
import { useTimeoutFn } from 'react-use';
import Prism$1, { languages, tokenize } from 'prismjs';
import { renderToStaticMarkup } from 'react-dom/server';
import { jsx } from 'slate-hyperscript';
import markdown from 'remark-parse';
import slate$1 from 'remark-slate';
import unified from 'unified';
import imageExtensions from 'image-extensions';

/**
 * Checks if `value` is classified as an `Array` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array, else `false`.
 * @example
 *
 * _.isArray([1, 2, 3]);
 * // => true
 *
 * _.isArray(document.body.children);
 * // => false
 *
 * _.isArray('abc');
 * // => false
 *
 * _.isArray(_.noop);
 * // => false
 */
var isArray = Array.isArray;

var isArray_1 = isArray;

/**
 * Casts `value` as an array if it's not one.
 *
 * @static
 * @memberOf _
 * @since 4.4.0
 * @category Lang
 * @param {*} value The value to inspect.
 * @returns {Array} Returns the cast array.
 * @example
 *
 * _.castArray(1);
 * // => [1]
 *
 * _.castArray({ 'a': 1 });
 * // => [{ 'a': 1 }]
 *
 * _.castArray('abc');
 * // => ['abc']
 *
 * _.castArray(null);
 * // => [null]
 *
 * _.castArray(undefined);
 * // => [undefined]
 *
 * _.castArray();
 * // => []
 *
 * var array = [1, 2, 3];
 * console.log(_.castArray(array) === array);
 * // => true
 */
function castArray() {
  if (!arguments.length) {
    return [];
  }
  var value = arguments[0];
  return isArray_1(value) ? value : [value];
}

var castArray_1 = castArray;

const withInlineVoid = ({
  plugins = [],
  inlineTypes = [],
  voidTypes = []
}) => editor => {
  const {
    isInline
  } = editor;
  const {
    isVoid
  } = editor;
  let allInlineTypes = [...inlineTypes];
  let allVoidTypes = [...voidTypes];
  plugins.forEach(plugin => {
    if (plugin.inlineTypes) {
      allInlineTypes = allInlineTypes.concat(castArray_1(plugin.inlineTypes));
    }

    if (plugin.voidTypes) {
      allVoidTypes = allVoidTypes.concat(castArray_1(plugin.voidTypes));
    }
  });

  editor.isInline = element => {
    return allInlineTypes.includes(element.type) ? true : isInline(element);
  };

  editor.isVoid = element => allVoidTypes.includes(element.type) ? true : isVoid(element);

  return editor;
};

/**
 * Get the block above a location (default: selection) by type.
 */

const getAboveByType = (editor, types, options = {}) => {
  types = castArray_1(types);
  return Editor.above(editor, Object.assign({
    match: n => types.includes(n.type)
  }, options));
};

/**
 * Get the block above a location (default: selection).
 * If not found, return the editor entry.
 */

const getBlockAbove = (editor, options = {}) => Editor.above(editor, Object.assign({
  match: n => Editor.isBlock(editor, n)
}, options)) || [editor, []];

const isAncestor = node => Element.isElement(node) || Editor.isEditor(node);

const getLastChild = (node, level) => {
  if (!(level + 1) || !isAncestor(node)) return node;
  const {
    children
  } = node;
  const lastNode = children[children.length - 1];
  return getLastChild(lastNode, level - 1);
};
/**
 * Get the last node at a given level.
 */


const getLastNode = (editor, level) => {
  const {
    children
  } = editor;
  const lastNode = children[children.length - 1];
  const [, lastPath] = Editor.last(editor, []);
  return [getLastChild(lastNode, level - 1), lastPath.slice(0, level + 1)];
};

/**
 * Get the next sibling nodes after a path.
 * @param ancestorEntry Ancestor of the sibling nodes
 * @param path Path of the reference node
 */
const getNextSiblingNodes = (ancestorEntry, path) => {
  const [ancestor, ancestorPath] = ancestorEntry;
  const leafIndex = path[ancestorPath.length];
  const siblings = [];

  if (leafIndex + 1 < ancestor.children.length) {
    for (let i = leafIndex + 1; i < ancestor.children.length; i++) {
      siblings.push(ancestor.children[i]);
    }
  }

  return siblings;
};

/**
 * Get the descendant node referred to by a specific path.
 * If the path is an empty array, it refers to the root node itself.
 * If the node is not found, return null.
 */

const getNode = (editor, path) => {
  try {
    return Node$1.get(editor, path);
  } catch (err) {
    return null;
  }
};

/**
 * Return {@link Editor.unhangRange} if `unhang` is true and if `at` (default: selection) is a range.
 */

const unhangRange = (editor, options = {}) => {
  const {
    at = editor.selection,
    voids,
    unhang = true
  } = options;

  if (Range.isRange(at) && unhang) {
    options.at = Editor.unhangRange(editor, at, {
      voids
    });
  }
};

const getNodes = (editor, options = {}) => {
  unhangRange(editor, options);
  return Editor.nodes(editor, options);
};

/**
 * Get editor node entries by id.
 */

const getNodesById = (editor, id) => {
  const ids = castArray_1(id);
  return [...Editor.nodes(editor, {
    mode: 'highest',
    match: n => {
      const nodeId = n.id;
      return !!nodeId && ids.includes(nodeId);
    },
    at: []
  })];
};

/**
 * Get the nodes with a type included in `types` at a location (default: selection).
 */

const getNodesByType = (editor, types, options = {}) => {
  types = castArray_1(types);
  return getNodes(editor, Object.assign({
    match: n => types.includes(n.type)
  }, options));
};

/**
 * A specialized version of `_.map` for arrays without support for iteratee
 * shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the new mapped array.
 */
function arrayMap(array, iteratee) {
  var index = -1,
      length = array == null ? 0 : array.length,
      result = Array(length);

  while (++index < length) {
    result[index] = iteratee(array[index], index, array);
  }
  return result;
}

var _arrayMap = arrayMap;

/**
 * Removes all key-value entries from the list cache.
 *
 * @private
 * @name clear
 * @memberOf ListCache
 */
function listCacheClear() {
  this.__data__ = [];
  this.size = 0;
}

var _listCacheClear = listCacheClear;

/**
 * Performs a
 * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * comparison between two values to determine if they are equivalent.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 * @example
 *
 * var object = { 'a': 1 };
 * var other = { 'a': 1 };
 *
 * _.eq(object, object);
 * // => true
 *
 * _.eq(object, other);
 * // => false
 *
 * _.eq('a', 'a');
 * // => true
 *
 * _.eq('a', Object('a'));
 * // => false
 *
 * _.eq(NaN, NaN);
 * // => true
 */
function eq(value, other) {
  return value === other || (value !== value && other !== other);
}

var eq_1 = eq;

/**
 * Gets the index at which the `key` is found in `array` of key-value pairs.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} key The key to search for.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function assocIndexOf(array, key) {
  var length = array.length;
  while (length--) {
    if (eq_1(array[length][0], key)) {
      return length;
    }
  }
  return -1;
}

var _assocIndexOf = assocIndexOf;

/** Used for built-in method references. */
var arrayProto = Array.prototype;

/** Built-in value references. */
var splice = arrayProto.splice;

/**
 * Removes `key` and its value from the list cache.
 *
 * @private
 * @name delete
 * @memberOf ListCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function listCacheDelete(key) {
  var data = this.__data__,
      index = _assocIndexOf(data, key);

  if (index < 0) {
    return false;
  }
  var lastIndex = data.length - 1;
  if (index == lastIndex) {
    data.pop();
  } else {
    splice.call(data, index, 1);
  }
  --this.size;
  return true;
}

var _listCacheDelete = listCacheDelete;

/**
 * Gets the list cache value for `key`.
 *
 * @private
 * @name get
 * @memberOf ListCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function listCacheGet(key) {
  var data = this.__data__,
      index = _assocIndexOf(data, key);

  return index < 0 ? undefined : data[index][1];
}

var _listCacheGet = listCacheGet;

/**
 * Checks if a list cache value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf ListCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function listCacheHas(key) {
  return _assocIndexOf(this.__data__, key) > -1;
}

var _listCacheHas = listCacheHas;

/**
 * Sets the list cache `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf ListCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the list cache instance.
 */
function listCacheSet(key, value) {
  var data = this.__data__,
      index = _assocIndexOf(data, key);

  if (index < 0) {
    ++this.size;
    data.push([key, value]);
  } else {
    data[index][1] = value;
  }
  return this;
}

var _listCacheSet = listCacheSet;

/**
 * Creates an list cache object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function ListCache(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `ListCache`.
ListCache.prototype.clear = _listCacheClear;
ListCache.prototype['delete'] = _listCacheDelete;
ListCache.prototype.get = _listCacheGet;
ListCache.prototype.has = _listCacheHas;
ListCache.prototype.set = _listCacheSet;

var _ListCache = ListCache;

/**
 * Removes all key-value entries from the stack.
 *
 * @private
 * @name clear
 * @memberOf Stack
 */
function stackClear() {
  this.__data__ = new _ListCache;
  this.size = 0;
}

var _stackClear = stackClear;

/**
 * Removes `key` and its value from the stack.
 *
 * @private
 * @name delete
 * @memberOf Stack
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function stackDelete(key) {
  var data = this.__data__,
      result = data['delete'](key);

  this.size = data.size;
  return result;
}

var _stackDelete = stackDelete;

/**
 * Gets the stack value for `key`.
 *
 * @private
 * @name get
 * @memberOf Stack
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function stackGet(key) {
  return this.__data__.get(key);
}

var _stackGet = stackGet;

/**
 * Checks if a stack value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Stack
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function stackHas(key) {
  return this.__data__.has(key);
}

var _stackHas = stackHas;

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

/** Detect free variable `global` from Node.js. */
var freeGlobal = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

var _freeGlobal = freeGlobal;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root = _freeGlobal || freeSelf || Function('return this')();

var _root = root;

/** Built-in value references. */
var Symbol$1 = _root.Symbol;

var _Symbol = Symbol$1;

/** Used for built-in method references. */
var objectProto = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty = objectProto.hasOwnProperty;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString = objectProto.toString;

/** Built-in value references. */
var symToStringTag = _Symbol ? _Symbol.toStringTag : undefined;

/**
 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the raw `toStringTag`.
 */
function getRawTag(value) {
  var isOwn = hasOwnProperty.call(value, symToStringTag),
      tag = value[symToStringTag];

  try {
    value[symToStringTag] = undefined;
    var unmasked = true;
  } catch (e) {}

  var result = nativeObjectToString.call(value);
  if (unmasked) {
    if (isOwn) {
      value[symToStringTag] = tag;
    } else {
      delete value[symToStringTag];
    }
  }
  return result;
}

var _getRawTag = getRawTag;

/** Used for built-in method references. */
var objectProto$1 = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString$1 = objectProto$1.toString;

/**
 * Converts `value` to a string using `Object.prototype.toString`.
 *
 * @private
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 */
function objectToString(value) {
  return nativeObjectToString$1.call(value);
}

var _objectToString = objectToString;

/** `Object#toString` result references. */
var nullTag = '[object Null]',
    undefinedTag = '[object Undefined]';

/** Built-in value references. */
var symToStringTag$1 = _Symbol ? _Symbol.toStringTag : undefined;

/**
 * The base implementation of `getTag` without fallbacks for buggy environments.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
function baseGetTag(value) {
  if (value == null) {
    return value === undefined ? undefinedTag : nullTag;
  }
  return (symToStringTag$1 && symToStringTag$1 in Object(value))
    ? _getRawTag(value)
    : _objectToString(value);
}

var _baseGetTag = baseGetTag;

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */
function isObject(value) {
  var type = typeof value;
  return value != null && (type == 'object' || type == 'function');
}

var isObject_1 = isObject;

/** `Object#toString` result references. */
var asyncTag = '[object AsyncFunction]',
    funcTag = '[object Function]',
    genTag = '[object GeneratorFunction]',
    proxyTag = '[object Proxy]';

/**
 * Checks if `value` is classified as a `Function` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a function, else `false`.
 * @example
 *
 * _.isFunction(_);
 * // => true
 *
 * _.isFunction(/abc/);
 * // => false
 */
function isFunction(value) {
  if (!isObject_1(value)) {
    return false;
  }
  // The use of `Object#toString` avoids issues with the `typeof` operator
  // in Safari 9 which returns 'object' for typed arrays and other constructors.
  var tag = _baseGetTag(value);
  return tag == funcTag || tag == genTag || tag == asyncTag || tag == proxyTag;
}

var isFunction_1 = isFunction;

/** Used to detect overreaching core-js shims. */
var coreJsData = _root['__core-js_shared__'];

var _coreJsData = coreJsData;

/** Used to detect methods masquerading as native. */
var maskSrcKey = (function() {
  var uid = /[^.]+$/.exec(_coreJsData && _coreJsData.keys && _coreJsData.keys.IE_PROTO || '');
  return uid ? ('Symbol(src)_1.' + uid) : '';
}());

/**
 * Checks if `func` has its source masked.
 *
 * @private
 * @param {Function} func The function to check.
 * @returns {boolean} Returns `true` if `func` is masked, else `false`.
 */
function isMasked(func) {
  return !!maskSrcKey && (maskSrcKey in func);
}

var _isMasked = isMasked;

/** Used for built-in method references. */
var funcProto = Function.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString = funcProto.toString;

/**
 * Converts `func` to its source code.
 *
 * @private
 * @param {Function} func The function to convert.
 * @returns {string} Returns the source code.
 */
function toSource(func) {
  if (func != null) {
    try {
      return funcToString.call(func);
    } catch (e) {}
    try {
      return (func + '');
    } catch (e) {}
  }
  return '';
}

var _toSource = toSource;

/**
 * Used to match `RegExp`
 * [syntax characters](http://ecma-international.org/ecma-262/7.0/#sec-patterns).
 */
var reRegExpChar = /[\\^$.*+?()[\]{}|]/g;

/** Used to detect host constructors (Safari). */
var reIsHostCtor = /^\[object .+?Constructor\]$/;

/** Used for built-in method references. */
var funcProto$1 = Function.prototype,
    objectProto$2 = Object.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString$1 = funcProto$1.toString;

/** Used to check objects for own properties. */
var hasOwnProperty$1 = objectProto$2.hasOwnProperty;

/** Used to detect if a method is native. */
var reIsNative = RegExp('^' +
  funcToString$1.call(hasOwnProperty$1).replace(reRegExpChar, '\\$&')
  .replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$'
);

/**
 * The base implementation of `_.isNative` without bad shim checks.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a native function,
 *  else `false`.
 */
function baseIsNative(value) {
  if (!isObject_1(value) || _isMasked(value)) {
    return false;
  }
  var pattern = isFunction_1(value) ? reIsNative : reIsHostCtor;
  return pattern.test(_toSource(value));
}

var _baseIsNative = baseIsNative;

/**
 * Gets the value at `key` of `object`.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {string} key The key of the property to get.
 * @returns {*} Returns the property value.
 */
function getValue(object, key) {
  return object == null ? undefined : object[key];
}

var _getValue = getValue;

/**
 * Gets the native function at `key` of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {string} key The key of the method to get.
 * @returns {*} Returns the function if it's native, else `undefined`.
 */
function getNative(object, key) {
  var value = _getValue(object, key);
  return _baseIsNative(value) ? value : undefined;
}

var _getNative = getNative;

/* Built-in method references that are verified to be native. */
var Map$1 = _getNative(_root, 'Map');

var _Map = Map$1;

/* Built-in method references that are verified to be native. */
var nativeCreate = _getNative(Object, 'create');

var _nativeCreate = nativeCreate;

/**
 * Removes all key-value entries from the hash.
 *
 * @private
 * @name clear
 * @memberOf Hash
 */
function hashClear() {
  this.__data__ = _nativeCreate ? _nativeCreate(null) : {};
  this.size = 0;
}

var _hashClear = hashClear;

/**
 * Removes `key` and its value from the hash.
 *
 * @private
 * @name delete
 * @memberOf Hash
 * @param {Object} hash The hash to modify.
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function hashDelete(key) {
  var result = this.has(key) && delete this.__data__[key];
  this.size -= result ? 1 : 0;
  return result;
}

var _hashDelete = hashDelete;

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED = '__lodash_hash_undefined__';

/** Used for built-in method references. */
var objectProto$3 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$2 = objectProto$3.hasOwnProperty;

/**
 * Gets the hash value for `key`.
 *
 * @private
 * @name get
 * @memberOf Hash
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function hashGet(key) {
  var data = this.__data__;
  if (_nativeCreate) {
    var result = data[key];
    return result === HASH_UNDEFINED ? undefined : result;
  }
  return hasOwnProperty$2.call(data, key) ? data[key] : undefined;
}

var _hashGet = hashGet;

/** Used for built-in method references. */
var objectProto$4 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$3 = objectProto$4.hasOwnProperty;

/**
 * Checks if a hash value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Hash
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function hashHas(key) {
  var data = this.__data__;
  return _nativeCreate ? (data[key] !== undefined) : hasOwnProperty$3.call(data, key);
}

var _hashHas = hashHas;

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED$1 = '__lodash_hash_undefined__';

/**
 * Sets the hash `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Hash
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the hash instance.
 */
function hashSet(key, value) {
  var data = this.__data__;
  this.size += this.has(key) ? 0 : 1;
  data[key] = (_nativeCreate && value === undefined) ? HASH_UNDEFINED$1 : value;
  return this;
}

var _hashSet = hashSet;

/**
 * Creates a hash object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Hash(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `Hash`.
Hash.prototype.clear = _hashClear;
Hash.prototype['delete'] = _hashDelete;
Hash.prototype.get = _hashGet;
Hash.prototype.has = _hashHas;
Hash.prototype.set = _hashSet;

var _Hash = Hash;

/**
 * Removes all key-value entries from the map.
 *
 * @private
 * @name clear
 * @memberOf MapCache
 */
function mapCacheClear() {
  this.size = 0;
  this.__data__ = {
    'hash': new _Hash,
    'map': new (_Map || _ListCache),
    'string': new _Hash
  };
}

var _mapCacheClear = mapCacheClear;

/**
 * Checks if `value` is suitable for use as unique object key.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is suitable, else `false`.
 */
function isKeyable(value) {
  var type = typeof value;
  return (type == 'string' || type == 'number' || type == 'symbol' || type == 'boolean')
    ? (value !== '__proto__')
    : (value === null);
}

var _isKeyable = isKeyable;

/**
 * Gets the data for `map`.
 *
 * @private
 * @param {Object} map The map to query.
 * @param {string} key The reference key.
 * @returns {*} Returns the map data.
 */
function getMapData(map, key) {
  var data = map.__data__;
  return _isKeyable(key)
    ? data[typeof key == 'string' ? 'string' : 'hash']
    : data.map;
}

var _getMapData = getMapData;

/**
 * Removes `key` and its value from the map.
 *
 * @private
 * @name delete
 * @memberOf MapCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function mapCacheDelete(key) {
  var result = _getMapData(this, key)['delete'](key);
  this.size -= result ? 1 : 0;
  return result;
}

var _mapCacheDelete = mapCacheDelete;

/**
 * Gets the map value for `key`.
 *
 * @private
 * @name get
 * @memberOf MapCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function mapCacheGet(key) {
  return _getMapData(this, key).get(key);
}

var _mapCacheGet = mapCacheGet;

/**
 * Checks if a map value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf MapCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function mapCacheHas(key) {
  return _getMapData(this, key).has(key);
}

var _mapCacheHas = mapCacheHas;

/**
 * Sets the map `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf MapCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the map cache instance.
 */
function mapCacheSet(key, value) {
  var data = _getMapData(this, key),
      size = data.size;

  data.set(key, value);
  this.size += data.size == size ? 0 : 1;
  return this;
}

var _mapCacheSet = mapCacheSet;

/**
 * Creates a map cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function MapCache(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `MapCache`.
MapCache.prototype.clear = _mapCacheClear;
MapCache.prototype['delete'] = _mapCacheDelete;
MapCache.prototype.get = _mapCacheGet;
MapCache.prototype.has = _mapCacheHas;
MapCache.prototype.set = _mapCacheSet;

var _MapCache = MapCache;

/** Used as the size to enable large array optimizations. */
var LARGE_ARRAY_SIZE = 200;

/**
 * Sets the stack `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Stack
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the stack cache instance.
 */
function stackSet(key, value) {
  var data = this.__data__;
  if (data instanceof _ListCache) {
    var pairs = data.__data__;
    if (!_Map || (pairs.length < LARGE_ARRAY_SIZE - 1)) {
      pairs.push([key, value]);
      this.size = ++data.size;
      return this;
    }
    data = this.__data__ = new _MapCache(pairs);
  }
  data.set(key, value);
  this.size = data.size;
  return this;
}

var _stackSet = stackSet;

/**
 * Creates a stack cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Stack(entries) {
  var data = this.__data__ = new _ListCache(entries);
  this.size = data.size;
}

// Add methods to `Stack`.
Stack.prototype.clear = _stackClear;
Stack.prototype['delete'] = _stackDelete;
Stack.prototype.get = _stackGet;
Stack.prototype.has = _stackHas;
Stack.prototype.set = _stackSet;

var _Stack = Stack;

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED$2 = '__lodash_hash_undefined__';

/**
 * Adds `value` to the array cache.
 *
 * @private
 * @name add
 * @memberOf SetCache
 * @alias push
 * @param {*} value The value to cache.
 * @returns {Object} Returns the cache instance.
 */
function setCacheAdd(value) {
  this.__data__.set(value, HASH_UNDEFINED$2);
  return this;
}

var _setCacheAdd = setCacheAdd;

/**
 * Checks if `value` is in the array cache.
 *
 * @private
 * @name has
 * @memberOf SetCache
 * @param {*} value The value to search for.
 * @returns {number} Returns `true` if `value` is found, else `false`.
 */
function setCacheHas(value) {
  return this.__data__.has(value);
}

var _setCacheHas = setCacheHas;

/**
 *
 * Creates an array cache object to store unique values.
 *
 * @private
 * @constructor
 * @param {Array} [values] The values to cache.
 */
function SetCache(values) {
  var index = -1,
      length = values == null ? 0 : values.length;

  this.__data__ = new _MapCache;
  while (++index < length) {
    this.add(values[index]);
  }
}

// Add methods to `SetCache`.
SetCache.prototype.add = SetCache.prototype.push = _setCacheAdd;
SetCache.prototype.has = _setCacheHas;

var _SetCache = SetCache;

/**
 * A specialized version of `_.some` for arrays without support for iteratee
 * shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {boolean} Returns `true` if any element passes the predicate check,
 *  else `false`.
 */
function arraySome(array, predicate) {
  var index = -1,
      length = array == null ? 0 : array.length;

  while (++index < length) {
    if (predicate(array[index], index, array)) {
      return true;
    }
  }
  return false;
}

var _arraySome = arraySome;

/**
 * Checks if a `cache` value for `key` exists.
 *
 * @private
 * @param {Object} cache The cache to query.
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function cacheHas(cache, key) {
  return cache.has(key);
}

var _cacheHas = cacheHas;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG = 1,
    COMPARE_UNORDERED_FLAG = 2;

/**
 * A specialized version of `baseIsEqualDeep` for arrays with support for
 * partial deep comparisons.
 *
 * @private
 * @param {Array} array The array to compare.
 * @param {Array} other The other array to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `array` and `other` objects.
 * @returns {boolean} Returns `true` if the arrays are equivalent, else `false`.
 */
function equalArrays(array, other, bitmask, customizer, equalFunc, stack) {
  var isPartial = bitmask & COMPARE_PARTIAL_FLAG,
      arrLength = array.length,
      othLength = other.length;

  if (arrLength != othLength && !(isPartial && othLength > arrLength)) {
    return false;
  }
  // Assume cyclic values are equal.
  var stacked = stack.get(array);
  if (stacked && stack.get(other)) {
    return stacked == other;
  }
  var index = -1,
      result = true,
      seen = (bitmask & COMPARE_UNORDERED_FLAG) ? new _SetCache : undefined;

  stack.set(array, other);
  stack.set(other, array);

  // Ignore non-index properties.
  while (++index < arrLength) {
    var arrValue = array[index],
        othValue = other[index];

    if (customizer) {
      var compared = isPartial
        ? customizer(othValue, arrValue, index, other, array, stack)
        : customizer(arrValue, othValue, index, array, other, stack);
    }
    if (compared !== undefined) {
      if (compared) {
        continue;
      }
      result = false;
      break;
    }
    // Recursively compare arrays (susceptible to call stack limits).
    if (seen) {
      if (!_arraySome(other, function(othValue, othIndex) {
            if (!_cacheHas(seen, othIndex) &&
                (arrValue === othValue || equalFunc(arrValue, othValue, bitmask, customizer, stack))) {
              return seen.push(othIndex);
            }
          })) {
        result = false;
        break;
      }
    } else if (!(
          arrValue === othValue ||
            equalFunc(arrValue, othValue, bitmask, customizer, stack)
        )) {
      result = false;
      break;
    }
  }
  stack['delete'](array);
  stack['delete'](other);
  return result;
}

var _equalArrays = equalArrays;

/** Built-in value references. */
var Uint8Array = _root.Uint8Array;

var _Uint8Array = Uint8Array;

/**
 * Converts `map` to its key-value pairs.
 *
 * @private
 * @param {Object} map The map to convert.
 * @returns {Array} Returns the key-value pairs.
 */
function mapToArray(map) {
  var index = -1,
      result = Array(map.size);

  map.forEach(function(value, key) {
    result[++index] = [key, value];
  });
  return result;
}

var _mapToArray = mapToArray;

/**
 * Converts `set` to an array of its values.
 *
 * @private
 * @param {Object} set The set to convert.
 * @returns {Array} Returns the values.
 */
function setToArray(set) {
  var index = -1,
      result = Array(set.size);

  set.forEach(function(value) {
    result[++index] = value;
  });
  return result;
}

var _setToArray = setToArray;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$1 = 1,
    COMPARE_UNORDERED_FLAG$1 = 2;

/** `Object#toString` result references. */
var boolTag = '[object Boolean]',
    dateTag = '[object Date]',
    errorTag = '[object Error]',
    mapTag = '[object Map]',
    numberTag = '[object Number]',
    regexpTag = '[object RegExp]',
    setTag = '[object Set]',
    stringTag = '[object String]',
    symbolTag = '[object Symbol]';

var arrayBufferTag = '[object ArrayBuffer]',
    dataViewTag = '[object DataView]';

/** Used to convert symbols to primitives and strings. */
var symbolProto = _Symbol ? _Symbol.prototype : undefined,
    symbolValueOf = symbolProto ? symbolProto.valueOf : undefined;

/**
 * A specialized version of `baseIsEqualDeep` for comparing objects of
 * the same `toStringTag`.
 *
 * **Note:** This function only supports comparing values with tags of
 * `Boolean`, `Date`, `Error`, `Number`, `RegExp`, or `String`.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {string} tag The `toStringTag` of the objects to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */
function equalByTag(object, other, tag, bitmask, customizer, equalFunc, stack) {
  switch (tag) {
    case dataViewTag:
      if ((object.byteLength != other.byteLength) ||
          (object.byteOffset != other.byteOffset)) {
        return false;
      }
      object = object.buffer;
      other = other.buffer;

    case arrayBufferTag:
      if ((object.byteLength != other.byteLength) ||
          !equalFunc(new _Uint8Array(object), new _Uint8Array(other))) {
        return false;
      }
      return true;

    case boolTag:
    case dateTag:
    case numberTag:
      // Coerce booleans to `1` or `0` and dates to milliseconds.
      // Invalid dates are coerced to `NaN`.
      return eq_1(+object, +other);

    case errorTag:
      return object.name == other.name && object.message == other.message;

    case regexpTag:
    case stringTag:
      // Coerce regexes to strings and treat strings, primitives and objects,
      // as equal. See http://www.ecma-international.org/ecma-262/7.0/#sec-regexp.prototype.tostring
      // for more details.
      return object == (other + '');

    case mapTag:
      var convert = _mapToArray;

    case setTag:
      var isPartial = bitmask & COMPARE_PARTIAL_FLAG$1;
      convert || (convert = _setToArray);

      if (object.size != other.size && !isPartial) {
        return false;
      }
      // Assume cyclic values are equal.
      var stacked = stack.get(object);
      if (stacked) {
        return stacked == other;
      }
      bitmask |= COMPARE_UNORDERED_FLAG$1;

      // Recursively compare objects (susceptible to call stack limits).
      stack.set(object, other);
      var result = _equalArrays(convert(object), convert(other), bitmask, customizer, equalFunc, stack);
      stack['delete'](object);
      return result;

    case symbolTag:
      if (symbolValueOf) {
        return symbolValueOf.call(object) == symbolValueOf.call(other);
      }
  }
  return false;
}

var _equalByTag = equalByTag;

/**
 * Appends the elements of `values` to `array`.
 *
 * @private
 * @param {Array} array The array to modify.
 * @param {Array} values The values to append.
 * @returns {Array} Returns `array`.
 */
function arrayPush(array, values) {
  var index = -1,
      length = values.length,
      offset = array.length;

  while (++index < length) {
    array[offset + index] = values[index];
  }
  return array;
}

var _arrayPush = arrayPush;

/**
 * The base implementation of `getAllKeys` and `getAllKeysIn` which uses
 * `keysFunc` and `symbolsFunc` to get the enumerable property names and
 * symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Function} keysFunc The function to get the keys of `object`.
 * @param {Function} symbolsFunc The function to get the symbols of `object`.
 * @returns {Array} Returns the array of property names and symbols.
 */
function baseGetAllKeys(object, keysFunc, symbolsFunc) {
  var result = keysFunc(object);
  return isArray_1(object) ? result : _arrayPush(result, symbolsFunc(object));
}

var _baseGetAllKeys = baseGetAllKeys;

/**
 * A specialized version of `_.filter` for arrays without support for
 * iteratee shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {Array} Returns the new filtered array.
 */
function arrayFilter(array, predicate) {
  var index = -1,
      length = array == null ? 0 : array.length,
      resIndex = 0,
      result = [];

  while (++index < length) {
    var value = array[index];
    if (predicate(value, index, array)) {
      result[resIndex++] = value;
    }
  }
  return result;
}

var _arrayFilter = arrayFilter;

/**
 * This method returns a new empty array.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {Array} Returns the new empty array.
 * @example
 *
 * var arrays = _.times(2, _.stubArray);
 *
 * console.log(arrays);
 * // => [[], []]
 *
 * console.log(arrays[0] === arrays[1]);
 * // => false
 */
function stubArray() {
  return [];
}

var stubArray_1 = stubArray;

/** Used for built-in method references. */
var objectProto$5 = Object.prototype;

/** Built-in value references. */
var propertyIsEnumerable = objectProto$5.propertyIsEnumerable;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeGetSymbols = Object.getOwnPropertySymbols;

/**
 * Creates an array of the own enumerable symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of symbols.
 */
var getSymbols = !nativeGetSymbols ? stubArray_1 : function(object) {
  if (object == null) {
    return [];
  }
  object = Object(object);
  return _arrayFilter(nativeGetSymbols(object), function(symbol) {
    return propertyIsEnumerable.call(object, symbol);
  });
};

var _getSymbols = getSymbols;

/**
 * The base implementation of `_.times` without support for iteratee shorthands
 * or max array length checks.
 *
 * @private
 * @param {number} n The number of times to invoke `iteratee`.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the array of results.
 */
function baseTimes(n, iteratee) {
  var index = -1,
      result = Array(n);

  while (++index < n) {
    result[index] = iteratee(index);
  }
  return result;
}

var _baseTimes = baseTimes;

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */
function isObjectLike(value) {
  return value != null && typeof value == 'object';
}

var isObjectLike_1 = isObjectLike;

/** `Object#toString` result references. */
var argsTag = '[object Arguments]';

/**
 * The base implementation of `_.isArguments`.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 */
function baseIsArguments(value) {
  return isObjectLike_1(value) && _baseGetTag(value) == argsTag;
}

var _baseIsArguments = baseIsArguments;

/** Used for built-in method references. */
var objectProto$6 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$4 = objectProto$6.hasOwnProperty;

/** Built-in value references. */
var propertyIsEnumerable$1 = objectProto$6.propertyIsEnumerable;

/**
 * Checks if `value` is likely an `arguments` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 *  else `false`.
 * @example
 *
 * _.isArguments(function() { return arguments; }());
 * // => true
 *
 * _.isArguments([1, 2, 3]);
 * // => false
 */
var isArguments = _baseIsArguments(function() { return arguments; }()) ? _baseIsArguments : function(value) {
  return isObjectLike_1(value) && hasOwnProperty$4.call(value, 'callee') &&
    !propertyIsEnumerable$1.call(value, 'callee');
};

var isArguments_1 = isArguments;

/**
 * This method returns `false`.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {boolean} Returns `false`.
 * @example
 *
 * _.times(2, _.stubFalse);
 * // => [false, false]
 */
function stubFalse() {
  return false;
}

var stubFalse_1 = stubFalse;

var isBuffer_1 = createCommonjsModule(function (module, exports) {
/** Detect free variable `exports`. */
var freeExports =  exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports = freeModule && freeModule.exports === freeExports;

/** Built-in value references. */
var Buffer = moduleExports ? _root.Buffer : undefined;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeIsBuffer = Buffer ? Buffer.isBuffer : undefined;

/**
 * Checks if `value` is a buffer.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a buffer, else `false`.
 * @example
 *
 * _.isBuffer(new Buffer(2));
 * // => true
 *
 * _.isBuffer(new Uint8Array(2));
 * // => false
 */
var isBuffer = nativeIsBuffer || stubFalse_1;

module.exports = isBuffer;
});

/** Used as references for various `Number` constants. */
var MAX_SAFE_INTEGER = 9007199254740991;

/** Used to detect unsigned integer values. */
var reIsUint = /^(?:0|[1-9]\d*)$/;

/**
 * Checks if `value` is a valid array-like index.
 *
 * @private
 * @param {*} value The value to check.
 * @param {number} [length=MAX_SAFE_INTEGER] The upper bounds of a valid index.
 * @returns {boolean} Returns `true` if `value` is a valid index, else `false`.
 */
function isIndex(value, length) {
  var type = typeof value;
  length = length == null ? MAX_SAFE_INTEGER : length;

  return !!length &&
    (type == 'number' ||
      (type != 'symbol' && reIsUint.test(value))) &&
        (value > -1 && value % 1 == 0 && value < length);
}

var _isIndex = isIndex;

/** Used as references for various `Number` constants. */
var MAX_SAFE_INTEGER$1 = 9007199254740991;

/**
 * Checks if `value` is a valid array-like length.
 *
 * **Note:** This method is loosely based on
 * [`ToLength`](http://ecma-international.org/ecma-262/7.0/#sec-tolength).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a valid length, else `false`.
 * @example
 *
 * _.isLength(3);
 * // => true
 *
 * _.isLength(Number.MIN_VALUE);
 * // => false
 *
 * _.isLength(Infinity);
 * // => false
 *
 * _.isLength('3');
 * // => false
 */
function isLength(value) {
  return typeof value == 'number' &&
    value > -1 && value % 1 == 0 && value <= MAX_SAFE_INTEGER$1;
}

var isLength_1 = isLength;

/** `Object#toString` result references. */
var argsTag$1 = '[object Arguments]',
    arrayTag = '[object Array]',
    boolTag$1 = '[object Boolean]',
    dateTag$1 = '[object Date]',
    errorTag$1 = '[object Error]',
    funcTag$1 = '[object Function]',
    mapTag$1 = '[object Map]',
    numberTag$1 = '[object Number]',
    objectTag = '[object Object]',
    regexpTag$1 = '[object RegExp]',
    setTag$1 = '[object Set]',
    stringTag$1 = '[object String]',
    weakMapTag = '[object WeakMap]';

var arrayBufferTag$1 = '[object ArrayBuffer]',
    dataViewTag$1 = '[object DataView]',
    float32Tag = '[object Float32Array]',
    float64Tag = '[object Float64Array]',
    int8Tag = '[object Int8Array]',
    int16Tag = '[object Int16Array]',
    int32Tag = '[object Int32Array]',
    uint8Tag = '[object Uint8Array]',
    uint8ClampedTag = '[object Uint8ClampedArray]',
    uint16Tag = '[object Uint16Array]',
    uint32Tag = '[object Uint32Array]';

/** Used to identify `toStringTag` values of typed arrays. */
var typedArrayTags = {};
typedArrayTags[float32Tag] = typedArrayTags[float64Tag] =
typedArrayTags[int8Tag] = typedArrayTags[int16Tag] =
typedArrayTags[int32Tag] = typedArrayTags[uint8Tag] =
typedArrayTags[uint8ClampedTag] = typedArrayTags[uint16Tag] =
typedArrayTags[uint32Tag] = true;
typedArrayTags[argsTag$1] = typedArrayTags[arrayTag] =
typedArrayTags[arrayBufferTag$1] = typedArrayTags[boolTag$1] =
typedArrayTags[dataViewTag$1] = typedArrayTags[dateTag$1] =
typedArrayTags[errorTag$1] = typedArrayTags[funcTag$1] =
typedArrayTags[mapTag$1] = typedArrayTags[numberTag$1] =
typedArrayTags[objectTag] = typedArrayTags[regexpTag$1] =
typedArrayTags[setTag$1] = typedArrayTags[stringTag$1] =
typedArrayTags[weakMapTag] = false;

/**
 * The base implementation of `_.isTypedArray` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 */
function baseIsTypedArray(value) {
  return isObjectLike_1(value) &&
    isLength_1(value.length) && !!typedArrayTags[_baseGetTag(value)];
}

var _baseIsTypedArray = baseIsTypedArray;

/**
 * The base implementation of `_.unary` without support for storing metadata.
 *
 * @private
 * @param {Function} func The function to cap arguments for.
 * @returns {Function} Returns the new capped function.
 */
function baseUnary(func) {
  return function(value) {
    return func(value);
  };
}

var _baseUnary = baseUnary;

var _nodeUtil = createCommonjsModule(function (module, exports) {
/** Detect free variable `exports`. */
var freeExports =  exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports = freeModule && freeModule.exports === freeExports;

/** Detect free variable `process` from Node.js. */
var freeProcess = moduleExports && _freeGlobal.process;

/** Used to access faster Node.js helpers. */
var nodeUtil = (function() {
  try {
    // Use `util.types` for Node.js 10+.
    var types = freeModule && freeModule.require && freeModule.require('util').types;

    if (types) {
      return types;
    }

    // Legacy `process.binding('util')` for Node.js < 10.
    return freeProcess && freeProcess.binding && freeProcess.binding('util');
  } catch (e) {}
}());

module.exports = nodeUtil;
});

/* Node.js helper references. */
var nodeIsTypedArray = _nodeUtil && _nodeUtil.isTypedArray;

/**
 * Checks if `value` is classified as a typed array.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 * @example
 *
 * _.isTypedArray(new Uint8Array);
 * // => true
 *
 * _.isTypedArray([]);
 * // => false
 */
var isTypedArray = nodeIsTypedArray ? _baseUnary(nodeIsTypedArray) : _baseIsTypedArray;

var isTypedArray_1 = isTypedArray;

/** Used for built-in method references. */
var objectProto$7 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$5 = objectProto$7.hasOwnProperty;

/**
 * Creates an array of the enumerable property names of the array-like `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @param {boolean} inherited Specify returning inherited property names.
 * @returns {Array} Returns the array of property names.
 */
function arrayLikeKeys(value, inherited) {
  var isArr = isArray_1(value),
      isArg = !isArr && isArguments_1(value),
      isBuff = !isArr && !isArg && isBuffer_1(value),
      isType = !isArr && !isArg && !isBuff && isTypedArray_1(value),
      skipIndexes = isArr || isArg || isBuff || isType,
      result = skipIndexes ? _baseTimes(value.length, String) : [],
      length = result.length;

  for (var key in value) {
    if ((inherited || hasOwnProperty$5.call(value, key)) &&
        !(skipIndexes && (
           // Safari 9 has enumerable `arguments.length` in strict mode.
           key == 'length' ||
           // Node.js 0.10 has enumerable non-index properties on buffers.
           (isBuff && (key == 'offset' || key == 'parent')) ||
           // PhantomJS 2 has enumerable non-index properties on typed arrays.
           (isType && (key == 'buffer' || key == 'byteLength' || key == 'byteOffset')) ||
           // Skip index properties.
           _isIndex(key, length)
        ))) {
      result.push(key);
    }
  }
  return result;
}

var _arrayLikeKeys = arrayLikeKeys;

/** Used for built-in method references. */
var objectProto$8 = Object.prototype;

/**
 * Checks if `value` is likely a prototype object.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a prototype, else `false`.
 */
function isPrototype(value) {
  var Ctor = value && value.constructor,
      proto = (typeof Ctor == 'function' && Ctor.prototype) || objectProto$8;

  return value === proto;
}

var _isPrototype = isPrototype;

/**
 * Creates a unary function that invokes `func` with its argument transformed.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {Function} transform The argument transform.
 * @returns {Function} Returns the new function.
 */
function overArg(func, transform) {
  return function(arg) {
    return func(transform(arg));
  };
}

var _overArg = overArg;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeKeys = _overArg(Object.keys, Object);

var _nativeKeys = nativeKeys;

/** Used for built-in method references. */
var objectProto$9 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$6 = objectProto$9.hasOwnProperty;

/**
 * The base implementation of `_.keys` which doesn't treat sparse arrays as dense.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function baseKeys(object) {
  if (!_isPrototype(object)) {
    return _nativeKeys(object);
  }
  var result = [];
  for (var key in Object(object)) {
    if (hasOwnProperty$6.call(object, key) && key != 'constructor') {
      result.push(key);
    }
  }
  return result;
}

var _baseKeys = baseKeys;

/**
 * Checks if `value` is array-like. A value is considered array-like if it's
 * not a function and has a `value.length` that's an integer greater than or
 * equal to `0` and less than or equal to `Number.MAX_SAFE_INTEGER`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is array-like, else `false`.
 * @example
 *
 * _.isArrayLike([1, 2, 3]);
 * // => true
 *
 * _.isArrayLike(document.body.children);
 * // => true
 *
 * _.isArrayLike('abc');
 * // => true
 *
 * _.isArrayLike(_.noop);
 * // => false
 */
function isArrayLike(value) {
  return value != null && isLength_1(value.length) && !isFunction_1(value);
}

var isArrayLike_1 = isArrayLike;

/**
 * Creates an array of the own enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects. See the
 * [ES spec](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
 * for more details.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keys(new Foo);
 * // => ['a', 'b'] (iteration order is not guaranteed)
 *
 * _.keys('hi');
 * // => ['0', '1']
 */
function keys(object) {
  return isArrayLike_1(object) ? _arrayLikeKeys(object) : _baseKeys(object);
}

var keys_1 = keys;

/**
 * Creates an array of own enumerable property names and symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names and symbols.
 */
function getAllKeys(object) {
  return _baseGetAllKeys(object, keys_1, _getSymbols);
}

var _getAllKeys = getAllKeys;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$2 = 1;

/** Used for built-in method references. */
var objectProto$a = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$7 = objectProto$a.hasOwnProperty;

/**
 * A specialized version of `baseIsEqualDeep` for objects with support for
 * partial deep comparisons.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */
function equalObjects(object, other, bitmask, customizer, equalFunc, stack) {
  var isPartial = bitmask & COMPARE_PARTIAL_FLAG$2,
      objProps = _getAllKeys(object),
      objLength = objProps.length,
      othProps = _getAllKeys(other),
      othLength = othProps.length;

  if (objLength != othLength && !isPartial) {
    return false;
  }
  var index = objLength;
  while (index--) {
    var key = objProps[index];
    if (!(isPartial ? key in other : hasOwnProperty$7.call(other, key))) {
      return false;
    }
  }
  // Assume cyclic values are equal.
  var stacked = stack.get(object);
  if (stacked && stack.get(other)) {
    return stacked == other;
  }
  var result = true;
  stack.set(object, other);
  stack.set(other, object);

  var skipCtor = isPartial;
  while (++index < objLength) {
    key = objProps[index];
    var objValue = object[key],
        othValue = other[key];

    if (customizer) {
      var compared = isPartial
        ? customizer(othValue, objValue, key, other, object, stack)
        : customizer(objValue, othValue, key, object, other, stack);
    }
    // Recursively compare objects (susceptible to call stack limits).
    if (!(compared === undefined
          ? (objValue === othValue || equalFunc(objValue, othValue, bitmask, customizer, stack))
          : compared
        )) {
      result = false;
      break;
    }
    skipCtor || (skipCtor = key == 'constructor');
  }
  if (result && !skipCtor) {
    var objCtor = object.constructor,
        othCtor = other.constructor;

    // Non `Object` object instances with different constructors are not equal.
    if (objCtor != othCtor &&
        ('constructor' in object && 'constructor' in other) &&
        !(typeof objCtor == 'function' && objCtor instanceof objCtor &&
          typeof othCtor == 'function' && othCtor instanceof othCtor)) {
      result = false;
    }
  }
  stack['delete'](object);
  stack['delete'](other);
  return result;
}

var _equalObjects = equalObjects;

/* Built-in method references that are verified to be native. */
var DataView = _getNative(_root, 'DataView');

var _DataView = DataView;

/* Built-in method references that are verified to be native. */
var Promise = _getNative(_root, 'Promise');

var _Promise = Promise;

/* Built-in method references that are verified to be native. */
var Set$1 = _getNative(_root, 'Set');

var _Set = Set$1;

/* Built-in method references that are verified to be native. */
var WeakMap = _getNative(_root, 'WeakMap');

var _WeakMap = WeakMap;

/** `Object#toString` result references. */
var mapTag$2 = '[object Map]',
    objectTag$1 = '[object Object]',
    promiseTag = '[object Promise]',
    setTag$2 = '[object Set]',
    weakMapTag$1 = '[object WeakMap]';

var dataViewTag$2 = '[object DataView]';

/** Used to detect maps, sets, and weakmaps. */
var dataViewCtorString = _toSource(_DataView),
    mapCtorString = _toSource(_Map),
    promiseCtorString = _toSource(_Promise),
    setCtorString = _toSource(_Set),
    weakMapCtorString = _toSource(_WeakMap);

/**
 * Gets the `toStringTag` of `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
var getTag = _baseGetTag;

// Fallback for data views, maps, sets, and weak maps in IE 11 and promises in Node.js < 6.
if ((_DataView && getTag(new _DataView(new ArrayBuffer(1))) != dataViewTag$2) ||
    (_Map && getTag(new _Map) != mapTag$2) ||
    (_Promise && getTag(_Promise.resolve()) != promiseTag) ||
    (_Set && getTag(new _Set) != setTag$2) ||
    (_WeakMap && getTag(new _WeakMap) != weakMapTag$1)) {
  getTag = function(value) {
    var result = _baseGetTag(value),
        Ctor = result == objectTag$1 ? value.constructor : undefined,
        ctorString = Ctor ? _toSource(Ctor) : '';

    if (ctorString) {
      switch (ctorString) {
        case dataViewCtorString: return dataViewTag$2;
        case mapCtorString: return mapTag$2;
        case promiseCtorString: return promiseTag;
        case setCtorString: return setTag$2;
        case weakMapCtorString: return weakMapTag$1;
      }
    }
    return result;
  };
}

var _getTag = getTag;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$3 = 1;

/** `Object#toString` result references. */
var argsTag$2 = '[object Arguments]',
    arrayTag$1 = '[object Array]',
    objectTag$2 = '[object Object]';

/** Used for built-in method references. */
var objectProto$b = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$8 = objectProto$b.hasOwnProperty;

/**
 * A specialized version of `baseIsEqual` for arrays and objects which performs
 * deep comparisons and tracks traversed objects enabling objects with circular
 * references to be compared.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} [stack] Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */
function baseIsEqualDeep(object, other, bitmask, customizer, equalFunc, stack) {
  var objIsArr = isArray_1(object),
      othIsArr = isArray_1(other),
      objTag = objIsArr ? arrayTag$1 : _getTag(object),
      othTag = othIsArr ? arrayTag$1 : _getTag(other);

  objTag = objTag == argsTag$2 ? objectTag$2 : objTag;
  othTag = othTag == argsTag$2 ? objectTag$2 : othTag;

  var objIsObj = objTag == objectTag$2,
      othIsObj = othTag == objectTag$2,
      isSameTag = objTag == othTag;

  if (isSameTag && isBuffer_1(object)) {
    if (!isBuffer_1(other)) {
      return false;
    }
    objIsArr = true;
    objIsObj = false;
  }
  if (isSameTag && !objIsObj) {
    stack || (stack = new _Stack);
    return (objIsArr || isTypedArray_1(object))
      ? _equalArrays(object, other, bitmask, customizer, equalFunc, stack)
      : _equalByTag(object, other, objTag, bitmask, customizer, equalFunc, stack);
  }
  if (!(bitmask & COMPARE_PARTIAL_FLAG$3)) {
    var objIsWrapped = objIsObj && hasOwnProperty$8.call(object, '__wrapped__'),
        othIsWrapped = othIsObj && hasOwnProperty$8.call(other, '__wrapped__');

    if (objIsWrapped || othIsWrapped) {
      var objUnwrapped = objIsWrapped ? object.value() : object,
          othUnwrapped = othIsWrapped ? other.value() : other;

      stack || (stack = new _Stack);
      return equalFunc(objUnwrapped, othUnwrapped, bitmask, customizer, stack);
    }
  }
  if (!isSameTag) {
    return false;
  }
  stack || (stack = new _Stack);
  return _equalObjects(object, other, bitmask, customizer, equalFunc, stack);
}

var _baseIsEqualDeep = baseIsEqualDeep;

/**
 * The base implementation of `_.isEqual` which supports partial comparisons
 * and tracks traversed objects.
 *
 * @private
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @param {boolean} bitmask The bitmask flags.
 *  1 - Unordered comparison
 *  2 - Partial comparison
 * @param {Function} [customizer] The function to customize comparisons.
 * @param {Object} [stack] Tracks traversed `value` and `other` objects.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 */
function baseIsEqual(value, other, bitmask, customizer, stack) {
  if (value === other) {
    return true;
  }
  if (value == null || other == null || (!isObjectLike_1(value) && !isObjectLike_1(other))) {
    return value !== value && other !== other;
  }
  return _baseIsEqualDeep(value, other, bitmask, customizer, baseIsEqual, stack);
}

var _baseIsEqual = baseIsEqual;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$4 = 1,
    COMPARE_UNORDERED_FLAG$2 = 2;

/**
 * The base implementation of `_.isMatch` without support for iteratee shorthands.
 *
 * @private
 * @param {Object} object The object to inspect.
 * @param {Object} source The object of property values to match.
 * @param {Array} matchData The property names, values, and compare flags to match.
 * @param {Function} [customizer] The function to customize comparisons.
 * @returns {boolean} Returns `true` if `object` is a match, else `false`.
 */
function baseIsMatch(object, source, matchData, customizer) {
  var index = matchData.length,
      length = index,
      noCustomizer = !customizer;

  if (object == null) {
    return !length;
  }
  object = Object(object);
  while (index--) {
    var data = matchData[index];
    if ((noCustomizer && data[2])
          ? data[1] !== object[data[0]]
          : !(data[0] in object)
        ) {
      return false;
    }
  }
  while (++index < length) {
    data = matchData[index];
    var key = data[0],
        objValue = object[key],
        srcValue = data[1];

    if (noCustomizer && data[2]) {
      if (objValue === undefined && !(key in object)) {
        return false;
      }
    } else {
      var stack = new _Stack;
      if (customizer) {
        var result = customizer(objValue, srcValue, key, object, source, stack);
      }
      if (!(result === undefined
            ? _baseIsEqual(srcValue, objValue, COMPARE_PARTIAL_FLAG$4 | COMPARE_UNORDERED_FLAG$2, customizer, stack)
            : result
          )) {
        return false;
      }
    }
  }
  return true;
}

var _baseIsMatch = baseIsMatch;

/**
 * Checks if `value` is suitable for strict equality comparisons, i.e. `===`.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` if suitable for strict
 *  equality comparisons, else `false`.
 */
function isStrictComparable(value) {
  return value === value && !isObject_1(value);
}

var _isStrictComparable = isStrictComparable;

/**
 * Gets the property names, values, and compare flags of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the match data of `object`.
 */
function getMatchData(object) {
  var result = keys_1(object),
      length = result.length;

  while (length--) {
    var key = result[length],
        value = object[key];

    result[length] = [key, value, _isStrictComparable(value)];
  }
  return result;
}

var _getMatchData = getMatchData;

/**
 * A specialized version of `matchesProperty` for source values suitable
 * for strict equality comparisons, i.e. `===`.
 *
 * @private
 * @param {string} key The key of the property to get.
 * @param {*} srcValue The value to match.
 * @returns {Function} Returns the new spec function.
 */
function matchesStrictComparable(key, srcValue) {
  return function(object) {
    if (object == null) {
      return false;
    }
    return object[key] === srcValue &&
      (srcValue !== undefined || (key in Object(object)));
  };
}

var _matchesStrictComparable = matchesStrictComparable;

/**
 * The base implementation of `_.matches` which doesn't clone `source`.
 *
 * @private
 * @param {Object} source The object of property values to match.
 * @returns {Function} Returns the new spec function.
 */
function baseMatches(source) {
  var matchData = _getMatchData(source);
  if (matchData.length == 1 && matchData[0][2]) {
    return _matchesStrictComparable(matchData[0][0], matchData[0][1]);
  }
  return function(object) {
    return object === source || _baseIsMatch(object, source, matchData);
  };
}

var _baseMatches = baseMatches;

/** `Object#toString` result references. */
var symbolTag$1 = '[object Symbol]';

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */
function isSymbol(value) {
  return typeof value == 'symbol' ||
    (isObjectLike_1(value) && _baseGetTag(value) == symbolTag$1);
}

var isSymbol_1 = isSymbol;

/** Used to match property names within property paths. */
var reIsDeepProp = /\.|\[(?:[^[\]]*|(["'])(?:(?!\1)[^\\]|\\.)*?\1)\]/,
    reIsPlainProp = /^\w*$/;

/**
 * Checks if `value` is a property name and not a property path.
 *
 * @private
 * @param {*} value The value to check.
 * @param {Object} [object] The object to query keys on.
 * @returns {boolean} Returns `true` if `value` is a property name, else `false`.
 */
function isKey(value, object) {
  if (isArray_1(value)) {
    return false;
  }
  var type = typeof value;
  if (type == 'number' || type == 'symbol' || type == 'boolean' ||
      value == null || isSymbol_1(value)) {
    return true;
  }
  return reIsPlainProp.test(value) || !reIsDeepProp.test(value) ||
    (object != null && value in Object(object));
}

var _isKey = isKey;

/** Error message constants. */
var FUNC_ERROR_TEXT = 'Expected a function';

/**
 * Creates a function that memoizes the result of `func`. If `resolver` is
 * provided, it determines the cache key for storing the result based on the
 * arguments provided to the memoized function. By default, the first argument
 * provided to the memoized function is used as the map cache key. The `func`
 * is invoked with the `this` binding of the memoized function.
 *
 * **Note:** The cache is exposed as the `cache` property on the memoized
 * function. Its creation may be customized by replacing the `_.memoize.Cache`
 * constructor with one whose instances implement the
 * [`Map`](http://ecma-international.org/ecma-262/7.0/#sec-properties-of-the-map-prototype-object)
 * method interface of `clear`, `delete`, `get`, `has`, and `set`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to have its output memoized.
 * @param {Function} [resolver] The function to resolve the cache key.
 * @returns {Function} Returns the new memoized function.
 * @example
 *
 * var object = { 'a': 1, 'b': 2 };
 * var other = { 'c': 3, 'd': 4 };
 *
 * var values = _.memoize(_.values);
 * values(object);
 * // => [1, 2]
 *
 * values(other);
 * // => [3, 4]
 *
 * object.a = 2;
 * values(object);
 * // => [1, 2]
 *
 * // Modify the result cache.
 * values.cache.set(object, ['a', 'b']);
 * values(object);
 * // => ['a', 'b']
 *
 * // Replace `_.memoize.Cache`.
 * _.memoize.Cache = WeakMap;
 */
function memoize(func, resolver) {
  if (typeof func != 'function' || (resolver != null && typeof resolver != 'function')) {
    throw new TypeError(FUNC_ERROR_TEXT);
  }
  var memoized = function() {
    var args = arguments,
        key = resolver ? resolver.apply(this, args) : args[0],
        cache = memoized.cache;

    if (cache.has(key)) {
      return cache.get(key);
    }
    var result = func.apply(this, args);
    memoized.cache = cache.set(key, result) || cache;
    return result;
  };
  memoized.cache = new (memoize.Cache || _MapCache);
  return memoized;
}

// Expose `MapCache`.
memoize.Cache = _MapCache;

var memoize_1 = memoize;

/** Used as the maximum memoize cache size. */
var MAX_MEMOIZE_SIZE = 500;

/**
 * A specialized version of `_.memoize` which clears the memoized function's
 * cache when it exceeds `MAX_MEMOIZE_SIZE`.
 *
 * @private
 * @param {Function} func The function to have its output memoized.
 * @returns {Function} Returns the new memoized function.
 */
function memoizeCapped(func) {
  var result = memoize_1(func, function(key) {
    if (cache.size === MAX_MEMOIZE_SIZE) {
      cache.clear();
    }
    return key;
  });

  var cache = result.cache;
  return result;
}

var _memoizeCapped = memoizeCapped;

/** Used to match property names within property paths. */
var rePropName = /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|$))/g;

/** Used to match backslashes in property paths. */
var reEscapeChar = /\\(\\)?/g;

/**
 * Converts `string` to a property path array.
 *
 * @private
 * @param {string} string The string to convert.
 * @returns {Array} Returns the property path array.
 */
var stringToPath = _memoizeCapped(function(string) {
  var result = [];
  if (string.charCodeAt(0) === 46 /* . */) {
    result.push('');
  }
  string.replace(rePropName, function(match, number, quote, subString) {
    result.push(quote ? subString.replace(reEscapeChar, '$1') : (number || match));
  });
  return result;
});

var _stringToPath = stringToPath;

/** Used as references for various `Number` constants. */
var INFINITY = 1 / 0;

/** Used to convert symbols to primitives and strings. */
var symbolProto$1 = _Symbol ? _Symbol.prototype : undefined,
    symbolToString = symbolProto$1 ? symbolProto$1.toString : undefined;

/**
 * The base implementation of `_.toString` which doesn't convert nullish
 * values to empty strings.
 *
 * @private
 * @param {*} value The value to process.
 * @returns {string} Returns the string.
 */
function baseToString(value) {
  // Exit early for strings to avoid a performance hit in some environments.
  if (typeof value == 'string') {
    return value;
  }
  if (isArray_1(value)) {
    // Recursively convert values (susceptible to call stack limits).
    return _arrayMap(value, baseToString) + '';
  }
  if (isSymbol_1(value)) {
    return symbolToString ? symbolToString.call(value) : '';
  }
  var result = (value + '');
  return (result == '0' && (1 / value) == -INFINITY) ? '-0' : result;
}

var _baseToString = baseToString;

/**
 * Converts `value` to a string. An empty string is returned for `null`
 * and `undefined` values. The sign of `-0` is preserved.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 * @example
 *
 * _.toString(null);
 * // => ''
 *
 * _.toString(-0);
 * // => '-0'
 *
 * _.toString([1, 2, 3]);
 * // => '1,2,3'
 */
function toString(value) {
  return value == null ? '' : _baseToString(value);
}

var toString_1 = toString;

/**
 * Casts `value` to a path array if it's not one.
 *
 * @private
 * @param {*} value The value to inspect.
 * @param {Object} [object] The object to query keys on.
 * @returns {Array} Returns the cast property path array.
 */
function castPath(value, object) {
  if (isArray_1(value)) {
    return value;
  }
  return _isKey(value, object) ? [value] : _stringToPath(toString_1(value));
}

var _castPath = castPath;

/** Used as references for various `Number` constants. */
var INFINITY$1 = 1 / 0;

/**
 * Converts `value` to a string key if it's not a string or symbol.
 *
 * @private
 * @param {*} value The value to inspect.
 * @returns {string|symbol} Returns the key.
 */
function toKey(value) {
  if (typeof value == 'string' || isSymbol_1(value)) {
    return value;
  }
  var result = (value + '');
  return (result == '0' && (1 / value) == -INFINITY$1) ? '-0' : result;
}

var _toKey = toKey;

/**
 * The base implementation of `_.get` without support for default values.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array|string} path The path of the property to get.
 * @returns {*} Returns the resolved value.
 */
function baseGet(object, path) {
  path = _castPath(path, object);

  var index = 0,
      length = path.length;

  while (object != null && index < length) {
    object = object[_toKey(path[index++])];
  }
  return (index && index == length) ? object : undefined;
}

var _baseGet = baseGet;

/**
 * Gets the value at `path` of `object`. If the resolved value is
 * `undefined`, the `defaultValue` is returned in its place.
 *
 * @static
 * @memberOf _
 * @since 3.7.0
 * @category Object
 * @param {Object} object The object to query.
 * @param {Array|string} path The path of the property to get.
 * @param {*} [defaultValue] The value returned for `undefined` resolved values.
 * @returns {*} Returns the resolved value.
 * @example
 *
 * var object = { 'a': [{ 'b': { 'c': 3 } }] };
 *
 * _.get(object, 'a[0].b.c');
 * // => 3
 *
 * _.get(object, ['a', '0', 'b', 'c']);
 * // => 3
 *
 * _.get(object, 'a.b.c', 'default');
 * // => 'default'
 */
function get(object, path, defaultValue) {
  var result = object == null ? undefined : _baseGet(object, path);
  return result === undefined ? defaultValue : result;
}

var get_1 = get;

/**
 * The base implementation of `_.hasIn` without support for deep paths.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {Array|string} key The key to check.
 * @returns {boolean} Returns `true` if `key` exists, else `false`.
 */
function baseHasIn(object, key) {
  return object != null && key in Object(object);
}

var _baseHasIn = baseHasIn;

/**
 * Checks if `path` exists on `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array|string} path The path to check.
 * @param {Function} hasFunc The function to check properties.
 * @returns {boolean} Returns `true` if `path` exists, else `false`.
 */
function hasPath(object, path, hasFunc) {
  path = _castPath(path, object);

  var index = -1,
      length = path.length,
      result = false;

  while (++index < length) {
    var key = _toKey(path[index]);
    if (!(result = object != null && hasFunc(object, key))) {
      break;
    }
    object = object[key];
  }
  if (result || ++index != length) {
    return result;
  }
  length = object == null ? 0 : object.length;
  return !!length && isLength_1(length) && _isIndex(key, length) &&
    (isArray_1(object) || isArguments_1(object));
}

var _hasPath = hasPath;

/**
 * Checks if `path` is a direct or inherited property of `object`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Object
 * @param {Object} object The object to query.
 * @param {Array|string} path The path to check.
 * @returns {boolean} Returns `true` if `path` exists, else `false`.
 * @example
 *
 * var object = _.create({ 'a': _.create({ 'b': 2 }) });
 *
 * _.hasIn(object, 'a');
 * // => true
 *
 * _.hasIn(object, 'a.b');
 * // => true
 *
 * _.hasIn(object, ['a', 'b']);
 * // => true
 *
 * _.hasIn(object, 'b');
 * // => false
 */
function hasIn(object, path) {
  return object != null && _hasPath(object, path, _baseHasIn);
}

var hasIn_1 = hasIn;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$5 = 1,
    COMPARE_UNORDERED_FLAG$3 = 2;

/**
 * The base implementation of `_.matchesProperty` which doesn't clone `srcValue`.
 *
 * @private
 * @param {string} path The path of the property to get.
 * @param {*} srcValue The value to match.
 * @returns {Function} Returns the new spec function.
 */
function baseMatchesProperty(path, srcValue) {
  if (_isKey(path) && _isStrictComparable(srcValue)) {
    return _matchesStrictComparable(_toKey(path), srcValue);
  }
  return function(object) {
    var objValue = get_1(object, path);
    return (objValue === undefined && objValue === srcValue)
      ? hasIn_1(object, path)
      : _baseIsEqual(srcValue, objValue, COMPARE_PARTIAL_FLAG$5 | COMPARE_UNORDERED_FLAG$3);
  };
}

var _baseMatchesProperty = baseMatchesProperty;

/**
 * This method returns the first argument it receives.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Util
 * @param {*} value Any value.
 * @returns {*} Returns `value`.
 * @example
 *
 * var object = { 'a': 1 };
 *
 * console.log(_.identity(object) === object);
 * // => true
 */
function identity(value) {
  return value;
}

var identity_1 = identity;

/**
 * The base implementation of `_.property` without support for deep paths.
 *
 * @private
 * @param {string} key The key of the property to get.
 * @returns {Function} Returns the new accessor function.
 */
function baseProperty(key) {
  return function(object) {
    return object == null ? undefined : object[key];
  };
}

var _baseProperty = baseProperty;

/**
 * A specialized version of `baseProperty` which supports deep paths.
 *
 * @private
 * @param {Array|string} path The path of the property to get.
 * @returns {Function} Returns the new accessor function.
 */
function basePropertyDeep(path) {
  return function(object) {
    return _baseGet(object, path);
  };
}

var _basePropertyDeep = basePropertyDeep;

/**
 * Creates a function that returns the value at `path` of a given object.
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Util
 * @param {Array|string} path The path of the property to get.
 * @returns {Function} Returns the new accessor function.
 * @example
 *
 * var objects = [
 *   { 'a': { 'b': 2 } },
 *   { 'a': { 'b': 1 } }
 * ];
 *
 * _.map(objects, _.property('a.b'));
 * // => [2, 1]
 *
 * _.map(_.sortBy(objects, _.property(['a', 'b'])), 'a.b');
 * // => [1, 2]
 */
function property(path) {
  return _isKey(path) ? _baseProperty(_toKey(path)) : _basePropertyDeep(path);
}

var property_1 = property;

/**
 * The base implementation of `_.iteratee`.
 *
 * @private
 * @param {*} [value=_.identity] The value to convert to an iteratee.
 * @returns {Function} Returns the iteratee.
 */
function baseIteratee(value) {
  // Don't store the `typeof` result in a variable to avoid a JIT bug in Safari 9.
  // See https://bugs.webkit.org/show_bug.cgi?id=156034 for more details.
  if (typeof value == 'function') {
    return value;
  }
  if (value == null) {
    return identity_1;
  }
  if (typeof value == 'object') {
    return isArray_1(value)
      ? _baseMatchesProperty(value[0], value[1])
      : _baseMatches(value);
  }
  return property_1(value);
}

var _baseIteratee = baseIteratee;

/**
 * Creates a base function for methods like `_.forIn` and `_.forOwn`.
 *
 * @private
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {Function} Returns the new base function.
 */
function createBaseFor(fromRight) {
  return function(object, iteratee, keysFunc) {
    var index = -1,
        iterable = Object(object),
        props = keysFunc(object),
        length = props.length;

    while (length--) {
      var key = props[fromRight ? length : ++index];
      if (iteratee(iterable[key], key, iterable) === false) {
        break;
      }
    }
    return object;
  };
}

var _createBaseFor = createBaseFor;

/**
 * The base implementation of `baseForOwn` which iterates over `object`
 * properties returned by `keysFunc` and invokes `iteratee` for each property.
 * Iteratee functions may exit iteration early by explicitly returning `false`.
 *
 * @private
 * @param {Object} object The object to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @param {Function} keysFunc The function to get the keys of `object`.
 * @returns {Object} Returns `object`.
 */
var baseFor = _createBaseFor();

var _baseFor = baseFor;

/**
 * The base implementation of `_.forOwn` without support for iteratee shorthands.
 *
 * @private
 * @param {Object} object The object to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Object} Returns `object`.
 */
function baseForOwn(object, iteratee) {
  return object && _baseFor(object, iteratee, keys_1);
}

var _baseForOwn = baseForOwn;

/**
 * Creates a `baseEach` or `baseEachRight` function.
 *
 * @private
 * @param {Function} eachFunc The function to iterate over a collection.
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {Function} Returns the new base function.
 */
function createBaseEach(eachFunc, fromRight) {
  return function(collection, iteratee) {
    if (collection == null) {
      return collection;
    }
    if (!isArrayLike_1(collection)) {
      return eachFunc(collection, iteratee);
    }
    var length = collection.length,
        index = fromRight ? length : -1,
        iterable = Object(collection);

    while ((fromRight ? index-- : ++index < length)) {
      if (iteratee(iterable[index], index, iterable) === false) {
        break;
      }
    }
    return collection;
  };
}

var _createBaseEach = createBaseEach;

/**
 * The base implementation of `_.forEach` without support for iteratee shorthands.
 *
 * @private
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array|Object} Returns `collection`.
 */
var baseEach = _createBaseEach(_baseForOwn);

var _baseEach = baseEach;

/**
 * The base implementation of `_.map` without support for iteratee shorthands.
 *
 * @private
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the new mapped array.
 */
function baseMap(collection, iteratee) {
  var index = -1,
      result = isArrayLike_1(collection) ? Array(collection.length) : [];

  _baseEach(collection, function(value, key, collection) {
    result[++index] = iteratee(value, key, collection);
  });
  return result;
}

var _baseMap = baseMap;

/**
 * Creates an array of values by running each element in `collection` thru
 * `iteratee`. The iteratee is invoked with three arguments:
 * (value, index|key, collection).
 *
 * Many lodash methods are guarded to work as iteratees for methods like
 * `_.every`, `_.filter`, `_.map`, `_.mapValues`, `_.reject`, and `_.some`.
 *
 * The guarded methods are:
 * `ary`, `chunk`, `curry`, `curryRight`, `drop`, `dropRight`, `every`,
 * `fill`, `invert`, `parseInt`, `random`, `range`, `rangeRight`, `repeat`,
 * `sampleSize`, `slice`, `some`, `sortBy`, `split`, `take`, `takeRight`,
 * `template`, `trim`, `trimEnd`, `trimStart`, and `words`
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Collection
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} [iteratee=_.identity] The function invoked per iteration.
 * @returns {Array} Returns the new mapped array.
 * @example
 *
 * function square(n) {
 *   return n * n;
 * }
 *
 * _.map([4, 8], square);
 * // => [16, 64]
 *
 * _.map({ 'a': 4, 'b': 8 }, square);
 * // => [16, 64] (iteration order is not guaranteed)
 *
 * var users = [
 *   { 'user': 'barney' },
 *   { 'user': 'fred' }
 * ];
 *
 * // The `_.property` iteratee shorthand.
 * _.map(users, 'user');
 * // => ['barney', 'fred']
 */
function map(collection, iteratee) {
  var func = isArray_1(collection) ? _arrayMap : _baseMap;
  return func(collection, _baseIteratee(iteratee));
}

var map_1 = map;

/* eslint-disable no-constant-condition */
/**
 * {@link Editor.before} with additional options.
 * TODO: support for sequence of any characters.
 */

const getPointBefore = (editor, at, options) => {
  var _a, _b, _c;

  if (!options || !options.match && !options.matchString) {
    return Editor.before(editor, at, options);
  }

  let beforeAt = at;
  let previousBeforePoint = Editor.point(editor, at, {
    edge: 'end'
  });
  const stackLength = (((_a = options.matchString) === null || _a === void 0 ? void 0 : _a.length) || 0) + 1;
  const stack = Array(stackLength);
  const unitOffset = !options.unit || options.unit === 'offset';
  let count = 0;

  while (true) {
    const beforePoint = Editor.before(editor, beforeAt, options); // not found

    if (!beforePoint) return; // different path

    if (!options.multiPaths && !Path.equals(beforePoint.path, previousBeforePoint.path)) {
      return;
    }

    const beforeString = Editor.string(editor, {
      anchor: beforePoint,
      focus: previousBeforePoint
    });
    const matchString = castArray_1(options.matchString);
    let beforeStringToMatch = beforeString;

    if (unitOffset && stackLength) {
      stack.unshift({
        point: beforePoint,
        text: beforeString
      });
      stack.pop();
      beforeStringToMatch = map_1(stack.slice(0, -1), 'text').join('');
    }

    if (matchString.includes(beforeStringToMatch) || ((_b = options.match) === null || _b === void 0 ? void 0 : _b.call(options, {
      beforeString: beforeStringToMatch,
      beforePoint,
      at
    }))) {
      if (options.afterMatch) {
        if (stackLength && unitOffset) {
          return (_c = stack[stack.length - 1]) === null || _c === void 0 ? void 0 : _c.point;
        }

        return previousBeforePoint;
      }

      return beforePoint;
    }

    previousBeforePoint = beforePoint;
    beforeAt = beforePoint;
    count += 1;

    if (!options.skipInvalid) {
      if (!matchString || count > matchString.length) return;
    }
  }
};

/**
 * Get the point from a location (default: selection).
 * If the location is a range, get the anchor point.
 * If the location is a path, get the point at this path with offset 0.
 * If `focus` is true, get the focus point.
 */

const getPointFromLocation = (editor, {
  at = editor.selection,
  focus
} = {}) => {
  let point;
  if (Range.isRange(at)) point = !focus ? at.anchor : at.focus;
  if (Point.isPoint(at)) point = at;
  if (Path.isPath(at)) point = {
    path: at,
    offset: 0
  };
  return point;
};

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __makeTemplateObject(cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
}

var global$1 = (typeof global !== "undefined" ? global :
            typeof self !== "undefined" ? self :
            typeof window !== "undefined" ? window : {});

// shim for using process in browser
// based off https://github.com/defunctzombie/node-process/blob/master/browser.js

function defaultSetTimout() {
    throw new Error('setTimeout has not been defined');
}
function defaultClearTimeout () {
    throw new Error('clearTimeout has not been defined');
}
var cachedSetTimeout = defaultSetTimout;
var cachedClearTimeout = defaultClearTimeout;
if (typeof global$1.setTimeout === 'function') {
    cachedSetTimeout = setTimeout;
}
if (typeof global$1.clearTimeout === 'function') {
    cachedClearTimeout = clearTimeout;
}

function runTimeout(fun) {
    if (cachedSetTimeout === setTimeout) {
        //normal enviroments in sane situations
        return setTimeout(fun, 0);
    }
    // if setTimeout wasn't available but was latter defined
    if ((cachedSetTimeout === defaultSetTimout || !cachedSetTimeout) && setTimeout) {
        cachedSetTimeout = setTimeout;
        return setTimeout(fun, 0);
    }
    try {
        // when when somebody has screwed with setTimeout but no I.E. maddness
        return cachedSetTimeout(fun, 0);
    } catch(e){
        try {
            // When we are in I.E. but the script has been evaled so I.E. doesn't trust the global object when called normally
            return cachedSetTimeout.call(null, fun, 0);
        } catch(e){
            // same as above but when it's a version of I.E. that must have the global object for 'this', hopfully our context correct otherwise it will throw a global error
            return cachedSetTimeout.call(this, fun, 0);
        }
    }


}
function runClearTimeout(marker) {
    if (cachedClearTimeout === clearTimeout) {
        //normal enviroments in sane situations
        return clearTimeout(marker);
    }
    // if clearTimeout wasn't available but was latter defined
    if ((cachedClearTimeout === defaultClearTimeout || !cachedClearTimeout) && clearTimeout) {
        cachedClearTimeout = clearTimeout;
        return clearTimeout(marker);
    }
    try {
        // when when somebody has screwed with setTimeout but no I.E. maddness
        return cachedClearTimeout(marker);
    } catch (e){
        try {
            // When we are in I.E. but the script has been evaled so I.E. doesn't  trust the global object when called normally
            return cachedClearTimeout.call(null, marker);
        } catch (e){
            // same as above but when it's a version of I.E. that must have the global object for 'this', hopfully our context correct otherwise it will throw a global error.
            // Some versions of I.E. have different rules for clearTimeout vs setTimeout
            return cachedClearTimeout.call(this, marker);
        }
    }



}
var queue = [];
var draining = false;
var currentQueue;
var queueIndex = -1;

function cleanUpNextTick() {
    if (!draining || !currentQueue) {
        return;
    }
    draining = false;
    if (currentQueue.length) {
        queue = currentQueue.concat(queue);
    } else {
        queueIndex = -1;
    }
    if (queue.length) {
        drainQueue();
    }
}

function drainQueue() {
    if (draining) {
        return;
    }
    var timeout = runTimeout(cleanUpNextTick);
    draining = true;

    var len = queue.length;
    while(len) {
        currentQueue = queue;
        queue = [];
        while (++queueIndex < len) {
            if (currentQueue) {
                currentQueue[queueIndex].run();
            }
        }
        queueIndex = -1;
        len = queue.length;
    }
    currentQueue = null;
    draining = false;
    runClearTimeout(timeout);
}
function nextTick(fun) {
    var args = new Array(arguments.length - 1);
    if (arguments.length > 1) {
        for (var i = 1; i < arguments.length; i++) {
            args[i - 1] = arguments[i];
        }
    }
    queue.push(new Item(fun, args));
    if (queue.length === 1 && !draining) {
        runTimeout(drainQueue);
    }
}
// v8 likes predictible objects
function Item(fun, array) {
    this.fun = fun;
    this.array = array;
}
Item.prototype.run = function () {
    this.fun.apply(null, this.array);
};
var title = 'browser';
var platform = 'browser';
var browser = true;
var env = {};
var argv = [];
var version = ''; // empty string to avoid regexp issues
var versions = {};
var release = {};
var config = {};

function noop() {}

var on = noop;
var addListener = noop;
var once = noop;
var off = noop;
var removeListener = noop;
var removeAllListeners = noop;
var emit = noop;

function binding(name) {
    throw new Error('process.binding is not supported');
}

function cwd () { return '/' }
function chdir (dir) {
    throw new Error('process.chdir is not supported');
}function umask() { return 0; }

// from https://github.com/kumavis/browser-process-hrtime/blob/master/index.js
var performance = global$1.performance || {};
var performanceNow =
  performance.now        ||
  performance.mozNow     ||
  performance.msNow      ||
  performance.oNow       ||
  performance.webkitNow  ||
  function(){ return (new Date()).getTime() };

// generate timestamp or delta
// see http://nodejs.org/api/process.html#process_process_hrtime
function hrtime(previousTimestamp){
  var clocktime = performanceNow.call(performance)*1e-3;
  var seconds = Math.floor(clocktime);
  var nanoseconds = Math.floor((clocktime%1)*1e9);
  if (previousTimestamp) {
    seconds = seconds - previousTimestamp[0];
    nanoseconds = nanoseconds - previousTimestamp[1];
    if (nanoseconds<0) {
      seconds--;
      nanoseconds += 1e9;
    }
  }
  return [seconds,nanoseconds]
}

var startTime = new Date();
function uptime() {
  var currentTime = new Date();
  var dif = currentTime - startTime;
  return dif / 1000;
}

var process = {
  nextTick: nextTick,
  title: title,
  browser: browser,
  env: env,
  argv: argv,
  version: version,
  versions: versions,
  on: on,
  addListener: addListener,
  once: once,
  off: off,
  removeListener: removeListener,
  removeAllListeners: removeAllListeners,
  emit: emit,
  binding: binding,
  cwd: cwd,
  chdir: chdir,
  umask: umask,
  hrtime: hrtime,
  platform: platform,
  release: release,
  config: config,
  uptime: uptime
};

var reactIs_development = createCommonjsModule(function (module, exports) {



{
  (function() {

// The Symbol used to tag the ReactElement-like types. If there is no native Symbol
// nor polyfill, then a plain number is used for performance.
var hasSymbol = typeof Symbol === 'function' && Symbol.for;
var REACT_ELEMENT_TYPE = hasSymbol ? Symbol.for('react.element') : 0xeac7;
var REACT_PORTAL_TYPE = hasSymbol ? Symbol.for('react.portal') : 0xeaca;
var REACT_FRAGMENT_TYPE = hasSymbol ? Symbol.for('react.fragment') : 0xeacb;
var REACT_STRICT_MODE_TYPE = hasSymbol ? Symbol.for('react.strict_mode') : 0xeacc;
var REACT_PROFILER_TYPE = hasSymbol ? Symbol.for('react.profiler') : 0xead2;
var REACT_PROVIDER_TYPE = hasSymbol ? Symbol.for('react.provider') : 0xeacd;
var REACT_CONTEXT_TYPE = hasSymbol ? Symbol.for('react.context') : 0xeace; // TODO: We don't use AsyncMode or ConcurrentMode anymore. They were temporary
// (unstable) APIs that have been removed. Can we remove the symbols?

var REACT_ASYNC_MODE_TYPE = hasSymbol ? Symbol.for('react.async_mode') : 0xeacf;
var REACT_CONCURRENT_MODE_TYPE = hasSymbol ? Symbol.for('react.concurrent_mode') : 0xeacf;
var REACT_FORWARD_REF_TYPE = hasSymbol ? Symbol.for('react.forward_ref') : 0xead0;
var REACT_SUSPENSE_TYPE = hasSymbol ? Symbol.for('react.suspense') : 0xead1;
var REACT_SUSPENSE_LIST_TYPE = hasSymbol ? Symbol.for('react.suspense_list') : 0xead8;
var REACT_MEMO_TYPE = hasSymbol ? Symbol.for('react.memo') : 0xead3;
var REACT_LAZY_TYPE = hasSymbol ? Symbol.for('react.lazy') : 0xead4;
var REACT_BLOCK_TYPE = hasSymbol ? Symbol.for('react.block') : 0xead9;
var REACT_FUNDAMENTAL_TYPE = hasSymbol ? Symbol.for('react.fundamental') : 0xead5;
var REACT_RESPONDER_TYPE = hasSymbol ? Symbol.for('react.responder') : 0xead6;
var REACT_SCOPE_TYPE = hasSymbol ? Symbol.for('react.scope') : 0xead7;

function isValidElementType(type) {
  return typeof type === 'string' || typeof type === 'function' || // Note: its typeof might be other than 'symbol' or 'number' if it's a polyfill.
  type === REACT_FRAGMENT_TYPE || type === REACT_CONCURRENT_MODE_TYPE || type === REACT_PROFILER_TYPE || type === REACT_STRICT_MODE_TYPE || type === REACT_SUSPENSE_TYPE || type === REACT_SUSPENSE_LIST_TYPE || typeof type === 'object' && type !== null && (type.$$typeof === REACT_LAZY_TYPE || type.$$typeof === REACT_MEMO_TYPE || type.$$typeof === REACT_PROVIDER_TYPE || type.$$typeof === REACT_CONTEXT_TYPE || type.$$typeof === REACT_FORWARD_REF_TYPE || type.$$typeof === REACT_FUNDAMENTAL_TYPE || type.$$typeof === REACT_RESPONDER_TYPE || type.$$typeof === REACT_SCOPE_TYPE || type.$$typeof === REACT_BLOCK_TYPE);
}

function typeOf(object) {
  if (typeof object === 'object' && object !== null) {
    var $$typeof = object.$$typeof;

    switch ($$typeof) {
      case REACT_ELEMENT_TYPE:
        var type = object.type;

        switch (type) {
          case REACT_ASYNC_MODE_TYPE:
          case REACT_CONCURRENT_MODE_TYPE:
          case REACT_FRAGMENT_TYPE:
          case REACT_PROFILER_TYPE:
          case REACT_STRICT_MODE_TYPE:
          case REACT_SUSPENSE_TYPE:
            return type;

          default:
            var $$typeofType = type && type.$$typeof;

            switch ($$typeofType) {
              case REACT_CONTEXT_TYPE:
              case REACT_FORWARD_REF_TYPE:
              case REACT_LAZY_TYPE:
              case REACT_MEMO_TYPE:
              case REACT_PROVIDER_TYPE:
                return $$typeofType;

              default:
                return $$typeof;
            }

        }

      case REACT_PORTAL_TYPE:
        return $$typeof;
    }
  }

  return undefined;
} // AsyncMode is deprecated along with isAsyncMode

var AsyncMode = REACT_ASYNC_MODE_TYPE;
var ConcurrentMode = REACT_CONCURRENT_MODE_TYPE;
var ContextConsumer = REACT_CONTEXT_TYPE;
var ContextProvider = REACT_PROVIDER_TYPE;
var Element = REACT_ELEMENT_TYPE;
var ForwardRef = REACT_FORWARD_REF_TYPE;
var Fragment = REACT_FRAGMENT_TYPE;
var Lazy = REACT_LAZY_TYPE;
var Memo = REACT_MEMO_TYPE;
var Portal = REACT_PORTAL_TYPE;
var Profiler = REACT_PROFILER_TYPE;
var StrictMode = REACT_STRICT_MODE_TYPE;
var Suspense = REACT_SUSPENSE_TYPE;
var hasWarnedAboutDeprecatedIsAsyncMode = false; // AsyncMode should be deprecated

function isAsyncMode(object) {
  {
    if (!hasWarnedAboutDeprecatedIsAsyncMode) {
      hasWarnedAboutDeprecatedIsAsyncMode = true; // Using console['warn'] to evade Babel and ESLint

      console['warn']('The ReactIs.isAsyncMode() alias has been deprecated, ' + 'and will be removed in React 17+. Update your code to use ' + 'ReactIs.isConcurrentMode() instead. It has the exact same API.');
    }
  }

  return isConcurrentMode(object) || typeOf(object) === REACT_ASYNC_MODE_TYPE;
}
function isConcurrentMode(object) {
  return typeOf(object) === REACT_CONCURRENT_MODE_TYPE;
}
function isContextConsumer(object) {
  return typeOf(object) === REACT_CONTEXT_TYPE;
}
function isContextProvider(object) {
  return typeOf(object) === REACT_PROVIDER_TYPE;
}
function isElement(object) {
  return typeof object === 'object' && object !== null && object.$$typeof === REACT_ELEMENT_TYPE;
}
function isForwardRef(object) {
  return typeOf(object) === REACT_FORWARD_REF_TYPE;
}
function isFragment(object) {
  return typeOf(object) === REACT_FRAGMENT_TYPE;
}
function isLazy(object) {
  return typeOf(object) === REACT_LAZY_TYPE;
}
function isMemo(object) {
  return typeOf(object) === REACT_MEMO_TYPE;
}
function isPortal(object) {
  return typeOf(object) === REACT_PORTAL_TYPE;
}
function isProfiler(object) {
  return typeOf(object) === REACT_PROFILER_TYPE;
}
function isStrictMode(object) {
  return typeOf(object) === REACT_STRICT_MODE_TYPE;
}
function isSuspense(object) {
  return typeOf(object) === REACT_SUSPENSE_TYPE;
}

exports.AsyncMode = AsyncMode;
exports.ConcurrentMode = ConcurrentMode;
exports.ContextConsumer = ContextConsumer;
exports.ContextProvider = ContextProvider;
exports.Element = Element;
exports.ForwardRef = ForwardRef;
exports.Fragment = Fragment;
exports.Lazy = Lazy;
exports.Memo = Memo;
exports.Portal = Portal;
exports.Profiler = Profiler;
exports.StrictMode = StrictMode;
exports.Suspense = Suspense;
exports.isAsyncMode = isAsyncMode;
exports.isConcurrentMode = isConcurrentMode;
exports.isContextConsumer = isContextConsumer;
exports.isContextProvider = isContextProvider;
exports.isElement = isElement;
exports.isForwardRef = isForwardRef;
exports.isFragment = isFragment;
exports.isLazy = isLazy;
exports.isMemo = isMemo;
exports.isPortal = isPortal;
exports.isProfiler = isProfiler;
exports.isStrictMode = isStrictMode;
exports.isSuspense = isSuspense;
exports.isValidElementType = isValidElementType;
exports.typeOf = typeOf;
  })();
}
});
var reactIs_development_1 = reactIs_development.AsyncMode;
var reactIs_development_2 = reactIs_development.ConcurrentMode;
var reactIs_development_3 = reactIs_development.ContextConsumer;
var reactIs_development_4 = reactIs_development.ContextProvider;
var reactIs_development_5 = reactIs_development.Element;
var reactIs_development_6 = reactIs_development.ForwardRef;
var reactIs_development_7 = reactIs_development.Fragment;
var reactIs_development_8 = reactIs_development.Lazy;
var reactIs_development_9 = reactIs_development.Memo;
var reactIs_development_10 = reactIs_development.Portal;
var reactIs_development_11 = reactIs_development.Profiler;
var reactIs_development_12 = reactIs_development.StrictMode;
var reactIs_development_13 = reactIs_development.Suspense;
var reactIs_development_14 = reactIs_development.isAsyncMode;
var reactIs_development_15 = reactIs_development.isConcurrentMode;
var reactIs_development_16 = reactIs_development.isContextConsumer;
var reactIs_development_17 = reactIs_development.isContextProvider;
var reactIs_development_18 = reactIs_development.isElement;
var reactIs_development_19 = reactIs_development.isForwardRef;
var reactIs_development_20 = reactIs_development.isFragment;
var reactIs_development_21 = reactIs_development.isLazy;
var reactIs_development_22 = reactIs_development.isMemo;
var reactIs_development_23 = reactIs_development.isPortal;
var reactIs_development_24 = reactIs_development.isProfiler;
var reactIs_development_25 = reactIs_development.isStrictMode;
var reactIs_development_26 = reactIs_development.isSuspense;
var reactIs_development_27 = reactIs_development.isValidElementType;
var reactIs_development_28 = reactIs_development.typeOf;

var reactIs = createCommonjsModule(function (module) {

{
  module.exports = reactIs_development;
}
});
var reactIs_1 = reactIs.typeOf;
var reactIs_2 = reactIs.isElement;
var reactIs_3 = reactIs.isValidElementType;

function stylis_min (W) {
  function M(d, c, e, h, a) {
    for (var m = 0, b = 0, v = 0, n = 0, q, g, x = 0, K = 0, k, u = k = q = 0, l = 0, r = 0, I = 0, t = 0, B = e.length, J = B - 1, y, f = '', p = '', F = '', G = '', C; l < B;) {
      g = e.charCodeAt(l);
      l === J && 0 !== b + n + v + m && (0 !== b && (g = 47 === b ? 10 : 47), n = v = m = 0, B++, J++);

      if (0 === b + n + v + m) {
        if (l === J && (0 < r && (f = f.replace(N, '')), 0 < f.trim().length)) {
          switch (g) {
            case 32:
            case 9:
            case 59:
            case 13:
            case 10:
              break;

            default:
              f += e.charAt(l);
          }

          g = 59;
        }

        switch (g) {
          case 123:
            f = f.trim();
            q = f.charCodeAt(0);
            k = 1;

            for (t = ++l; l < B;) {
              switch (g = e.charCodeAt(l)) {
                case 123:
                  k++;
                  break;

                case 125:
                  k--;
                  break;

                case 47:
                  switch (g = e.charCodeAt(l + 1)) {
                    case 42:
                    case 47:
                      a: {
                        for (u = l + 1; u < J; ++u) {
                          switch (e.charCodeAt(u)) {
                            case 47:
                              if (42 === g && 42 === e.charCodeAt(u - 1) && l + 2 !== u) {
                                l = u + 1;
                                break a;
                              }

                              break;

                            case 10:
                              if (47 === g) {
                                l = u + 1;
                                break a;
                              }

                          }
                        }

                        l = u;
                      }

                  }

                  break;

                case 91:
                  g++;

                case 40:
                  g++;

                case 34:
                case 39:
                  for (; l++ < J && e.charCodeAt(l) !== g;) {
                  }

              }

              if (0 === k) break;
              l++;
            }

            k = e.substring(t, l);
            0 === q && (q = (f = f.replace(ca, '').trim()).charCodeAt(0));

            switch (q) {
              case 64:
                0 < r && (f = f.replace(N, ''));
                g = f.charCodeAt(1);

                switch (g) {
                  case 100:
                  case 109:
                  case 115:
                  case 45:
                    r = c;
                    break;

                  default:
                    r = O;
                }

                k = M(c, r, k, g, a + 1);
                t = k.length;
                0 < A && (r = X(O, f, I), C = H(3, k, r, c, D, z, t, g, a, h), f = r.join(''), void 0 !== C && 0 === (t = (k = C.trim()).length) && (g = 0, k = ''));
                if (0 < t) switch (g) {
                  case 115:
                    f = f.replace(da, ea);

                  case 100:
                  case 109:
                  case 45:
                    k = f + '{' + k + '}';
                    break;

                  case 107:
                    f = f.replace(fa, '$1 $2');
                    k = f + '{' + k + '}';
                    k = 1 === w || 2 === w && L('@' + k, 3) ? '@-webkit-' + k + '@' + k : '@' + k;
                    break;

                  default:
                    k = f + k, 112 === h && (k = (p += k, ''));
                } else k = '';
                break;

              default:
                k = M(c, X(c, f, I), k, h, a + 1);
            }

            F += k;
            k = I = r = u = q = 0;
            f = '';
            g = e.charCodeAt(++l);
            break;

          case 125:
          case 59:
            f = (0 < r ? f.replace(N, '') : f).trim();
            if (1 < (t = f.length)) switch (0 === u && (q = f.charCodeAt(0), 45 === q || 96 < q && 123 > q) && (t = (f = f.replace(' ', ':')).length), 0 < A && void 0 !== (C = H(1, f, c, d, D, z, p.length, h, a, h)) && 0 === (t = (f = C.trim()).length) && (f = '\x00\x00'), q = f.charCodeAt(0), g = f.charCodeAt(1), q) {
              case 0:
                break;

              case 64:
                if (105 === g || 99 === g) {
                  G += f + e.charAt(l);
                  break;
                }

              default:
                58 !== f.charCodeAt(t - 1) && (p += P(f, q, g, f.charCodeAt(2)));
            }
            I = r = u = q = 0;
            f = '';
            g = e.charCodeAt(++l);
        }
      }

      switch (g) {
        case 13:
        case 10:
          47 === b ? b = 0 : 0 === 1 + q && 107 !== h && 0 < f.length && (r = 1, f += '\x00');
          0 < A * Y && H(0, f, c, d, D, z, p.length, h, a, h);
          z = 1;
          D++;
          break;

        case 59:
        case 125:
          if (0 === b + n + v + m) {
            z++;
            break;
          }

        default:
          z++;
          y = e.charAt(l);

          switch (g) {
            case 9:
            case 32:
              if (0 === n + m + b) switch (x) {
                case 44:
                case 58:
                case 9:
                case 32:
                  y = '';
                  break;

                default:
                  32 !== g && (y = ' ');
              }
              break;

            case 0:
              y = '\\0';
              break;

            case 12:
              y = '\\f';
              break;

            case 11:
              y = '\\v';
              break;

            case 38:
              0 === n + b + m && (r = I = 1, y = '\f' + y);
              break;

            case 108:
              if (0 === n + b + m + E && 0 < u) switch (l - u) {
                case 2:
                  112 === x && 58 === e.charCodeAt(l - 3) && (E = x);

                case 8:
                  111 === K && (E = K);
              }
              break;

            case 58:
              0 === n + b + m && (u = l);
              break;

            case 44:
              0 === b + v + n + m && (r = 1, y += '\r');
              break;

            case 34:
            case 39:
              0 === b && (n = n === g ? 0 : 0 === n ? g : n);
              break;

            case 91:
              0 === n + b + v && m++;
              break;

            case 93:
              0 === n + b + v && m--;
              break;

            case 41:
              0 === n + b + m && v--;
              break;

            case 40:
              if (0 === n + b + m) {
                if (0 === q) switch (2 * x + 3 * K) {
                  case 533:
                    break;

                  default:
                    q = 1;
                }
                v++;
              }

              break;

            case 64:
              0 === b + v + n + m + u + k && (k = 1);
              break;

            case 42:
            case 47:
              if (!(0 < n + m + v)) switch (b) {
                case 0:
                  switch (2 * g + 3 * e.charCodeAt(l + 1)) {
                    case 235:
                      b = 47;
                      break;

                    case 220:
                      t = l, b = 42;
                  }

                  break;

                case 42:
                  47 === g && 42 === x && t + 2 !== l && (33 === e.charCodeAt(t + 2) && (p += e.substring(t, l + 1)), y = '', b = 0);
              }
          }

          0 === b && (f += y);
      }

      K = x;
      x = g;
      l++;
    }

    t = p.length;

    if (0 < t) {
      r = c;
      if (0 < A && (C = H(2, p, r, d, D, z, t, h, a, h), void 0 !== C && 0 === (p = C).length)) return G + p + F;
      p = r.join(',') + '{' + p + '}';

      if (0 !== w * E) {
        2 !== w || L(p, 2) || (E = 0);

        switch (E) {
          case 111:
            p = p.replace(ha, ':-moz-$1') + p;
            break;

          case 112:
            p = p.replace(Q, '::-webkit-input-$1') + p.replace(Q, '::-moz-$1') + p.replace(Q, ':-ms-input-$1') + p;
        }

        E = 0;
      }
    }

    return G + p + F;
  }

  function X(d, c, e) {
    var h = c.trim().split(ia);
    c = h;
    var a = h.length,
        m = d.length;

    switch (m) {
      case 0:
      case 1:
        var b = 0;

        for (d = 0 === m ? '' : d[0] + ' '; b < a; ++b) {
          c[b] = Z(d, c[b], e).trim();
        }

        break;

      default:
        var v = b = 0;

        for (c = []; b < a; ++b) {
          for (var n = 0; n < m; ++n) {
            c[v++] = Z(d[n] + ' ', h[b], e).trim();
          }
        }

    }

    return c;
  }

  function Z(d, c, e) {
    var h = c.charCodeAt(0);
    33 > h && (h = (c = c.trim()).charCodeAt(0));

    switch (h) {
      case 38:
        return c.replace(F, '$1' + d.trim());

      case 58:
        return d.trim() + c.replace(F, '$1' + d.trim());

      default:
        if (0 < 1 * e && 0 < c.indexOf('\f')) return c.replace(F, (58 === d.charCodeAt(0) ? '' : '$1') + d.trim());
    }

    return d + c;
  }

  function P(d, c, e, h) {
    var a = d + ';',
        m = 2 * c + 3 * e + 4 * h;

    if (944 === m) {
      d = a.indexOf(':', 9) + 1;
      var b = a.substring(d, a.length - 1).trim();
      b = a.substring(0, d).trim() + b + ';';
      return 1 === w || 2 === w && L(b, 1) ? '-webkit-' + b + b : b;
    }

    if (0 === w || 2 === w && !L(a, 1)) return a;

    switch (m) {
      case 1015:
        return 97 === a.charCodeAt(10) ? '-webkit-' + a + a : a;

      case 951:
        return 116 === a.charCodeAt(3) ? '-webkit-' + a + a : a;

      case 963:
        return 110 === a.charCodeAt(5) ? '-webkit-' + a + a : a;

      case 1009:
        if (100 !== a.charCodeAt(4)) break;

      case 969:
      case 942:
        return '-webkit-' + a + a;

      case 978:
        return '-webkit-' + a + '-moz-' + a + a;

      case 1019:
      case 983:
        return '-webkit-' + a + '-moz-' + a + '-ms-' + a + a;

      case 883:
        if (45 === a.charCodeAt(8)) return '-webkit-' + a + a;
        if (0 < a.indexOf('image-set(', 11)) return a.replace(ja, '$1-webkit-$2') + a;
        break;

      case 932:
        if (45 === a.charCodeAt(4)) switch (a.charCodeAt(5)) {
          case 103:
            return '-webkit-box-' + a.replace('-grow', '') + '-webkit-' + a + '-ms-' + a.replace('grow', 'positive') + a;

          case 115:
            return '-webkit-' + a + '-ms-' + a.replace('shrink', 'negative') + a;

          case 98:
            return '-webkit-' + a + '-ms-' + a.replace('basis', 'preferred-size') + a;
        }
        return '-webkit-' + a + '-ms-' + a + a;

      case 964:
        return '-webkit-' + a + '-ms-flex-' + a + a;

      case 1023:
        if (99 !== a.charCodeAt(8)) break;
        b = a.substring(a.indexOf(':', 15)).replace('flex-', '').replace('space-between', 'justify');
        return '-webkit-box-pack' + b + '-webkit-' + a + '-ms-flex-pack' + b + a;

      case 1005:
        return ka.test(a) ? a.replace(aa, ':-webkit-') + a.replace(aa, ':-moz-') + a : a;

      case 1e3:
        b = a.substring(13).trim();
        c = b.indexOf('-') + 1;

        switch (b.charCodeAt(0) + b.charCodeAt(c)) {
          case 226:
            b = a.replace(G, 'tb');
            break;

          case 232:
            b = a.replace(G, 'tb-rl');
            break;

          case 220:
            b = a.replace(G, 'lr');
            break;

          default:
            return a;
        }

        return '-webkit-' + a + '-ms-' + b + a;

      case 1017:
        if (-1 === a.indexOf('sticky', 9)) break;

      case 975:
        c = (a = d).length - 10;
        b = (33 === a.charCodeAt(c) ? a.substring(0, c) : a).substring(d.indexOf(':', 7) + 1).trim();

        switch (m = b.charCodeAt(0) + (b.charCodeAt(7) | 0)) {
          case 203:
            if (111 > b.charCodeAt(8)) break;

          case 115:
            a = a.replace(b, '-webkit-' + b) + ';' + a;
            break;

          case 207:
          case 102:
            a = a.replace(b, '-webkit-' + (102 < m ? 'inline-' : '') + 'box') + ';' + a.replace(b, '-webkit-' + b) + ';' + a.replace(b, '-ms-' + b + 'box') + ';' + a;
        }

        return a + ';';

      case 938:
        if (45 === a.charCodeAt(5)) switch (a.charCodeAt(6)) {
          case 105:
            return b = a.replace('-items', ''), '-webkit-' + a + '-webkit-box-' + b + '-ms-flex-' + b + a;

          case 115:
            return '-webkit-' + a + '-ms-flex-item-' + a.replace(ba, '') + a;

          default:
            return '-webkit-' + a + '-ms-flex-line-pack' + a.replace('align-content', '').replace(ba, '') + a;
        }
        break;

      case 973:
      case 989:
        if (45 !== a.charCodeAt(3) || 122 === a.charCodeAt(4)) break;

      case 931:
      case 953:
        if (!0 === la.test(d)) return 115 === (b = d.substring(d.indexOf(':') + 1)).charCodeAt(0) ? P(d.replace('stretch', 'fill-available'), c, e, h).replace(':fill-available', ':stretch') : a.replace(b, '-webkit-' + b) + a.replace(b, '-moz-' + b.replace('fill-', '')) + a;
        break;

      case 962:
        if (a = '-webkit-' + a + (102 === a.charCodeAt(5) ? '-ms-' + a : '') + a, 211 === e + h && 105 === a.charCodeAt(13) && 0 < a.indexOf('transform', 10)) return a.substring(0, a.indexOf(';', 27) + 1).replace(ma, '$1-webkit-$2') + a;
    }

    return a;
  }

  function L(d, c) {
    var e = d.indexOf(1 === c ? ':' : '{'),
        h = d.substring(0, 3 !== c ? e : 10);
    e = d.substring(e + 1, d.length - 1);
    return R(2 !== c ? h : h.replace(na, '$1'), e, c);
  }

  function ea(d, c) {
    var e = P(c, c.charCodeAt(0), c.charCodeAt(1), c.charCodeAt(2));
    return e !== c + ';' ? e.replace(oa, ' or ($1)').substring(4) : '(' + c + ')';
  }

  function H(d, c, e, h, a, m, b, v, n, q) {
    for (var g = 0, x = c, w; g < A; ++g) {
      switch (w = S[g].call(B, d, x, e, h, a, m, b, v, n, q)) {
        case void 0:
        case !1:
        case !0:
        case null:
          break;

        default:
          x = w;
      }
    }

    if (x !== c) return x;
  }

  function T(d) {
    switch (d) {
      case void 0:
      case null:
        A = S.length = 0;
        break;

      default:
        if ('function' === typeof d) S[A++] = d;else if ('object' === typeof d) for (var c = 0, e = d.length; c < e; ++c) {
          T(d[c]);
        } else Y = !!d | 0;
    }

    return T;
  }

  function U(d) {
    d = d.prefix;
    void 0 !== d && (R = null, d ? 'function' !== typeof d ? w = 1 : (w = 2, R = d) : w = 0);
    return U;
  }

  function B(d, c) {
    var e = d;
    33 > e.charCodeAt(0) && (e = e.trim());
    V = e;
    e = [V];

    if (0 < A) {
      var h = H(-1, c, e, e, D, z, 0, 0, 0, 0);
      void 0 !== h && 'string' === typeof h && (c = h);
    }

    var a = M(O, e, c, 0, 0);
    0 < A && (h = H(-2, a, e, e, D, z, a.length, 0, 0, 0), void 0 !== h && (a = h));
    V = '';
    E = 0;
    z = D = 1;
    return a;
  }

  var ca = /^\0+/g,
      N = /[\0\r\f]/g,
      aa = /: */g,
      ka = /zoo|gra/,
      ma = /([,: ])(transform)/g,
      ia = /,\r+?/g,
      F = /([\t\r\n ])*\f?&/g,
      fa = /@(k\w+)\s*(\S*)\s*/,
      Q = /::(place)/g,
      ha = /:(read-only)/g,
      G = /[svh]\w+-[tblr]{2}/,
      da = /\(\s*(.*)\s*\)/g,
      oa = /([\s\S]*?);/g,
      ba = /-self|flex-/g,
      na = /[^]*?(:[rp][el]a[\w-]+)[^]*/,
      la = /stretch|:\s*\w+\-(?:conte|avail)/,
      ja = /([^-])(image-set\()/,
      z = 1,
      D = 1,
      E = 0,
      w = 1,
      O = [],
      S = [],
      A = 0,
      R = null,
      Y = 0,
      V = '';
  B.use = T;
  B.set = U;
  void 0 !== W && U(W);
  return B;
}

var unitlessKeys = {
  animationIterationCount: 1,
  borderImageOutset: 1,
  borderImageSlice: 1,
  borderImageWidth: 1,
  boxFlex: 1,
  boxFlexGroup: 1,
  boxOrdinalGroup: 1,
  columnCount: 1,
  columns: 1,
  flex: 1,
  flexGrow: 1,
  flexPositive: 1,
  flexShrink: 1,
  flexNegative: 1,
  flexOrder: 1,
  gridRow: 1,
  gridRowEnd: 1,
  gridRowSpan: 1,
  gridRowStart: 1,
  gridColumn: 1,
  gridColumnEnd: 1,
  gridColumnSpan: 1,
  gridColumnStart: 1,
  msGridRow: 1,
  msGridRowSpan: 1,
  msGridColumn: 1,
  msGridColumnSpan: 1,
  fontWeight: 1,
  lineHeight: 1,
  opacity: 1,
  order: 1,
  orphans: 1,
  tabSize: 1,
  widows: 1,
  zIndex: 1,
  zoom: 1,
  WebkitLineClamp: 1,
  // SVG-related properties
  fillOpacity: 1,
  floodOpacity: 1,
  stopOpacity: 1,
  strokeDasharray: 1,
  strokeDashoffset: 1,
  strokeMiterlimit: 1,
  strokeOpacity: 1,
  strokeWidth: 1
};

function memoize$1(fn) {
  var cache = {};
  return function (arg) {
    if (cache[arg] === undefined) cache[arg] = fn(arg);
    return cache[arg];
  };
}

var reactPropsRegex = /^((children|dangerouslySetInnerHTML|key|ref|autoFocus|defaultValue|defaultChecked|innerHTML|suppressContentEditableWarning|suppressHydrationWarning|valueLink|accept|acceptCharset|accessKey|action|allow|allowUserMedia|allowPaymentRequest|allowFullScreen|allowTransparency|alt|async|autoComplete|autoPlay|capture|cellPadding|cellSpacing|challenge|charSet|checked|cite|classID|className|cols|colSpan|content|contentEditable|contextMenu|controls|controlsList|coords|crossOrigin|data|dateTime|decoding|default|defer|dir|disabled|disablePictureInPicture|download|draggable|encType|form|formAction|formEncType|formMethod|formNoValidate|formTarget|frameBorder|headers|height|hidden|high|href|hrefLang|htmlFor|httpEquiv|id|inputMode|integrity|is|keyParams|keyType|kind|label|lang|list|loading|loop|low|marginHeight|marginWidth|max|maxLength|media|mediaGroup|method|min|minLength|multiple|muted|name|nonce|noValidate|open|optimum|pattern|placeholder|playsInline|poster|preload|profile|radioGroup|readOnly|referrerPolicy|rel|required|reversed|role|rows|rowSpan|sandbox|scope|scoped|scrolling|seamless|selected|shape|size|sizes|slot|span|spellCheck|src|srcDoc|srcLang|srcSet|start|step|style|summary|tabIndex|target|title|type|useMap|value|width|wmode|wrap|about|datatype|inlist|prefix|property|resource|typeof|vocab|autoCapitalize|autoCorrect|autoSave|color|inert|itemProp|itemScope|itemType|itemID|itemRef|on|results|security|unselectable|accentHeight|accumulate|additive|alignmentBaseline|allowReorder|alphabetic|amplitude|arabicForm|ascent|attributeName|attributeType|autoReverse|azimuth|baseFrequency|baselineShift|baseProfile|bbox|begin|bias|by|calcMode|capHeight|clip|clipPathUnits|clipPath|clipRule|colorInterpolation|colorInterpolationFilters|colorProfile|colorRendering|contentScriptType|contentStyleType|cursor|cx|cy|d|decelerate|descent|diffuseConstant|direction|display|divisor|dominantBaseline|dur|dx|dy|edgeMode|elevation|enableBackground|end|exponent|externalResourcesRequired|fill|fillOpacity|fillRule|filter|filterRes|filterUnits|floodColor|floodOpacity|focusable|fontFamily|fontSize|fontSizeAdjust|fontStretch|fontStyle|fontVariant|fontWeight|format|from|fr|fx|fy|g1|g2|glyphName|glyphOrientationHorizontal|glyphOrientationVertical|glyphRef|gradientTransform|gradientUnits|hanging|horizAdvX|horizOriginX|ideographic|imageRendering|in|in2|intercept|k|k1|k2|k3|k4|kernelMatrix|kernelUnitLength|kerning|keyPoints|keySplines|keyTimes|lengthAdjust|letterSpacing|lightingColor|limitingConeAngle|local|markerEnd|markerMid|markerStart|markerHeight|markerUnits|markerWidth|mask|maskContentUnits|maskUnits|mathematical|mode|numOctaves|offset|opacity|operator|order|orient|orientation|origin|overflow|overlinePosition|overlineThickness|panose1|paintOrder|pathLength|patternContentUnits|patternTransform|patternUnits|pointerEvents|points|pointsAtX|pointsAtY|pointsAtZ|preserveAlpha|preserveAspectRatio|primitiveUnits|r|radius|refX|refY|renderingIntent|repeatCount|repeatDur|requiredExtensions|requiredFeatures|restart|result|rotate|rx|ry|scale|seed|shapeRendering|slope|spacing|specularConstant|specularExponent|speed|spreadMethod|startOffset|stdDeviation|stemh|stemv|stitchTiles|stopColor|stopOpacity|strikethroughPosition|strikethroughThickness|string|stroke|strokeDasharray|strokeDashoffset|strokeLinecap|strokeLinejoin|strokeMiterlimit|strokeOpacity|strokeWidth|surfaceScale|systemLanguage|tableValues|targetX|targetY|textAnchor|textDecoration|textRendering|textLength|to|transform|u1|u2|underlinePosition|underlineThickness|unicode|unicodeBidi|unicodeRange|unitsPerEm|vAlphabetic|vHanging|vIdeographic|vMathematical|values|vectorEffect|version|vertAdvY|vertOriginX|vertOriginY|viewBox|viewTarget|visibility|widths|wordSpacing|writingMode|x|xHeight|x1|x2|xChannelSelector|xlinkActuate|xlinkArcrole|xlinkHref|xlinkRole|xlinkShow|xlinkTitle|xlinkType|xmlBase|xmlns|xmlnsXlink|xmlLang|xmlSpace|y|y1|y2|yChannelSelector|z|zoomAndPan|for|class|autofocus)|(([Dd][Aa][Tt][Aa]|[Aa][Rr][Ii][Aa]|x)-.*))$/; // https://esbench.com/bench/5bfee68a4cd7e6009ef61d23

var index = memoize$1(function (prop) {
  return reactPropsRegex.test(prop) || prop.charCodeAt(0) === 111
  /* o */
  && prop.charCodeAt(1) === 110
  /* n */
  && prop.charCodeAt(2) < 91;
}
/* Z+1 */
);

/**
 * Copyright 2015, Yahoo! Inc.
 * Copyrights licensed under the New BSD License. See the accompanying LICENSE file for terms.
 */
var REACT_STATICS = {
  childContextTypes: true,
  contextType: true,
  contextTypes: true,
  defaultProps: true,
  displayName: true,
  getDefaultProps: true,
  getDerivedStateFromError: true,
  getDerivedStateFromProps: true,
  mixins: true,
  propTypes: true,
  type: true
};
var KNOWN_STATICS = {
  name: true,
  length: true,
  prototype: true,
  caller: true,
  callee: true,
  arguments: true,
  arity: true
};
var FORWARD_REF_STATICS = {
  '$$typeof': true,
  render: true,
  defaultProps: true,
  displayName: true,
  propTypes: true
};
var MEMO_STATICS = {
  '$$typeof': true,
  compare: true,
  defaultProps: true,
  displayName: true,
  propTypes: true,
  type: true
};
var TYPE_STATICS = {};
TYPE_STATICS[reactIs.ForwardRef] = FORWARD_REF_STATICS;
TYPE_STATICS[reactIs.Memo] = MEMO_STATICS;

function getStatics(component) {
  // React v16.11 and below
  if (reactIs.isMemo(component)) {
    return MEMO_STATICS;
  } // React v16.12 and above


  return TYPE_STATICS[component['$$typeof']] || REACT_STATICS;
}

var defineProperty = Object.defineProperty;
var getOwnPropertyNames = Object.getOwnPropertyNames;
var getOwnPropertySymbols = Object.getOwnPropertySymbols;
var getOwnPropertyDescriptor = Object.getOwnPropertyDescriptor;
var getPrototypeOf = Object.getPrototypeOf;
var objectPrototype = Object.prototype;
function hoistNonReactStatics(targetComponent, sourceComponent, blacklist) {
  if (typeof sourceComponent !== 'string') {
    // don't hoist over string (html) components
    if (objectPrototype) {
      var inheritedComponent = getPrototypeOf(sourceComponent);

      if (inheritedComponent && inheritedComponent !== objectPrototype) {
        hoistNonReactStatics(targetComponent, inheritedComponent, blacklist);
      }
    }

    var keys = getOwnPropertyNames(sourceComponent);

    if (getOwnPropertySymbols) {
      keys = keys.concat(getOwnPropertySymbols(sourceComponent));
    }

    var targetStatics = getStatics(targetComponent);
    var sourceStatics = getStatics(sourceComponent);

    for (var i = 0; i < keys.length; ++i) {
      var key = keys[i];

      if (!KNOWN_STATICS[key] && !(blacklist && blacklist[key]) && !(sourceStatics && sourceStatics[key]) && !(targetStatics && targetStatics[key])) {
        var descriptor = getOwnPropertyDescriptor(sourceComponent, key);

        try {
          // Avoid failures from read-only properties
          defineProperty(targetComponent, key, descriptor);
        } catch (e) {}
      }
    }
  }

  return targetComponent;
}

var hoistNonReactStatics_cjs = hoistNonReactStatics;

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

// 
var interleave = (function (strings, interpolations) {
  var result = [strings[0]];

  for (var i = 0, len = interpolations.length; i < len; i += 1) {
    result.push(interpolations[i], strings[i + 1]);
  }

  return result;
});

// 
var isPlainObject = (function (x) {
  return x !== null && typeof x === 'object' && (x.toString ? x.toString() : Object.prototype.toString.call(x)) === '[object Object]' && !reactIs_1(x);
});

// 
var EMPTY_ARRAY = Object.freeze([]);
var EMPTY_OBJECT = Object.freeze({});

// 
function isFunction$1(test) {
  return typeof test === 'function';
}

// 
function getComponentName(target) {
  return ( typeof target === 'string' && target ) || // $FlowFixMe
  target.displayName || // $FlowFixMe
  target.name || 'Component';
}

// 
function isStatelessFunction(test) {
  return typeof test === 'function' && !(test.prototype && test.prototype.isReactComponent);
}

// 
function isStyledComponent(target) {
  return target && typeof target.styledComponentId === 'string';
}

// 
var SC_ATTR = typeof process !== 'undefined' && ( process.env.SC_ATTR) || 'data-styled';
var SC_ATTR_ACTIVE = 'active';
var SC_ATTR_VERSION = 'data-styled-version';
var SC_VERSION = "5.1.1";
var SPLITTER = '/*!sc*/\n';
var IS_BROWSER = typeof window !== 'undefined' && 'HTMLElement' in window;
var DISABLE_SPEEDY = typeof SC_DISABLE_SPEEDY === 'boolean' && SC_DISABLE_SPEEDY || typeof process !== 'undefined' && ( process.env.SC_DISABLE_SPEEDY) || process.env.NODE_ENV !== 'production'; // Shared empty execution context when generating static styles

// 

/* eslint-disable camelcase, no-undef */
var getNonce = function getNonce() {
  return typeof __webpack_nonce__ !== 'undefined' ? __webpack_nonce__ : null;
};

var errorMap = {
  "1": "Cannot create styled-component for component: %s.\n\n",
  "2": "Can't collect styles once you've consumed a `ServerStyleSheet`'s styles! `ServerStyleSheet` is a one off instance for each server-side render cycle.\n\n- Are you trying to reuse it across renders?\n- Are you accidentally calling collectStyles twice?\n\n",
  "3": "Streaming SSR is only supported in a Node.js environment; Please do not try to call this method in the browser.\n\n",
  "4": "The `StyleSheetManager` expects a valid target or sheet prop!\n\n- Does this error occur on the client and is your target falsy?\n- Does this error occur on the server and is the sheet falsy?\n\n",
  "5": "The clone method cannot be used on the client!\n\n- Are you running in a client-like environment on the server?\n- Are you trying to run SSR on the client?\n\n",
  "6": "Trying to insert a new style tag, but the given Node is unmounted!\n\n- Are you using a custom target that isn't mounted?\n- Does your document not have a valid head element?\n- Have you accidentally removed a style tag manually?\n\n",
  "7": "ThemeProvider: Please return an object from your \"theme\" prop function, e.g.\n\n```js\ntheme={() => ({})}\n```\n\n",
  "8": "ThemeProvider: Please make your \"theme\" prop an object.\n\n",
  "9": "Missing document `<head>`\n\n",
  "10": "Cannot find a StyleSheet instance. Usually this happens if there are multiple copies of styled-components loaded at once. Check out this issue for how to troubleshoot and fix the common cases where this situation can happen: https://github.com/styled-components/styled-components/issues/1941#issuecomment-417862021\n\n",
  "11": "_This error was replaced with a dev-time warning, it will be deleted for v4 final._ [createGlobalStyle] received children which will not be rendered. Please use the component without passing children elements.\n\n",
  "12": "It seems you are interpolating a keyframe declaration (%s) into an untagged string. This was supported in styled-components v3, but is not longer supported in v4 as keyframes are now injected on-demand. Please wrap your string in the css\\`\\` helper which ensures the styles are injected correctly. See https://www.styled-components.com/docs/api#css\n\n",
  "13": "%s is not a styled component and cannot be referred to via component selector. See https://www.styled-components.com/docs/advanced#referring-to-other-components for more details.\n\n",
  "14": "ThemeProvider: \"theme\" prop is required.\n\n",
  "15": "A stylis plugin has been supplied that is not named. We need a name for each plugin to be able to prevent styling collisions between different stylis configurations within the same app. Before you pass your plugin to `<StyleSheetManager stylisPlugins={[]}>`, please make sure each plugin is uniquely-named, e.g.\n\n```js\nObject.defineProperty(importedPlugin, 'name', { value: 'some-unique-name' });\n```\n\n",
  "16": "Reached the limit of how many styled components may be created at group %s.\nYou may only create up to 1,073,741,824 components. If you're creating components dynamically,\nas for instance in your render method then you may be running into this limitation.\n\n",
  "17": "CSSStyleSheet could not be found on HTMLStyleElement.\nHas styled-components' style tag been unmounted or altered by another script?\n"
};

// 
var ERRORS =  errorMap ;
/**
 * super basic version of sprintf
 */

function format() {
  var a = arguments.length <= 0 ? undefined : arguments[0];
  var b = [];

  for (var c = 1, len = arguments.length; c < len; c += 1) {
    b.push(c < 0 || arguments.length <= c ? undefined : arguments[c]);
  }

  b.forEach(function (d) {
    a = a.replace(/%[a-z]/, d);
  });
  return a;
}
/**
 * Create an error file out of errors.md for development and a simple web link to the full errors
 * in production mode.
 */


function throwStyledComponentsError(code) {
  for (var _len = arguments.length, interpolations = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    interpolations[_key - 1] = arguments[_key];
  }

  {
    throw new Error(format.apply(void 0, [ERRORS[code]].concat(interpolations)).trim());
  }
}

// 
var ELEMENT_TYPE = 1;
/* Node.ELEMENT_TYPE */

/** Find last style element if any inside target */

var findLastStyleTag = function findLastStyleTag(target) {
  var childNodes = target.childNodes;

  for (var i = childNodes.length; i >= 0; i--) {
    var child = childNodes[i];

    if (child && child.nodeType === ELEMENT_TYPE && child.hasAttribute(SC_ATTR)) {
      return child;
    }
  }

  return undefined;
};
/** Create a style element inside `target` or <head> after the last */


var makeStyleTag = function makeStyleTag(target) {
  var head = document.head;
  var parent = target || head;
  var style = document.createElement('style');
  var prevStyle = findLastStyleTag(parent);
  var nextSibling = prevStyle !== undefined ? prevStyle.nextSibling : null;
  style.setAttribute(SC_ATTR, SC_ATTR_ACTIVE);
  style.setAttribute(SC_ATTR_VERSION, SC_VERSION);
  var nonce = getNonce();
  if (nonce) style.setAttribute('nonce', nonce);
  parent.insertBefore(style, nextSibling);
  return style;
};
/** Get the CSSStyleSheet instance for a given style element */

var getSheet = function getSheet(tag) {
  if (tag.sheet) {
    return tag.sheet;
  } // Avoid Firefox quirk where the style element might not have a sheet property


  var _document = document,
      styleSheets = _document.styleSheets;

  for (var i = 0, l = styleSheets.length; i < l; i++) {
    var sheet = styleSheets[i];

    if (sheet.ownerNode === tag) {
      return sheet;
    }
  }

  throwStyledComponentsError(17);
  return undefined;
};

// 
/** Create a CSSStyleSheet-like tag depending on the environment */

var makeTag = function makeTag(_ref) {
  var isServer = _ref.isServer,
      useCSSOMInjection = _ref.useCSSOMInjection,
      target = _ref.target;

  if (isServer) {
    return new VirtualTag(target);
  } else if (useCSSOMInjection) {
    return new CSSOMTag(target);
  } else {
    return new TextTag(target);
  }
};
var CSSOMTag = /*#__PURE__*/function () {
  function CSSOMTag(target) {
    var element = this.element = makeStyleTag(target); // Avoid Edge bug where empty style elements don't create sheets

    element.appendChild(document.createTextNode(''));
    this.sheet = getSheet(element);
    this.length = 0;
  }

  var _proto = CSSOMTag.prototype;

  _proto.insertRule = function insertRule(index, rule) {
    try {
      this.sheet.insertRule(rule, index);
      this.length++;
      return true;
    } catch (_error) {
      return false;
    }
  };

  _proto.deleteRule = function deleteRule(index) {
    this.sheet.deleteRule(index);
    this.length--;
  };

  _proto.getRule = function getRule(index) {
    var rule = this.sheet.cssRules[index]; // Avoid IE11 quirk where cssText is inaccessible on some invalid rules

    if (rule !== undefined && typeof rule.cssText === 'string') {
      return rule.cssText;
    } else {
      return '';
    }
  };

  return CSSOMTag;
}();
/** A Tag that emulates the CSSStyleSheet API but uses text nodes */

var TextTag = /*#__PURE__*/function () {
  function TextTag(target) {
    var element = this.element = makeStyleTag(target);
    this.nodes = element.childNodes;
    this.length = 0;
  }

  var _proto2 = TextTag.prototype;

  _proto2.insertRule = function insertRule(index, rule) {
    if (index <= this.length && index >= 0) {
      var node = document.createTextNode(rule);
      var refNode = this.nodes[index];
      this.element.insertBefore(node, refNode || null);
      this.length++;
      return true;
    } else {
      return false;
    }
  };

  _proto2.deleteRule = function deleteRule(index) {
    this.element.removeChild(this.nodes[index]);
    this.length--;
  };

  _proto2.getRule = function getRule(index) {
    if (index < this.length) {
      return this.nodes[index].textContent;
    } else {
      return '';
    }
  };

  return TextTag;
}();
/** A completely virtual (server-side) Tag that doesn't manipulate the DOM */

var VirtualTag = /*#__PURE__*/function () {
  function VirtualTag(_target) {
    this.rules = [];
    this.length = 0;
  }

  var _proto3 = VirtualTag.prototype;

  _proto3.insertRule = function insertRule(index, rule) {
    if (index <= this.length) {
      this.rules.splice(index, 0, rule);
      this.length++;
      return true;
    } else {
      return false;
    }
  };

  _proto3.deleteRule = function deleteRule(index) {
    this.rules.splice(index, 1);
    this.length--;
  };

  _proto3.getRule = function getRule(index) {
    if (index < this.length) {
      return this.rules[index];
    } else {
      return '';
    }
  };

  return VirtualTag;
}();

// 
/** Create a GroupedTag with an underlying Tag implementation */

var makeGroupedTag = function makeGroupedTag(tag) {
  return new DefaultGroupedTag(tag);
};
var BASE_SIZE = 1 << 9;

var DefaultGroupedTag = /*#__PURE__*/function () {
  function DefaultGroupedTag(tag) {
    this.groupSizes = new Uint32Array(BASE_SIZE);
    this.length = BASE_SIZE;
    this.tag = tag;
  }

  var _proto = DefaultGroupedTag.prototype;

  _proto.indexOfGroup = function indexOfGroup(group) {
    var index = 0;

    for (var i = 0; i < group; i++) {
      index += this.groupSizes[i];
    }

    return index;
  };

  _proto.insertRules = function insertRules(group, rules) {
    if (group >= this.groupSizes.length) {
      var oldBuffer = this.groupSizes;
      var oldSize = oldBuffer.length;
      var newSize = oldSize;

      while (group >= newSize) {
        newSize <<= 1;

        if (newSize < 0) {
          throwStyledComponentsError(16, "" + group);
        }
      }

      this.groupSizes = new Uint32Array(newSize);
      this.groupSizes.set(oldBuffer);
      this.length = newSize;

      for (var i = oldSize; i < newSize; i++) {
        this.groupSizes[i] = 0;
      }
    }

    var ruleIndex = this.indexOfGroup(group + 1);

    for (var _i = 0, l = rules.length; _i < l; _i++) {
      if (this.tag.insertRule(ruleIndex, rules[_i])) {
        this.groupSizes[group]++;
        ruleIndex++;
      }
    }
  };

  _proto.clearGroup = function clearGroup(group) {
    if (group < this.length) {
      var length = this.groupSizes[group];
      var startIndex = this.indexOfGroup(group);
      var endIndex = startIndex + length;
      this.groupSizes[group] = 0;

      for (var i = startIndex; i < endIndex; i++) {
        this.tag.deleteRule(startIndex);
      }
    }
  };

  _proto.getGroup = function getGroup(group) {
    var css = '';

    if (group >= this.length || this.groupSizes[group] === 0) {
      return css;
    }

    var length = this.groupSizes[group];
    var startIndex = this.indexOfGroup(group);
    var endIndex = startIndex + length;

    for (var i = startIndex; i < endIndex; i++) {
      css += "" + this.tag.getRule(i) + SPLITTER;
    }

    return css;
  };

  return DefaultGroupedTag;
}();

// 
var MAX_SMI = 1 << 31 - 1;
var groupIDRegister = new Map();
var reverseRegister = new Map();
var nextFreeGroup = 1;
var getGroupForId = function getGroupForId(id) {
  if (groupIDRegister.has(id)) {
    return groupIDRegister.get(id);
  }

  var group = nextFreeGroup++;

  if ( ((group | 0) < 0 || group > MAX_SMI)) {
    throwStyledComponentsError(16, "" + group);
  }

  groupIDRegister.set(id, group);
  reverseRegister.set(group, id);
  return group;
};
var getIdForGroup = function getIdForGroup(group) {
  return reverseRegister.get(group);
};
var setGroupForId = function setGroupForId(id, group) {
  if (group >= nextFreeGroup) {
    nextFreeGroup = group + 1;
  }

  groupIDRegister.set(id, group);
  reverseRegister.set(group, id);
};

// 
var SELECTOR = "style[" + SC_ATTR + "][" + SC_ATTR_VERSION + "=\"" + SC_VERSION + "\"]";
var MARKER_RE = new RegExp("^" + SC_ATTR + "\\.g(\\d+)\\[id=\"([\\w\\d-]+)\"\\].*?\"([^\"]*)");
var outputSheet = function outputSheet(sheet) {
  var tag = sheet.getTag();
  var length = tag.length;
  var css = '';

  for (var group = 0; group < length; group++) {
    var id = getIdForGroup(group);
    if (id === undefined) continue;
    var names = sheet.names.get(id);
    var rules = tag.getGroup(group);
    if (names === undefined || rules.length === 0) continue;
    var selector = SC_ATTR + ".g" + group + "[id=\"" + id + "\"]";
    var content = '';

    if (names !== undefined) {
      names.forEach(function (name) {
        if (name.length > 0) {
          content += name + ",";
        }
      });
    } // NOTE: It's easier to collect rules and have the marker
    // after the actual rules to simplify the rehydration


    css += "" + rules + selector + "{content:\"" + content + "\"}" + SPLITTER;
  }

  return css;
};

var rehydrateNamesFromContent = function rehydrateNamesFromContent(sheet, id, content) {
  var names = content.split(',');
  var name;

  for (var i = 0, l = names.length; i < l; i++) {
    // eslint-disable-next-line
    if (name = names[i]) {
      sheet.registerName(id, name);
    }
  }
};

var rehydrateSheetFromTag = function rehydrateSheetFromTag(sheet, style) {
  var parts = style.innerHTML.split(SPLITTER);
  var rules = [];

  for (var i = 0, l = parts.length; i < l; i++) {
    var part = parts[i].trim();
    if (!part) continue;
    var marker = part.match(MARKER_RE);

    if (marker) {
      var group = parseInt(marker[1], 10) | 0;
      var id = marker[2];

      if (group !== 0) {
        // Rehydrate componentId to group index mapping
        setGroupForId(id, group); // Rehydrate names and rules
        // looks like: data-styled.g11[id="idA"]{content:"nameA,"}

        rehydrateNamesFromContent(sheet, id, marker[3]);
        sheet.getTag().insertRules(group, rules);
      }

      rules.length = 0;
    } else {
      rules.push(part);
    }
  }
};

var rehydrateSheet = function rehydrateSheet(sheet) {
  var nodes = document.querySelectorAll(SELECTOR);

  for (var i = 0, l = nodes.length; i < l; i++) {
    var node = nodes[i];

    if (node && node.getAttribute(SC_ATTR) !== SC_ATTR_ACTIVE) {
      rehydrateSheetFromTag(sheet, node);

      if (node.parentNode) {
        node.parentNode.removeChild(node);
      }
    }
  }
};

var SHOULD_REHYDRATE = IS_BROWSER;
var defaultOptions = {
  isServer: !IS_BROWSER,
  useCSSOMInjection: !DISABLE_SPEEDY
};
/** Contains the main stylesheet logic for stringification and caching */

var StyleSheet = /*#__PURE__*/function () {
  /** Register a group ID to give it an index */
  StyleSheet.registerId = function registerId(id) {
    return getGroupForId(id);
  };

  function StyleSheet(options, globalStyles, names) {
    if (options === void 0) {
      options = defaultOptions;
    }

    if (globalStyles === void 0) {
      globalStyles = {};
    }

    this.options = _extends({}, defaultOptions, {}, options);
    this.gs = globalStyles;
    this.names = new Map(names); // We rehydrate only once and use the sheet that is created first

    if (!this.options.isServer && IS_BROWSER && SHOULD_REHYDRATE) {
      SHOULD_REHYDRATE = false;
      rehydrateSheet(this);
    }
  }

  var _proto = StyleSheet.prototype;

  _proto.reconstructWithOptions = function reconstructWithOptions(options) {
    return new StyleSheet(_extends({}, this.options, {}, options), this.gs, this.names);
  };

  _proto.allocateGSInstance = function allocateGSInstance(id) {
    return this.gs[id] = (this.gs[id] || 0) + 1;
  }
  /** Lazily initialises a GroupedTag for when it's actually needed */
  ;

  _proto.getTag = function getTag() {
    return this.tag || (this.tag = makeGroupedTag(makeTag(this.options)));
  }
  /** Check whether a name is known for caching */
  ;

  _proto.hasNameForId = function hasNameForId(id, name) {
    return this.names.has(id) && this.names.get(id).has(name);
  }
  /** Mark a group's name as known for caching */
  ;

  _proto.registerName = function registerName(id, name) {
    getGroupForId(id);

    if (!this.names.has(id)) {
      var groupNames = new Set();
      groupNames.add(name);
      this.names.set(id, groupNames);
    } else {
      this.names.get(id).add(name);
    }
  }
  /** Insert new rules which also marks the name as known */
  ;

  _proto.insertRules = function insertRules(id, name, rules) {
    this.registerName(id, name);
    this.getTag().insertRules(getGroupForId(id), rules);
  }
  /** Clears all cached names for a given group ID */
  ;

  _proto.clearNames = function clearNames(id) {
    if (this.names.has(id)) {
      this.names.get(id).clear();
    }
  }
  /** Clears all rules for a given group ID */
  ;

  _proto.clearRules = function clearRules(id) {
    this.getTag().clearGroup(getGroupForId(id));
    this.clearNames(id);
  }
  /** Clears the entire tag which deletes all rules but not its names */
  ;

  _proto.clearTag = function clearTag() {
    // NOTE: This does not clear the names, since it's only used during SSR
    // so that we can continuously output only new rules
    this.tag = undefined;
  }
  /** Outputs the current sheet as a CSS string with markers for SSR */
  ;

  _proto.toString = function toString() {
    return outputSheet(this);
  };

  return StyleSheet;
}();

// 

/* eslint-disable */
var SEED = 5381; // When we have separate strings it's useful to run a progressive
// version of djb2 where we pretend that we're still looping over
// the same string

var phash = function phash(h, x) {
  var i = x.length;

  while (i) {
    h = h * 33 ^ x.charCodeAt(--i);
  }

  return h;
}; // This is a djb2 hashing function

var hash = function hash(x) {
  return phash(SEED, x);
};

/**
 * MIT License
 *
 * Copyright (c) 2016 Sultan Tarimo
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of
 * this software and associated documentation files (the "Software"),
 * to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or
 * sell copies of the Software and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS
 * OR IMPLIED INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY,
 * WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR
 * IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

/* eslint-disable */
function insertRulePlugin (insertRule) {
  var delimiter = '/*|*/';
  var needle = delimiter + "}";

  function toSheet(block) {
    if (block) {
      try {
        insertRule(block + "}");
      } catch (e) {}
    }
  }

  return function ruleSheet(context, content, selectors, parents, line, column, length, ns, depth, at) {
    switch (context) {
      // property
      case 1:
        // @import
        if (depth === 0 && content.charCodeAt(0) === 64) return insertRule(content + ";"), '';
        break;
      // selector

      case 2:
        if (ns === 0) return content + delimiter;
        break;
      // at-rule

      case 3:
        switch (ns) {
          // @font-face, @page
          case 102:
          case 112:
            return insertRule(selectors[0] + content), '';

          default:
            return content + (at === 0 ? delimiter : '');
        }

      case -2:
        content.split(needle).forEach(toSheet);
    }
  };
}

var COMMENT_REGEX = /^\s*\/\/.*$/gm;
function createStylisInstance(_temp) {
  var _ref = _temp === void 0 ? EMPTY_OBJECT : _temp,
      _ref$options = _ref.options,
      options = _ref$options === void 0 ? EMPTY_OBJECT : _ref$options,
      _ref$plugins = _ref.plugins,
      plugins = _ref$plugins === void 0 ? EMPTY_ARRAY : _ref$plugins;

  var stylis = new stylis_min(options); // Wrap `insertRulePlugin to build a list of rules,
  // and then make our own plugin to return the rules. This
  // makes it easier to hook into the existing SSR architecture

  var parsingRules = []; // eslint-disable-next-line consistent-return

  var returnRulesPlugin = function returnRulesPlugin(context) {
    if (context === -2) {
      var parsedRules = parsingRules;
      parsingRules = [];
      return parsedRules;
    }
  };

  var parseRulesPlugin = insertRulePlugin(function (rule) {
    parsingRules.push(rule);
  });

  var _componentId;

  var _selector;

  var _selectorRegexp;

  var selfReferenceReplacer = function selfReferenceReplacer(match, offset, string) {
    if ( // the first self-ref is always untouched
    offset > 0 && // there should be at least two self-refs to do a replacement (.b > .b)
    string.slice(0, offset).indexOf(_selector) !== -1 && // no consecutive self refs (.b.b); that is a precedence boost and treated differently
    string.slice(offset - _selector.length, offset) !== _selector) {
      return "." + _componentId;
    }

    return match;
  };
  /**
   * When writing a style like
   *
   * & + & {
   *   color: red;
   * }
   *
   * The second ampersand should be a reference to the static component class. stylis
   * has no knowledge of static class so we have to intelligently replace the base selector.
   *
   * https://github.com/thysultan/stylis.js#plugins <- more info about the context phase values
   * "2" means this plugin is taking effect at the very end after all other processing is complete
   */


  var selfReferenceReplacementPlugin = function selfReferenceReplacementPlugin(context, _, selectors) {
    if (context === 2 && selectors.length && selectors[0].lastIndexOf(_selector) > 0) {
      // eslint-disable-next-line no-param-reassign
      selectors[0] = selectors[0].replace(_selectorRegexp, selfReferenceReplacer);
    }
  };

  stylis.use([].concat(plugins, [selfReferenceReplacementPlugin, parseRulesPlugin, returnRulesPlugin]));

  function stringifyRules(css, selector, prefix, componentId) {
    if (componentId === void 0) {
      componentId = '&';
    }

    var flatCSS = css.replace(COMMENT_REGEX, '');
    var cssStr = selector && prefix ? prefix + " " + selector + " { " + flatCSS + " }" : flatCSS; // stylis has no concept of state to be passed to plugins
    // but since JS is single=threaded, we can rely on that to ensure
    // these properties stay in sync with the current stylis run

    _componentId = componentId;
    _selector = selector;
    _selectorRegexp = new RegExp("\\" + _selector + "\\b", 'g');
    return stylis(prefix || !selector ? '' : selector, cssStr);
  }

  stringifyRules.hash = plugins.length ? plugins.reduce(function (acc, plugin) {
    if (!plugin.name) {
      throwStyledComponentsError(15);
    }

    return phash(acc, plugin.name);
  }, SEED).toString() : '';
  return stringifyRules;
}

// 
var StyleSheetContext = React__default.createContext();
var StyleSheetConsumer = StyleSheetContext.Consumer;
var StylisContext = React__default.createContext();
var StylisConsumer = StylisContext.Consumer;
var masterSheet = new StyleSheet();
var masterStylis = createStylisInstance();
function useStyleSheet() {
  return useContext(StyleSheetContext) || masterSheet;
}
function useStylis() {
  return useContext(StylisContext) || masterStylis;
}

// 

var Keyframes = /*#__PURE__*/function () {
  function Keyframes(name, stringifyArgs) {
    var _this = this;

    this.inject = function (styleSheet) {
      if (!styleSheet.hasNameForId(_this.id, _this.name)) {
        styleSheet.insertRules(_this.id, _this.name, masterStylis.apply(void 0, _this.stringifyArgs));
      }
    };

    this.toString = function () {
      return throwStyledComponentsError(12, String(_this.name));
    };

    this.name = name;
    this.id = "sc-keyframes-" + name;
    this.stringifyArgs = stringifyArgs;
  }

  var _proto = Keyframes.prototype;

  _proto.getName = function getName() {
    return this.name;
  };

  return Keyframes;
}();

// 

/**
 * inlined version of
 * https://github.com/facebook/fbjs/blob/master/packages/fbjs/src/core/hyphenateStyleName.js
 */
var uppercasePattern = /([A-Z])/g;
var msPattern = /^ms-/;
/**
 * Hyphenates a camelcased CSS property name, for example:
 *
 *   > hyphenateStyleName('backgroundColor')
 *   < "background-color"
 *   > hyphenateStyleName('MozTransition')
 *   < "-moz-transition"
 *   > hyphenateStyleName('msTransition')
 *   < "-ms-transition"
 *
 * As Modernizr suggests (http://modernizr.com/docs/#prefixed), an `ms` prefix
 * is converted to `-ms-`.
 *
 * @param {string} string
 * @return {string}
 */

function hyphenateStyleName(string) {
  return string.replace(uppercasePattern, '-$1').toLowerCase().replace(msPattern, '-ms-');
}

// 

function addUnitIfNeeded(name, value) {
  // https://github.com/amilajack/eslint-plugin-flowtype-errors/issues/133
  // $FlowFixMe
  if (value == null || typeof value === 'boolean' || value === '') {
    return '';
  }

  if (typeof value === 'number' && value !== 0 && !(name in unitlessKeys)) {
    return value + "px"; // Presumes implicit 'px' suffix for unitless numbers
  }

  return String(value).trim();
}

// 
/**
 * It's falsish not falsy because 0 is allowed.
 */

var isFalsish = function isFalsish(chunk) {
  return chunk === undefined || chunk === null || chunk === false || chunk === '';
};

var objToCssArray = function objToCssArray(obj, prevKey) {
  var rules = [];
  var keys = Object.keys(obj);
  keys.forEach(function (key) {
    if (!isFalsish(obj[key])) {
      if (isPlainObject(obj[key])) {
        rules.push.apply(rules, objToCssArray(obj[key], key));
        return rules;
      } else if (isFunction$1(obj[key])) {
        rules.push(hyphenateStyleName(key) + ":", obj[key], ';');
        return rules;
      }

      rules.push(hyphenateStyleName(key) + ": " + addUnitIfNeeded(key, obj[key]) + ";");
    }

    return rules;
  });
  return prevKey ? [prevKey + " {"].concat(rules, ['}']) : rules;
};
function flatten(chunk, executionContext, styleSheet) {
  if (Array.isArray(chunk)) {
    var ruleSet = [];

    for (var i = 0, len = chunk.length, result; i < len; i += 1) {
      result = flatten(chunk[i], executionContext, styleSheet);
      if (result === '') continue;else if (Array.isArray(result)) ruleSet.push.apply(ruleSet, result);else ruleSet.push(result);
    }

    return ruleSet;
  }

  if (isFalsish(chunk)) {
    return '';
  }
  /* Handle other components */


  if (isStyledComponent(chunk)) {
    return "." + chunk.styledComponentId;
  }
  /* Either execute or defer the function */


  if (isFunction$1(chunk)) {
    if (isStatelessFunction(chunk) && executionContext) {
      var _result = chunk(executionContext);

      if ( reactIs_2(_result)) {
        // eslint-disable-next-line no-console
        console.warn(getComponentName(chunk) + " is not a styled component and cannot be referred to via component selector. See https://www.styled-components.com/docs/advanced#referring-to-other-components for more details.");
      }

      return flatten(_result, executionContext, styleSheet);
    } else return chunk;
  }

  if (chunk instanceof Keyframes) {
    if (styleSheet) {
      chunk.inject(styleSheet);
      return chunk.getName();
    } else return chunk;
  }
  /* Handle objects */


  return isPlainObject(chunk) ? objToCssArray(chunk) : chunk.toString();
}

// 
function css(styles) {
  for (var _len = arguments.length, interpolations = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    interpolations[_key - 1] = arguments[_key];
  }

  if (isFunction$1(styles) || isPlainObject(styles)) {
    // $FlowFixMe
    return flatten(interleave(EMPTY_ARRAY, [styles].concat(interpolations)));
  }

  if (interpolations.length === 0 && styles.length === 1 && typeof styles[0] === "string") {
    // $FlowFixMe
    return styles;
  } // $FlowFixMe


  return flatten(interleave(styles, interpolations));
}

function constructWithOptions(componentConstructor, tag, options) {
  if (options === void 0) {
    options = EMPTY_OBJECT;
  }

  if (!reactIs_3(tag)) {
    return throwStyledComponentsError(1, String(tag));
  }
  /* This is callable directly as a template function */
  // $FlowFixMe: Not typed to avoid destructuring arguments


  var templateFunction = function templateFunction() {
    return componentConstructor(tag, options, css.apply(void 0, arguments));
  };
  /* If config methods are called, wrap up a new template function and merge options */


  templateFunction.withConfig = function (config) {
    return constructWithOptions(componentConstructor, tag, _extends({}, options, {}, config));
  };
  /* Modify/inject new props at runtime */


  templateFunction.attrs = function (attrs) {
    return constructWithOptions(componentConstructor, tag, _extends({}, options, {
      attrs: Array.prototype.concat(options.attrs, attrs).filter(Boolean)
    }));
  };

  return templateFunction;
}

/* eslint-disable */

/**
  mixin-deep; https://github.com/jonschlinkert/mixin-deep
  Inlined such that it will be consistently transpiled to an IE-compatible syntax.

  The MIT License (MIT)

  Copyright (c) 2014-present, Jon Schlinkert.

  Permission is hereby granted, free of charge, to any person obtaining a copy
  of this software and associated documentation files (the "Software"), to deal
  in the Software without restriction, including without limitation the rights
  to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
  copies of the Software, and to permit persons to whom the Software is
  furnished to do so, subject to the following conditions:

  The above copyright notice and this permission notice shall be included in
  all copies or substantial portions of the Software.

  THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
  IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
  FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
  AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
  LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
  OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
  THE SOFTWARE.
*/
var isObject$1 = function isObject(val) {
  return typeof val === 'function' || typeof val === 'object' && val !== null && !Array.isArray(val);
};

var isValidKey = function isValidKey(key) {
  return key !== '__proto__' && key !== 'constructor' && key !== 'prototype';
};

function mixin(target, val, key) {
  var obj = target[key];

  if (isObject$1(val) && isObject$1(obj)) {
    mixinDeep(obj, val);
  } else {
    target[key] = val;
  }
}

function mixinDeep(target) {
  for (var _len = arguments.length, rest = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    rest[_key - 1] = arguments[_key];
  }

  for (var _i = 0, _rest = rest; _i < _rest.length; _i++) {
    var obj = _rest[_i];

    if (isObject$1(obj)) {
      for (var key in obj) {
        if (isValidKey(key)) {
          mixin(target, obj[key], key);
        }
      }
    }
  }

  return target;
}

// 

/* eslint-disable no-bitwise */
var AD_REPLACER_R = /(a)(d)/gi;
/* This is the "capacity" of our alphabet i.e. 2x26 for all letters plus their capitalised
 * counterparts */

var charsLength = 52;
/* start at 75 for 'a' until 'z' (25) and then start at 65 for capitalised letters */

var getAlphabeticChar = function getAlphabeticChar(code) {
  return String.fromCharCode(code + (code > 25 ? 39 : 97));
};
/* input a number, usually a hash and convert it to base-52 */


function generateAlphabeticName(code) {
  var name = '';
  var x;
  /* get a char and divide by alphabet-length */

  for (x = Math.abs(code); x > charsLength; x = x / charsLength | 0) {
    name = getAlphabeticChar(x % charsLength) + name;
  }

  return (getAlphabeticChar(x % charsLength) + name).replace(AD_REPLACER_R, '$1-$2');
}

// 
/*
 ComponentStyle is all the CSS-specific stuff, not
 the React-specific stuff.
 */

var ComponentStyle = /*#__PURE__*/function () {
  function ComponentStyle(rules, componentId) {
    this.rules = rules;
    this.staticRulesId = '';
    this.isStatic = process.env.NODE_ENV === 'production' ;
    this.componentId = componentId;
    this.baseHash = hash(componentId); // NOTE: This registers the componentId, which ensures a consistent order
    // for this component's styles compared to others

    StyleSheet.registerId(componentId);
  }
  /*
   * Flattens a rule set into valid CSS
   * Hashes it, wraps the whole chunk in a .hash1234 {}
   * Returns the hash to be injected on render()
   * */


  var _proto = ComponentStyle.prototype;

  _proto.generateAndInjectStyles = function generateAndInjectStyles(executionContext, styleSheet, stylis) {
    var componentId = this.componentId; // force dynamic classnames if user-supplied stylis plugins are in use

    if (this.isStatic && !stylis.hash) {
      if (this.staticRulesId && styleSheet.hasNameForId(componentId, this.staticRulesId)) {
        return this.staticRulesId;
      }

      var cssStatic = flatten(this.rules, executionContext, styleSheet).join('');
      var name = generateAlphabeticName(phash(this.baseHash, cssStatic.length) >>> 0);

      if (!styleSheet.hasNameForId(componentId, name)) {
        var cssStaticFormatted = stylis(cssStatic, "." + name, undefined, componentId);
        styleSheet.insertRules(componentId, name, cssStaticFormatted);
      }

      this.staticRulesId = name;
      return name;
    } else {
      var length = this.rules.length;
      var dynamicHash = phash(this.baseHash, stylis.hash);
      var css = '';

      for (var i = 0; i < length; i++) {
        var partRule = this.rules[i];

        if (typeof partRule === 'string') {
          css += partRule;
          dynamicHash = phash(dynamicHash, partRule + i);
        } else {
          var partChunk = flatten(partRule, executionContext, styleSheet);
          var partString = Array.isArray(partChunk) ? partChunk.join('') : partChunk;
          dynamicHash = phash(dynamicHash, partString + i);
          css += partString;
        }
      }

      var _name = generateAlphabeticName(dynamicHash >>> 0);

      if (!styleSheet.hasNameForId(componentId, _name)) {
        var cssFormatted = stylis(css, "." + _name, undefined, componentId);
        styleSheet.insertRules(componentId, _name, cssFormatted);
      }

      return _name;
    }
  };

  return ComponentStyle;
}();

// 
var LIMIT = 200;
var createWarnTooManyClasses = (function (displayName, componentId) {
  var generatedClasses = {};
  var warningSeen = false;
  return function (className) {
    if (!warningSeen) {
      generatedClasses[className] = true;

      if (Object.keys(generatedClasses).length >= LIMIT) {
        // Unable to find latestRule in test environment.

        /* eslint-disable no-console, prefer-template */
        var parsedIdString = componentId ? " with the id of \"" + componentId + "\"" : '';
        console.warn("Over " + LIMIT + " classes were generated for component " + displayName + parsedIdString + ".\n" + 'Consider using the attrs method, together with a style object for frequently changed styles.\n' + 'Example:\n' + '  const Component = styled.div.attrs(props => ({\n' + '    style: {\n' + '      background: props.background,\n' + '    },\n' + '  }))`width: 100%;`\n\n' + '  <Component />');
        warningSeen = true;
        generatedClasses = {};
      }
    }
  };
});

// 
var invalidHookCallRe = /invalid hook call/i;
var seen = new Set();
var checkDynamicCreation = function checkDynamicCreation(displayName, componentId) {
  {
    var parsedIdString = componentId ? " with the id of \"" + componentId + "\"" : '';
    var message = "The component " + displayName + parsedIdString + " has been created dynamically.\n" + 'You may see this warning because you\'ve called styled inside another component.\n' + 'To resolve this only create new StyledComponents outside of any render method and function component.';

    try {
      // We purposefully call `useRef` outside of a component and expect it to throw
      // If it doesn't, then we're inside another component.
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useRef();

      if (!seen.has(message)) {
        // eslint-disable-next-line no-console
        console.warn(message);
        seen.add(message);
      }
    } catch (error) {
      // The error here is expected, since we're expecting anything that uses `checkDynamicCreation` to
      // be called outside of a React component.
      if (invalidHookCallRe.test(error.message)) {
        // This shouldn't happen, but resets `warningSeen` if we had this error happen intermittently
        seen["delete"](message);
      }
    }
  }
};

// 
var determineTheme = (function (props, providedTheme, defaultProps) {
  if (defaultProps === void 0) {
    defaultProps = EMPTY_OBJECT;
  }

  return props.theme !== defaultProps.theme && props.theme || providedTheme || defaultProps.theme;
});

// 
// Source: https://www.w3.org/TR/cssom-1/#serialize-an-identifier
// Control characters and non-letter first symbols are not supported
var escapeRegex = /[!"#$%&'()*+,./:;<=>?@[\\\]^`{|}~-]+/g;
var dashesAtEnds = /(^-|-$)/g;
/**
 * TODO: Explore using CSS.escape when it becomes more available
 * in evergreen browsers.
 */

function escape(str) {
  return str // Replace all possible CSS selectors
  .replace(escapeRegex, '-') // Remove extraneous hyphens at the start and end
  .replace(dashesAtEnds, '');
}

// 
function isTag(target) {
  return typeof target === 'string' && ( target.charAt(0) === target.charAt(0).toLowerCase() );
}

// 
function generateDisplayName(target) {
  // $FlowFixMe
  return isTag(target) ? "styled." + target : "Styled(" + getComponentName(target) + ")";
}

// 
var generateComponentId = (function (str) {
  return generateAlphabeticName(hash(str) >>> 0);
});

/**
 * Convenience function for joining strings to form className chains
 */
function joinStrings(a, b) {
  return a && b ? a + " " + b : a || b;
}

var ThemeContext = React__default.createContext();
var ThemeConsumer = ThemeContext.Consumer;

/* global $Call */

var identifiers = {};
/* We depend on components having unique IDs */

function generateId(displayName, parentComponentId) {
  var name = typeof displayName !== 'string' ? 'sc' : escape(displayName); // Ensure that no displayName can lead to duplicate componentIds

  identifiers[name] = (identifiers[name] || 0) + 1;
  var componentId = name + "-" + generateComponentId(name + identifiers[name]);
  return parentComponentId ? parentComponentId + "-" + componentId : componentId;
}

function useResolvedAttrs(theme, props, attrs) {
  if (theme === void 0) {
    theme = EMPTY_OBJECT;
  }

  // NOTE: can't memoize this
  // returns [context, resolvedAttrs]
  // where resolvedAttrs is only the things injected by the attrs themselves
  var context = _extends({}, props, {
    theme: theme
  });

  var resolvedAttrs = {};
  attrs.forEach(function (attrDef) {
    var resolvedAttrDef = attrDef;
    var key;

    if (isFunction$1(resolvedAttrDef)) {
      resolvedAttrDef = resolvedAttrDef(context);
    }
    /* eslint-disable guard-for-in */


    for (key in resolvedAttrDef) {
      context[key] = resolvedAttrs[key] = key === 'className' ? joinStrings(resolvedAttrs[key], resolvedAttrDef[key]) : resolvedAttrDef[key];
    }
    /* eslint-enable guard-for-in */

  });
  return [context, resolvedAttrs];
}

function useInjectedStyle(componentStyle, hasAttrs, resolvedAttrs, warnTooManyClasses) {
  var styleSheet = useStyleSheet();
  var stylis = useStylis(); // statically styled-components don't need to build an execution context object,
  // and shouldn't be increasing the number of class names

  var isStatic = componentStyle.isStatic && !hasAttrs;
  var className = isStatic ? componentStyle.generateAndInjectStyles(EMPTY_OBJECT, styleSheet, stylis) : componentStyle.generateAndInjectStyles(resolvedAttrs, styleSheet, stylis);
  useDebugValue(className);

  if ( !isStatic && warnTooManyClasses) {
    warnTooManyClasses(className);
  }

  return className;
}

function useStyledComponentImpl(forwardedComponent, props, forwardedRef) {
  var componentAttrs = forwardedComponent.attrs,
      componentStyle = forwardedComponent.componentStyle,
      defaultProps = forwardedComponent.defaultProps,
      foldedComponentIds = forwardedComponent.foldedComponentIds,
      shouldForwardProp = forwardedComponent.shouldForwardProp,
      styledComponentId = forwardedComponent.styledComponentId,
      target = forwardedComponent.target;
  useDebugValue(styledComponentId); // NOTE: the non-hooks version only subscribes to this when !componentStyle.isStatic,
  // but that'd be against the rules-of-hooks. We could be naughty and do it anyway as it
  // should be an immutable value, but behave for now.

  var theme = determineTheme(props, useContext(ThemeContext), defaultProps);

  var _useResolvedAttrs = useResolvedAttrs(theme || EMPTY_OBJECT, props, componentAttrs),
      context = _useResolvedAttrs[0],
      attrs = _useResolvedAttrs[1];

  var generatedClassName = useInjectedStyle(componentStyle, componentAttrs.length > 0, context,  forwardedComponent.warnTooManyClasses );
  var refToForward = forwardedRef;
  var elementToBeCreated = attrs.$as || props.$as || attrs.as || props.as || target;
  var isTargetTag = isTag(elementToBeCreated);
  var computedProps = attrs !== props ? _extends({}, props, {}, attrs) : props;
  var propFilterFn = shouldForwardProp || isTargetTag && index;
  var propsForElement = {}; // eslint-disable-next-line guard-for-in

  for (var key in computedProps) {
    if (key[0] === '$' || key === 'as') continue;else if (key === 'forwardedAs') {
      propsForElement.as = computedProps[key];
    } else if (!propFilterFn || propFilterFn(key, index)) {
      // Don't pass through non HTML tags through to HTML elements
      propsForElement[key] = computedProps[key];
    }
  }

  if (props.style && attrs.style !== props.style) {
    propsForElement.style = _extends({}, props.style, {}, attrs.style);
  }

  propsForElement.className = Array.prototype.concat(foldedComponentIds, styledComponentId, generatedClassName !== styledComponentId ? generatedClassName : null, props.className, attrs.className).filter(Boolean).join(' ');
  propsForElement.ref = refToForward;
  return createElement(elementToBeCreated, propsForElement);
}

function createStyledComponent(target, options, rules) {
  var isTargetStyledComp = isStyledComponent(target);
  var isCompositeComponent = !isTag(target);
  var _options$displayName = options.displayName,
      displayName = _options$displayName === void 0 ? generateDisplayName(target) : _options$displayName,
      _options$componentId = options.componentId,
      componentId = _options$componentId === void 0 ? generateId(options.displayName, options.parentComponentId) : _options$componentId,
      _options$attrs = options.attrs,
      attrs = _options$attrs === void 0 ? EMPTY_ARRAY : _options$attrs;
  var styledComponentId = options.displayName && options.componentId ? escape(options.displayName) + "-" + options.componentId : options.componentId || componentId; // fold the underlying StyledComponent attrs up (implicit extend)

  var finalAttrs = // $FlowFixMe
  isTargetStyledComp && target.attrs ? Array.prototype.concat(target.attrs, attrs).filter(Boolean) : attrs; // eslint-disable-next-line prefer-destructuring

  var shouldForwardProp = options.shouldForwardProp; // $FlowFixMe

  if (isTargetStyledComp && target.shouldForwardProp) {
    if (shouldForwardProp) {
      // compose nested shouldForwardProp calls
      shouldForwardProp = function shouldForwardProp(prop, filterFn) {
        return (// $FlowFixMe
          target.shouldForwardProp(prop, filterFn) && options.shouldForwardProp(prop, filterFn)
        );
      };
    } else {
      // eslint-disable-next-line prefer-destructuring
      shouldForwardProp = target.shouldForwardProp;
    }
  }

  var componentStyle = new ComponentStyle(isTargetStyledComp ? // fold the underlying StyledComponent rules up (implicit extend)
  // $FlowFixMe
  target.componentStyle.rules.concat(rules) : rules, styledComponentId);
  /**
   * forwardRef creates a new interim component, which we'll take advantage of
   * instead of extending ParentComponent to create _another_ interim class
   */

  var WrappedStyledComponent; // eslint-disable-next-line react-hooks/rules-of-hooks

  var forwardRef = function forwardRef(props, ref) {
    return useStyledComponentImpl(WrappedStyledComponent, props, ref);
  };

  forwardRef.displayName = displayName; // $FlowFixMe this is a forced cast to merge it StyledComponentWrapperProperties

  WrappedStyledComponent = React__default.forwardRef(forwardRef);
  WrappedStyledComponent.attrs = finalAttrs;
  WrappedStyledComponent.componentStyle = componentStyle;
  WrappedStyledComponent.displayName = displayName;
  WrappedStyledComponent.shouldForwardProp = shouldForwardProp; // this static is used to preserve the cascade of static classes for component selector
  // purposes; this is especially important with usage of the css prop

  WrappedStyledComponent.foldedComponentIds = isTargetStyledComp ? // $FlowFixMe
  Array.prototype.concat(target.foldedComponentIds, target.styledComponentId) : EMPTY_ARRAY;
  WrappedStyledComponent.styledComponentId = styledComponentId; // fold the underlying StyledComponent target up since we folded the styles

  WrappedStyledComponent.target = isTargetStyledComp ? // $FlowFixMe
  target.target : target; // $FlowFixMe

  WrappedStyledComponent.withComponent = function withComponent(tag) {
    var previousComponentId = options.componentId,
        optionsToCopy = _objectWithoutPropertiesLoose(options, ["componentId"]);

    var newComponentId = previousComponentId && previousComponentId + "-" + (isTag(tag) ? tag : escape(getComponentName(tag)));

    var newOptions = _extends({}, optionsToCopy, {
      attrs: finalAttrs,
      componentId: newComponentId
    });

    return createStyledComponent(tag, newOptions, rules);
  }; // $FlowFixMe


  Object.defineProperty(WrappedStyledComponent, 'defaultProps', {
    get: function get() {
      return this._foldedDefaultProps;
    },
    set: function set(obj) {
      // $FlowFixMe
      this._foldedDefaultProps = isTargetStyledComp ? mixinDeep({}, target.defaultProps, obj) : obj;
    }
  });

  {
    checkDynamicCreation(displayName, styledComponentId);
    WrappedStyledComponent.warnTooManyClasses = createWarnTooManyClasses(displayName, styledComponentId);
  } // $FlowFixMe


  WrappedStyledComponent.toString = function () {
    return "." + WrappedStyledComponent.styledComponentId;
  };

  if (isCompositeComponent) {
    hoistNonReactStatics_cjs(WrappedStyledComponent, target, {
      // all SC-specific things should not be hoisted
      attrs: true,
      componentStyle: true,
      displayName: true,
      foldedComponentIds: true,
      shouldForwardProp: true,
      self: true,
      styledComponentId: true,
      target: true,
      withComponent: true
    });
  }

  return WrappedStyledComponent;
}

// 
// Thanks to ReactDOMFactories for this handy list!
var domElements = ['a', 'abbr', 'address', 'area', 'article', 'aside', 'audio', 'b', 'base', 'bdi', 'bdo', 'big', 'blockquote', 'body', 'br', 'button', 'canvas', 'caption', 'cite', 'code', 'col', 'colgroup', 'data', 'datalist', 'dd', 'del', 'details', 'dfn', 'dialog', 'div', 'dl', 'dt', 'em', 'embed', 'fieldset', 'figcaption', 'figure', 'footer', 'form', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'head', 'header', 'hgroup', 'hr', 'html', 'i', 'iframe', 'img', 'input', 'ins', 'kbd', 'keygen', 'label', 'legend', 'li', 'link', 'main', 'map', 'mark', 'marquee', 'menu', 'menuitem', 'meta', 'meter', 'nav', 'noscript', 'object', 'ol', 'optgroup', 'option', 'output', 'p', 'param', 'picture', 'pre', 'progress', 'q', 'rp', 'rt', 'ruby', 's', 'samp', 'script', 'section', 'select', 'small', 'source', 'span', 'strong', 'style', 'sub', 'summary', 'sup', 'table', 'tbody', 'td', 'textarea', 'tfoot', 'th', 'thead', 'time', 'title', 'tr', 'track', 'u', 'ul', 'var', 'video', 'wbr', // SVG
'circle', 'clipPath', 'defs', 'ellipse', 'foreignObject', 'g', 'image', 'line', 'linearGradient', 'marker', 'mask', 'path', 'pattern', 'polygon', 'polyline', 'radialGradient', 'rect', 'stop', 'svg', 'text', 'tspan'];

// 

var styled = function styled(tag) {
  return constructWithOptions(createStyledComponent, tag);
}; // Shorthands for all valid HTML Elements


domElements.forEach(function (domElement) {
  styled[domElement] = styled(domElement);
});
/* Warning if you've imported this file on React Native */

if ( typeof navigator !== 'undefined' && navigator.product === 'ReactNative') {
  // eslint-disable-next-line no-console
  console.warn("It looks like you've imported 'styled-components' on React Native.\n" + "Perhaps you're looking to import 'styled-components/native'?\n" + 'Read more about this at https://www.styled-components.com/docs/basics#react-native');
}
/* Warning if there are several instances of styled-components */


if ( typeof window !== 'undefined') {
  window['__styled-components-init__'] = window['__styled-components-init__'] || 0;

  if (window['__styled-components-init__'] === 1) {
    // eslint-disable-next-line no-console
    console.warn("It looks like there are several instances of 'styled-components' initialized in this application. " + 'This may cause dynamic styles not rendering properly, errors happening during rehydration process, ' + 'missing theme prop, and makes your application bigger without a good reason.\n\n' + 'See https://s-c.sh/2BAXzed for more info.');
  }

  window['__styled-components-init__'] += 1;
}

function isValidProp(key) {
    return index(key);
}
function filterSVGProps(props) {
    return Object.keys(props).reduce(function (p, k) {
        if (isValidProp(k)) {
            p[k] = props[k];
        }
        return p;
    }, {});
}
var StyledIconBaseBase = forwardRef(function (props, ref) {
    var children = props.children, iconAttrs = props.iconAttrs, iconVerticalAlign = props.iconVerticalAlign, iconViewBox = props.iconViewBox, size = props.size, title = props.title, otherProps = __rest(props, ["children", "iconAttrs", "iconVerticalAlign", "iconViewBox", "size", "title"]);
    var iconProps = __assign({ viewBox: iconViewBox, height: props.height !== undefined ? props.height : size, width: props.width !== undefined ? props.width : size, 'aria-hidden': title == null ? 'true' : undefined, focusable: 'false', role: title != null ? 'img' : undefined }, iconAttrs);
    var svgProps = filterSVGProps(otherProps);
    return (createElement("svg", __assign({}, iconProps, svgProps, { ref: ref }),
        title && createElement("title", { key: "icon-title" }, title),
        children));
});
var StyledIconBase = styled(StyledIconBaseBase)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  display: inline-block;\n  vertical-align: ", ";\n  overflow: hidden;\n"], ["\n  display: inline-block;\n  vertical-align: ", ";\n  overflow: hidden;\n"])), function (props) { return props.iconVerticalAlign; });
var templateObject_1;

var DragIndicator = forwardRef(function (props, ref) {
    var attrs = {
        "fill": "currentColor",
        "xmlns": "http://www.w3.org/2000/svg",
    };
    return (createElement(StyledIconBase, __assign({ iconAttrs: attrs, iconVerticalAlign: "middle", iconViewBox: "0 0 24 24" }, props, { ref: ref }),
        createElement("path", { fill: "none", d: "M0 0h24v24H0V0z" }),
        createElement("path", { d: "M11 18c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm-2-8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" })));
});
DragIndicator.displayName = 'DragIndicator';

const useDragBlock = (editor, id) => {
  return useDrag({
    item: {
      type: 'block',
      id
    },
    collect: monitor => ({
      isDragging: monitor.isDragging()
    }),
    begin: () => {
      document.body.classList.add('dragging');
    },
    end: () => {
      document.body.classList.remove('dragging');
    }
  });
};

/**
 * See {@link Range.isExpanded}.
 * Return false if `range` is not defined.
 */

const isExpanded = range => !!range && Range.isExpanded(range);

/**
 * Get block path by id.
 */

const getBlockPathById = (editor, id) => {
  const nodeEntries = getNodesById(editor, id);
  if (!nodeEntries.length) return;
  const [, path] = nodeEntries[0];
  return path;
};

/**
 * If dragging a block A over another block B:
 * get the direction of block A relative to block B.
 */
const getHoverDirection = (dragItem, monitor, ref, hoverId) => {
  var _a;

  if (!ref.current) return;
  const dragId = dragItem.id; // Don't replace items with themselves

  if (dragId === hoverId) return; // Determine rectangle on screen

  const hoverBoundingRect = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect(); // Get vertical middle

  const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2; // Determine mouse position

  const clientOffset = monitor.getClientOffset();
  if (!clientOffset) return; // Get pixels to the top

  const hoverClientY = clientOffset.y - hoverBoundingRect.top; // Only perform the move when the mouse has crossed half of the items height
  // When dragging downwards, only move when the cursor is below 50%
  // When dragging upwards, only move when the cursor is above 50%
  // Dragging downwards
  // if (dragId < hoverId && hoverClientY < hoverMiddleY) {

  if (hoverClientY < hoverMiddleY) {
    return 'top';
  } // Dragging upwards
  // if (dragId > hoverId && hoverClientY > hoverMiddleY) {


  if (hoverClientY >= hoverMiddleY) {
    return 'bottom';
  }
};

/**
 * Get new direction if updated
 */
const getNewDirection = (previousDir, dir) => {
  if (!dir && previousDir) {
    return '';
  }

  if (dir === 'top' && previousDir !== 'top') {
    return 'top';
  }

  if (dir === 'bottom' && previousDir !== 'bottom') {
    return 'bottom';
  }
};

const useDropBlockOnEditor = (editor, {
  blockRef,
  id,
  dropLine,
  setDropLine
}) => {
  return useDrop({
    accept: 'block',
    drop: (dragItem, monitor) => {
      const direction = getHoverDirection(dragItem, monitor, blockRef, id);
      if (!direction) return;
      const dragPath = getBlockPathById(editor, dragItem.id);
      if (!dragPath) return;
      ReactEditor.focus(editor);
      let dropPath;

      if (direction === 'bottom') {
        dropPath = getBlockPathById(editor, id);
        if (!dropPath) return;
        if (Path.equals(dragPath, Path.next(dropPath))) return;
      }

      if (direction === 'top') {
        const nodePath = getBlockPathById(editor, id);
        if (!nodePath) return;
        dropPath = [...nodePath.slice(0, -1), nodePath[nodePath.length - 1] - 1];
        if (Path.equals(dragPath, dropPath)) return;
      }

      if (direction) {
        const _dropPath = dropPath;
        const before = Path.isBefore(dragPath, _dropPath) && Path.compare(dragPath, _dropPath) !== -1 && Path.isSibling(dragPath, _dropPath);
        const to = before ? _dropPath : Path.next(_dropPath);
        Transforms.moveNodes(editor, {
          at: dragPath,
          to
        });
      }
    },
    collect: monitor => ({
      isOver: monitor.isOver()
    }),

    hover(item, monitor) {
      const direction = getHoverDirection(item, monitor, blockRef, id);
      const dropLineDir = getNewDirection(dropLine, direction);
      if (dropLineDir) setDropLine(dropLineDir);

      if (direction && isExpanded(editor.selection)) {
        ReactEditor.focus(editor);
        Transforms.collapse(editor);
      }
    }

  });
};

const useDndBlock = ({
  id,
  blockRef,
  removePreview
}) => {
  const editor = useEditor();
  const [dropLine, setDropLine] = useState('');
  const [{
    isDragging
  }, dragRef, preview] = useDragBlock(editor, id);
  const [{
    isOver
  }, drop] = useDropBlockOnEditor(editor, {
    id,
    blockRef,
    dropLine,
    setDropLine
  });

  if (removePreview) {
    drop(blockRef);
    preview(getEmptyImage(), {
      captureDraggingState: true
    });
  } else {
    preview(drop(blockRef));
  }

  if (!isOver && dropLine) {
    setDropLine('');
  }

  return {
    isDragging,
    dropLine,
    dragRef
  };
};

const GrabberTooltipContent = () => /*#__PURE__*/React__default.createElement("div", {
  style: {
    fontSize: 12
  }
}, /*#__PURE__*/React__default.createElement("div", null, "Drag ", /*#__PURE__*/React__default.createElement("span", {
  style: {
    color: 'rgba(255, 255, 255, 0.45)'
  }
}, "to move")));

const grabberTooltipProps = {
  content: /*#__PURE__*/React__default.createElement(GrabberTooltipContent, null),
  placement: 'bottom',
  arrow: false,
  offset: [0, 0],
  delay: [300, 0],
  duration: [0, 0],
  hideOnClick: true,
  theme: 'small'
};

const classNames = {
  root: 'slate-Selectable',
  gutterLeft: 'slate-gutter-left'
};
const getSelectableStyles = ({
  className,
  direction,
  isDragging,
  selected
}) => {
  return {
    root: [classNames.root, {
      position: 'relative',
      opacity: isDragging ? 0.5 : 1,
      backgroundColor: selected ? 'rgb(181, 215, 255)' : undefined,
      selectors: {
        ':hover .slate-gutter-left': {
          opacity: 1
        }
      }
    }, className],
    block: {},
    blockAndGutter: {
      paddingTop: 3,
      paddingBottom: 3
    },
    gutterLeft: [{
      position: 'absolute',
      top: 0,
      transform: 'translateX(-100%)',
      display: 'flex',
      height: '100%',
      opacity: 0
    }, classNames.gutterLeft],
    blockToolbarWrapper: {
      display: 'flex',
      // alignItems: 'center',
      height: '1.5em'
    },
    blockToolbar: {
      width: 18,
      height: 18,
      marginRight: 4,
      pointerEvents: 'auto'
    },
    dragButton: {
      minWidth: 18,
      height: 18,
      padding: 0,
      backgroundColor: 'transparent',
      backgroundRepeat: 'no-repeat',
      border: 'none',
      cursor: 'pointer',
      overflow: 'hidden',
      outline: 'none'
    },
    dragIcon: {
      width: 18,
      height: 18,
      color: 'rgba(55, 53, 47, 0.3)'
    },
    dropLine: {
      position: 'absolute',
      left: 0,
      right: 0,
      top: direction === 'top' ? -1 : undefined,
      bottom: direction === 'bottom' ? -1 : undefined,
      height: 2,
      opacity: 1,
      background: '#B4D5FF'
    }
  };
};

const getClassNames = classNamesFunction();

const SelectableBase = ({
  children,
  element,
  className,
  styles,
  componentRef
}) => {
  const blockRef = useRef(null);
  const rootRef = useRef(null);
  const multiRootRef = useMergedRef(componentRef, rootRef);
  const {
    dropLine,
    dragRef,
    isDragging
  } = useDndBlock({
    id: element.id,
    blockRef: rootRef
  });
  const dragWrapperRef = useRef(null);
  const multiDragRef = useMergedRef(dragRef, dragWrapperRef);
  const classNames = getClassNames(styles, {
    className,
    direction: dropLine,
    isDragging
  });
  return /*#__PURE__*/React__default.createElement("div", {
    className: classNames.root,
    ref: multiRootRef
  }, /*#__PURE__*/React__default.createElement("div", {
    ref: blockRef,
    className: mergeStyles(classNames.blockAndGutter, classNames.block)
  }, children, !!dropLine && /*#__PURE__*/React__default.createElement("div", {
    className: classNames.dropLine,
    contentEditable: false
  })), /*#__PURE__*/React__default.createElement("div", {
    className: mergeStyles(classNames.blockAndGutter, classNames.gutterLeft),
    contentEditable: false
  }, /*#__PURE__*/React__default.createElement("div", {
    className: classNames.blockToolbarWrapper
  }, /*#__PURE__*/React__default.createElement("div", {
    className: classNames.blockToolbar
  }, /*#__PURE__*/React__default.createElement(Tippy, Object.assign({}, grabberTooltipProps), /*#__PURE__*/React__default.createElement("div", {
    ref: multiDragRef
  }, /*#__PURE__*/React__default.createElement("button", {
    type: "button",
    className: classNames.dragButton,
    onMouseDown: e => e.stopPropagation()
  }, /*#__PURE__*/React__default.createElement(DragIndicator, {
    className: classNames.dragIcon
  }))))))));
};

const Selectable = styled$1(SelectableBase, getSelectableStyles, undefined, {
  scope: 'Selectable'
});

const getSelectableElement = ({
  component: Component,
  styles,
  level,
  filter,
  allowReadOnly = false
}) => {
  return /*#__PURE__*/forwardRef((_a, ref) => {
    var {
      attributes,
      element
    } = _a,
        props = __rest(_a, ["attributes", "element"]);

    const editor = useEditor();
    const readOnly = useReadOnly();
    const path = useMemo(() => ReactEditor.findPath(editor, element), [editor, element]);
    const filteredOut = useMemo(() => Number.isInteger(level) && level !== path.length - 1 || filter && filter(editor, path), [path, editor]);

    if (filteredOut || !allowReadOnly && readOnly) {
      return /*#__PURE__*/React__default.createElement(Component, Object.assign({
        attributes: attributes,
        element: element
      }, props));
    }

    return /*#__PURE__*/React__default.createElement(Selectable, {
      attributes: attributes,
      componentRef: ref,
      element: element,
      styles: styles
    }, /*#__PURE__*/React__default.createElement(Component, Object.assign({
      attributes: attributes,
      element: element
    }, props)));
  });
};

/**
 * Select the start of a block by id and focus the editor.
 */

const focusBlockStartById = (editor, id) => {
  const path = getBlockPathById(editor, id);
  if (!path) return;
  Transforms.select(editor, Editor.start(editor, path));
  ReactEditor.focus(editor);
};

/**
 * Get blocks with an id
 */

const getBlocks = (editor, options) => {
  return [...Editor.nodes(editor, Object.assign({
    match: n => Editor.isBlock(editor, n) && !!n.id
  }, options))];
};

/**
 * Get node entries range.
 */

const getNodesRange = (editor, nodeEntries) => {
  if (!nodeEntries.length) return;
  const firstBlockPath = nodeEntries[0][1];
  const lastBlockPath = nodeEntries[nodeEntries.length - 1][1];
  return Editor.range(editor, firstBlockPath, lastBlockPath);
};

/**
 * Remove blocks with an id and focus the editor.
 */

const removeBlocksAndFocus = (editor, options) => {
  unhangRange(editor, options);
  const nodeEntries = getBlocks(editor, options);
  Transforms.removeNodes(editor, {
    at: getNodesRange(editor, nodeEntries)
  });
  ReactEditor.focus(editor);
};

/**
 * Select the block above the selection by id and focus the editor.
 */

const selectBlockById = (editor, id) => {
  const path = getBlockPathById(editor, id);
  if (!path) return;
  Transforms.select(editor, Editor.range(editor, path));
  ReactEditor.focus(editor);
};

/**
 * Select blocks by selection or by id.
 * If the block with id is not selected, select the block with id.
 * Else, select the blocks above the selection.
 */

const selectBlocksBySelectionOrId = (editor, id) => {
  if (!editor.selection) return;
  const blockEntries = getBlocks(editor, {
    at: editor.selection
  });
  const isBlockSelected = blockEntries.some(blockEntry => blockEntry[0].id === id);

  if (isBlockSelected) {
    Transforms.select(editor, getNodesRange(editor, blockEntries));
    ReactEditor.focus(editor);
  } else {
    selectBlockById(editor, id);
  }
};

/**
 * Is the node entry filling a condition.
 */
const isNodeType = (entry, {
  filter = () => true,
  allow = [],
  exclude = []
} = {}) => {
  let filterAllow = () => true;

  if (allow.length) {
    filterAllow = ([n]) => allow.includes(n.type);
  }

  let filterExclude = () => true;

  if (exclude.length) {
    filterExclude = ([n]) => !exclude.includes(n.type);
  }

  return !!entry && filter(entry) && filterAllow(entry) && filterExclude(entry);
};

/**
 * Find the block before a block by id.
 * If not found, find the first block by id and return [null, its previous path]
 */

const getPreviousBlockById = (editor, id, query) => {
  const nodePath = getBlockPathById(editor, id);

  if (nodePath) {
    const prevEntry = Editor.previous(editor, {
      at: nodePath
    });

    if (prevEntry && prevEntry[0].id && Editor.isBlock(editor, prevEntry[0])) {
      return prevEntry;
    }
  }

  let found = false;
  const nodeEntries = [...Editor.nodes(editor, {
    mode: 'highest',
    reverse: true,
    match: n => {
      // filter nodes that are not blocks and without id.
      if (!Editor.isBlock(editor, n) || !n.id) return false; // find the block then take the previous one.

      if (n.id === id) {
        found = true;
        return false;
      }

      return found && n.id !== id && isNodeType([n, []], query);
    },
    at: []
  })];

  if (nodeEntries.length) {
    return nodeEntries[0];
  }

  if (!found) return;
  const firstNodeEntry = [...Editor.nodes(editor, {
    mode: 'highest',
    match: n => {
      return Editor.isBlock(editor, n) && n.id && isNodeType([n, []], query);
    },
    at: []
  })];

  if (firstNodeEntry.length) {
    const [, path] = firstNodeEntry[0];
    path[path.length - 1] = path[path.length - 1] - 1;
    return [null, path];
  }
};

/**
 * Get range from {@link getPointBefore} to the end point of `at`.
 */

const getRangeBefore = (editor, at, options) => {
  const anchor = getPointBefore(editor, at, options);
  if (!anchor) return;
  const focus = Editor.point(editor, at, {
    edge: 'end'
  });
  return {
    anchor,
    focus
  };
};

/**
 * Get the range from the start of the block above a location (default: selection) to the location.
 */

const getRangeFromBlockStart = (editor, options = {}) => {
  const [, path] = getBlockAbove(editor, options);
  const start = Editor.start(editor, path);
  const focus = getPointFromLocation(editor, options);
  if (!focus) return;
  return {
    anchor: start,
    focus
  };
};

/**
 * See {@link Editor.string}.
 * If `at` is not defined, return an empty string.
 */

const getText = (editor, at) => {
  var _a;

  return (_a = at && Editor.string(editor, at)) !== null && _a !== void 0 ? _a : '';
};

/**
 * Get the selected text.
 * Return empty string if no selection.
 */

const getSelectionText = editor => getText(editor, editor.selection);

/**
 * Is an ancestor empty (empty text and no inline children).
 */

const isAncestorEmpty = (editor, node) => !Node$1.string(node) && !node.children.some(n => Editor.isInline(editor, n));

/**
 * Is the block above the selection empty.
 */

const isBlockAboveEmpty = editor => {
  const blockEntry = getBlockAbove(editor);
  const [block] = blockEntry;
  return isAncestorEmpty(editor, block);
};

/**
 * Is there empty text after the selection.
 * If there is no leaf after the selected leaf, return {@link Editor.isEnd}.
 * Else, check if the next leaves are empty.
 */

const isBlockTextEmptyAfterSelection = editor => {
  if (!editor.selection) return false;
  const blockAbove = getBlockAbove(editor);
  const cursor = editor.selection.focus;
  if (!Editor.isEnd(editor, cursor, Editor.parent(editor, editor.selection)[1])) return false;
  const siblingNodes = getNextSiblingNodes(blockAbove, cursor.path);

  if (siblingNodes.length) {
    for (const siblingNode of siblingNodes) {
      if (siblingNode.text) {
        return false;
      }
    }
  } else {
    return Editor.isEnd(editor, cursor, blockAbove[1]);
  }

  return true;
};

/**
 * See {@link Range.isCollapsed}.
 * Return false if `range` is not defined.
 */

const isCollapsed = range => !!range && Range.isCollapsed(range);

const isDescendant = node => Element.isElement(node) || Text.isText(node);

/**
 * {@link Editor.isEnd}. If point is null, return false.
 */

const isEnd = (editor, point, at) => !!point && Editor.isEnd(editor, point, at);

/**
 * Is it the first child of the parent
 */
const isFirstChild = path => path[path.length - 1] === 0;

const isMarkActive = (editor, type) => {
  const marks = Editor.marks(editor);
  return marks ? marks[type] === true : false;
};

/**
 * Is there a node with a type included in `types` at a location (default: selection).
 */

const isNodeTypeIn = (editor, types, options = {}) => {
  const [match] = getNodesByType(editor, types, options);
  return !!match;
};

const isPointAtRoot = point => point.path.length === 2;

const AFTER_MATCH_REGEX = /^(\s|$)/;
/**
 * Is a point at the end of a word
 */

const isPointAtWordEnd = (editor, {
  at
}) => {
  // Point after at
  const after = Editor.after(editor, at); // From at to after

  const afterRange = Editor.range(editor, at, after);
  const afterText = getText(editor, afterRange); // Match regex on after text

  return !!afterText.match(AFTER_MATCH_REGEX);
};

const isRangeAtRoot = range => isPointAtRoot(range.anchor) || isPointAtRoot(range.focus);

/**
 * Is the selection focus at the end of its parent block.
 */

const isSelectionAtBlockEnd = editor => {
  var _a;

  const [, path] = getBlockAbove(editor);
  return isEnd(editor, (_a = editor.selection) === null || _a === void 0 ? void 0 : _a.focus, path);
};

/**
 * {@link Editor.isStart}. If point is null, return false.
 */

const isStart = (editor, point, at) => !!point && Editor.isStart(editor, point, at);

/**
 * Is the selection focus at the start of its parent block.
 */

const isSelectionAtBlockStart = editor => {
  var _a;

  const [, path] = getBlockAbove(editor);
  return isStart(editor, (_a = editor.selection) === null || _a === void 0 ? void 0 : _a.focus, path);
};

/**
 * Is the selection expanded.
 */

const isSelectionExpanded = editor => isExpanded(editor.selection);

const isTextByPath = (editor, path) => {
  const node = Node$1.get(editor, path);
  return Text.isText(node);
};

const createDocumentNode = (type = 'p', text = '', remaining = []) => [{
  children: [{
    type,
    children: [{
      text
    }]
  }, ...remaining]
}];

const createNode = (type = 'p', text = '') => ({
  type,
  children: [{
    text
  }]
});

const escapeRegExp = text => {
  return text.replace(/[-[\]{}()*+?.,\\^$|#\\s]/g, '\\$&');
};

/**
 * Get a deserializer by type, node names, class names and styles.
 */

const getNodeDeserializer = ({
  type,
  node,
  rules
}) => {
  const deserializers = [];
  rules.forEach(({
    nodeNames = '*',
    style,
    className
  }) => {
    nodeNames = castArray_1(nodeNames);
    nodeNames.forEach(nodeName => {
      deserializers.push({
        type,
        deserialize: el => {
          if (nodeNames.length && !nodeNames.includes(el.nodeName) && nodeName !== '*') return;
          if (className && !el.classList.contains(className)) return;

          if (style) {
            for (const [key, value] of Object.entries(style)) {
              const values = castArray_1(value);
              if (!values.includes(el.style[key])) return;
            }
          }

          return node(el);
        }
      });
    });
  });
  return deserializers;
};

/**
 * See {@link getNodeDeserializer}.
 */

const getElementDeserializer = options => getNodeDeserializer(Object.assign(Object.assign({}, options), {
  node: () => ({
    type: options.type
  })
}));

/**
 * Call a handler if defined
 */
const getHandler = (cb, ...args) => () => {
  cb === null || cb === void 0 ? void 0 : cb(...args);
};

/**
 * See {@link getNodeDeserializer}.
 */

const getLeafDeserializer = options => getNodeDeserializer(Object.assign(Object.assign({}, options), {
  node: () => ({
    [options.type]: true
  })
}));

/**
 * Prevent default and call a handler if defined
 */
const getPreventDefaultHandler = (cb, ...args) => event => {
  event.preventDefault();
  cb === null || cb === void 0 ? void 0 : cb(...args);
};

var defineProperty$1 = (function() {
  try {
    var func = _getNative(Object, 'defineProperty');
    func({}, '', {});
    return func;
  } catch (e) {}
}());

var _defineProperty = defineProperty$1;

/**
 * The base implementation of `assignValue` and `assignMergeValue` without
 * value checks.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function baseAssignValue(object, key, value) {
  if (key == '__proto__' && _defineProperty) {
    _defineProperty(object, key, {
      'configurable': true,
      'enumerable': true,
      'value': value,
      'writable': true
    });
  } else {
    object[key] = value;
  }
}

var _baseAssignValue = baseAssignValue;

/** Used for built-in method references. */
var objectProto$c = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$9 = objectProto$c.hasOwnProperty;

/**
 * Assigns `value` to `key` of `object` if the existing value is not equivalent
 * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * for equality comparisons.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function assignValue(object, key, value) {
  var objValue = object[key];
  if (!(hasOwnProperty$9.call(object, key) && eq_1(objValue, value)) ||
      (value === undefined && !(key in object))) {
    _baseAssignValue(object, key, value);
  }
}

var _assignValue = assignValue;

/**
 * The base implementation of `_.set`.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {Array|string} path The path of the property to set.
 * @param {*} value The value to set.
 * @param {Function} [customizer] The function to customize path creation.
 * @returns {Object} Returns `object`.
 */
function baseSet(object, path, value, customizer) {
  if (!isObject_1(object)) {
    return object;
  }
  path = _castPath(path, object);

  var index = -1,
      length = path.length,
      lastIndex = length - 1,
      nested = object;

  while (nested != null && ++index < length) {
    var key = _toKey(path[index]),
        newValue = value;

    if (index != lastIndex) {
      var objValue = nested[key];
      newValue = customizer ? customizer(objValue, key, nested) : undefined;
      if (newValue === undefined) {
        newValue = isObject_1(objValue)
          ? objValue
          : (_isIndex(path[index + 1]) ? [] : {});
      }
    }
    _assignValue(nested, key, newValue);
    nested = nested[key];
  }
  return object;
}

var _baseSet = baseSet;

/**
 * The base implementation of  `_.pickBy` without support for iteratee shorthands.
 *
 * @private
 * @param {Object} object The source object.
 * @param {string[]} paths The property paths to pick.
 * @param {Function} predicate The function invoked per property.
 * @returns {Object} Returns the new object.
 */
function basePickBy(object, paths, predicate) {
  var index = -1,
      length = paths.length,
      result = {};

  while (++index < length) {
    var path = paths[index],
        value = _baseGet(object, path);

    if (predicate(value, path)) {
      _baseSet(result, _castPath(path, object), value);
    }
  }
  return result;
}

var _basePickBy = basePickBy;

/** Built-in value references. */
var getPrototype = _overArg(Object.getPrototypeOf, Object);

var _getPrototype = getPrototype;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeGetSymbols$1 = Object.getOwnPropertySymbols;

/**
 * Creates an array of the own and inherited enumerable symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of symbols.
 */
var getSymbolsIn = !nativeGetSymbols$1 ? stubArray_1 : function(object) {
  var result = [];
  while (object) {
    _arrayPush(result, _getSymbols(object));
    object = _getPrototype(object);
  }
  return result;
};

var _getSymbolsIn = getSymbolsIn;

/**
 * This function is like
 * [`Object.keys`](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
 * except that it includes inherited enumerable properties.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function nativeKeysIn(object) {
  var result = [];
  if (object != null) {
    for (var key in Object(object)) {
      result.push(key);
    }
  }
  return result;
}

var _nativeKeysIn = nativeKeysIn;

/** Used for built-in method references. */
var objectProto$d = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$a = objectProto$d.hasOwnProperty;

/**
 * The base implementation of `_.keysIn` which doesn't treat sparse arrays as dense.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function baseKeysIn(object) {
  if (!isObject_1(object)) {
    return _nativeKeysIn(object);
  }
  var isProto = _isPrototype(object),
      result = [];

  for (var key in object) {
    if (!(key == 'constructor' && (isProto || !hasOwnProperty$a.call(object, key)))) {
      result.push(key);
    }
  }
  return result;
}

var _baseKeysIn = baseKeysIn;

/**
 * Creates an array of the own and inherited enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keysIn(new Foo);
 * // => ['a', 'b', 'c'] (iteration order is not guaranteed)
 */
function keysIn(object) {
  return isArrayLike_1(object) ? _arrayLikeKeys(object, true) : _baseKeysIn(object);
}

var keysIn_1 = keysIn;

/**
 * Creates an array of own and inherited enumerable property names and
 * symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names and symbols.
 */
function getAllKeysIn(object) {
  return _baseGetAllKeys(object, keysIn_1, _getSymbolsIn);
}

var _getAllKeysIn = getAllKeysIn;

/**
 * Creates an object composed of the `object` properties `predicate` returns
 * truthy for. The predicate is invoked with two arguments: (value, key).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Object
 * @param {Object} object The source object.
 * @param {Function} [predicate=_.identity] The function invoked per property.
 * @returns {Object} Returns the new object.
 * @example
 *
 * var object = { 'a': 1, 'b': '2', 'c': 3 };
 *
 * _.pickBy(object, _.isNumber);
 * // => { 'a': 1, 'c': 3 }
 */
function pickBy(object, predicate) {
  if (object == null) {
    return {};
  }
  var props = _arrayMap(_getAllKeysIn(object), function(prop) {
    return [prop];
  });
  predicate = _baseIteratee(predicate);
  return _basePickBy(object, props, function(value, path) {
    return predicate(value, path[0]);
  });
}

var pickBy_1 = pickBy;

/**
 * Get a `renderElement` handler for a single type.
 * If the given `type` is equals to the slate element type, render the given `component`.
 * You can pass props by using `rootProps`. Falsy props are ignored.
 */

const getRenderElement = ({
  type,
  component: Component,
  rootProps
}) => _a => {
  var {
    attributes
  } = _a,
      props = __rest(_a, ["attributes"]);

  if (props.element.type === type) {
    return /*#__PURE__*/createElement(Component, Object.assign({
      attributes: attributes
    }, props, pickBy_1(rootProps)));
  }
};
/**
 * Get a `renderElement` handler for multiple types.
 */

const getRenderElements = options => ({
  attributes,
  element,
  children
}) => {
  for (const {
    type,
    component: Component,
    rootProps
  } of options) {
    if (element.type === type) {
      return /*#__PURE__*/createElement(Component, Object.assign({
        attributes: attributes,
        element: element
      }, pickBy_1(rootProps)), children);
    }
  }
};

/**
 * Get a `renderLeaf` handler for a single type.
 */

const getRenderLeaf = ({
  type,
  component: Component,
  rootProps
}) => ({
  children,
  leaf
}) => {
  if (leaf[type] && !!leaf.text) {
    return /*#__PURE__*/createElement(Component, Object.assign({
      leaf: leaf
    }, pickBy_1(rootProps)), children);
  }

  return children;
};

/**
 * A faster alternative to `Function#apply`, this function invokes `func`
 * with the `this` binding of `thisArg` and the arguments of `args`.
 *
 * @private
 * @param {Function} func The function to invoke.
 * @param {*} thisArg The `this` binding of `func`.
 * @param {Array} args The arguments to invoke `func` with.
 * @returns {*} Returns the result of `func`.
 */
function apply(func, thisArg, args) {
  switch (args.length) {
    case 0: return func.call(thisArg);
    case 1: return func.call(thisArg, args[0]);
    case 2: return func.call(thisArg, args[0], args[1]);
    case 3: return func.call(thisArg, args[0], args[1], args[2]);
  }
  return func.apply(thisArg, args);
}

var _apply = apply;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax = Math.max;

/**
 * A specialized version of `baseRest` which transforms the rest array.
 *
 * @private
 * @param {Function} func The function to apply a rest parameter to.
 * @param {number} [start=func.length-1] The start position of the rest parameter.
 * @param {Function} transform The rest array transform.
 * @returns {Function} Returns the new function.
 */
function overRest(func, start, transform) {
  start = nativeMax(start === undefined ? (func.length - 1) : start, 0);
  return function() {
    var args = arguments,
        index = -1,
        length = nativeMax(args.length - start, 0),
        array = Array(length);

    while (++index < length) {
      array[index] = args[start + index];
    }
    index = -1;
    var otherArgs = Array(start + 1);
    while (++index < start) {
      otherArgs[index] = args[index];
    }
    otherArgs[start] = transform(array);
    return _apply(func, this, otherArgs);
  };
}

var _overRest = overRest;

/**
 * Creates a function that returns `value`.
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Util
 * @param {*} value The value to return from the new function.
 * @returns {Function} Returns the new constant function.
 * @example
 *
 * var objects = _.times(2, _.constant({ 'a': 1 }));
 *
 * console.log(objects);
 * // => [{ 'a': 1 }, { 'a': 1 }]
 *
 * console.log(objects[0] === objects[1]);
 * // => true
 */
function constant(value) {
  return function() {
    return value;
  };
}

var constant_1 = constant;

/**
 * The base implementation of `setToString` without support for hot loop shorting.
 *
 * @private
 * @param {Function} func The function to modify.
 * @param {Function} string The `toString` result.
 * @returns {Function} Returns `func`.
 */
var baseSetToString = !_defineProperty ? identity_1 : function(func, string) {
  return _defineProperty(func, 'toString', {
    'configurable': true,
    'enumerable': false,
    'value': constant_1(string),
    'writable': true
  });
};

var _baseSetToString = baseSetToString;

/** Used to detect hot functions by number of calls within a span of milliseconds. */
var HOT_COUNT = 800,
    HOT_SPAN = 16;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeNow = Date.now;

/**
 * Creates a function that'll short out and invoke `identity` instead
 * of `func` when it's called `HOT_COUNT` or more times in `HOT_SPAN`
 * milliseconds.
 *
 * @private
 * @param {Function} func The function to restrict.
 * @returns {Function} Returns the new shortable function.
 */
function shortOut(func) {
  var count = 0,
      lastCalled = 0;

  return function() {
    var stamp = nativeNow(),
        remaining = HOT_SPAN - (stamp - lastCalled);

    lastCalled = stamp;
    if (remaining > 0) {
      if (++count >= HOT_COUNT) {
        return arguments[0];
      }
    } else {
      count = 0;
    }
    return func.apply(undefined, arguments);
  };
}

var _shortOut = shortOut;

/**
 * Sets the `toString` method of `func` to return `string`.
 *
 * @private
 * @param {Function} func The function to modify.
 * @param {Function} string The `toString` result.
 * @returns {Function} Returns `func`.
 */
var setToString = _shortOut(_baseSetToString);

var _setToString = setToString;

/**
 * The base implementation of `_.rest` which doesn't validate or coerce arguments.
 *
 * @private
 * @param {Function} func The function to apply a rest parameter to.
 * @param {number} [start=func.length-1] The start position of the rest parameter.
 * @returns {Function} Returns the new function.
 */
function baseRest(func, start) {
  return _setToString(_overRest(func, start, identity_1), func + '');
}

var _baseRest = baseRest;

/**
 * This function is like `assignValue` except that it doesn't assign
 * `undefined` values.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function assignMergeValue(object, key, value) {
  if ((value !== undefined && !eq_1(object[key], value)) ||
      (value === undefined && !(key in object))) {
    _baseAssignValue(object, key, value);
  }
}

var _assignMergeValue = assignMergeValue;

var _cloneBuffer = createCommonjsModule(function (module, exports) {
/** Detect free variable `exports`. */
var freeExports =  exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports = freeModule && freeModule.exports === freeExports;

/** Built-in value references. */
var Buffer = moduleExports ? _root.Buffer : undefined,
    allocUnsafe = Buffer ? Buffer.allocUnsafe : undefined;

/**
 * Creates a clone of  `buffer`.
 *
 * @private
 * @param {Buffer} buffer The buffer to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Buffer} Returns the cloned buffer.
 */
function cloneBuffer(buffer, isDeep) {
  if (isDeep) {
    return buffer.slice();
  }
  var length = buffer.length,
      result = allocUnsafe ? allocUnsafe(length) : new buffer.constructor(length);

  buffer.copy(result);
  return result;
}

module.exports = cloneBuffer;
});

/**
 * Creates a clone of `arrayBuffer`.
 *
 * @private
 * @param {ArrayBuffer} arrayBuffer The array buffer to clone.
 * @returns {ArrayBuffer} Returns the cloned array buffer.
 */
function cloneArrayBuffer(arrayBuffer) {
  var result = new arrayBuffer.constructor(arrayBuffer.byteLength);
  new _Uint8Array(result).set(new _Uint8Array(arrayBuffer));
  return result;
}

var _cloneArrayBuffer = cloneArrayBuffer;

/**
 * Creates a clone of `typedArray`.
 *
 * @private
 * @param {Object} typedArray The typed array to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Object} Returns the cloned typed array.
 */
function cloneTypedArray(typedArray, isDeep) {
  var buffer = isDeep ? _cloneArrayBuffer(typedArray.buffer) : typedArray.buffer;
  return new typedArray.constructor(buffer, typedArray.byteOffset, typedArray.length);
}

var _cloneTypedArray = cloneTypedArray;

/**
 * Copies the values of `source` to `array`.
 *
 * @private
 * @param {Array} source The array to copy values from.
 * @param {Array} [array=[]] The array to copy values to.
 * @returns {Array} Returns `array`.
 */
function copyArray(source, array) {
  var index = -1,
      length = source.length;

  array || (array = Array(length));
  while (++index < length) {
    array[index] = source[index];
  }
  return array;
}

var _copyArray = copyArray;

/** Built-in value references. */
var objectCreate = Object.create;

/**
 * The base implementation of `_.create` without support for assigning
 * properties to the created object.
 *
 * @private
 * @param {Object} proto The object to inherit from.
 * @returns {Object} Returns the new object.
 */
var baseCreate = (function() {
  function object() {}
  return function(proto) {
    if (!isObject_1(proto)) {
      return {};
    }
    if (objectCreate) {
      return objectCreate(proto);
    }
    object.prototype = proto;
    var result = new object;
    object.prototype = undefined;
    return result;
  };
}());

var _baseCreate = baseCreate;

/**
 * Initializes an object clone.
 *
 * @private
 * @param {Object} object The object to clone.
 * @returns {Object} Returns the initialized clone.
 */
function initCloneObject(object) {
  return (typeof object.constructor == 'function' && !_isPrototype(object))
    ? _baseCreate(_getPrototype(object))
    : {};
}

var _initCloneObject = initCloneObject;

/**
 * This method is like `_.isArrayLike` except that it also checks if `value`
 * is an object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array-like object,
 *  else `false`.
 * @example
 *
 * _.isArrayLikeObject([1, 2, 3]);
 * // => true
 *
 * _.isArrayLikeObject(document.body.children);
 * // => true
 *
 * _.isArrayLikeObject('abc');
 * // => false
 *
 * _.isArrayLikeObject(_.noop);
 * // => false
 */
function isArrayLikeObject(value) {
  return isObjectLike_1(value) && isArrayLike_1(value);
}

var isArrayLikeObject_1 = isArrayLikeObject;

/** `Object#toString` result references. */
var objectTag$3 = '[object Object]';

/** Used for built-in method references. */
var funcProto$2 = Function.prototype,
    objectProto$e = Object.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString$2 = funcProto$2.toString;

/** Used to check objects for own properties. */
var hasOwnProperty$b = objectProto$e.hasOwnProperty;

/** Used to infer the `Object` constructor. */
var objectCtorString = funcToString$2.call(Object);

/**
 * Checks if `value` is a plain object, that is, an object created by the
 * `Object` constructor or one with a `[[Prototype]]` of `null`.
 *
 * @static
 * @memberOf _
 * @since 0.8.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a plain object, else `false`.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 * }
 *
 * _.isPlainObject(new Foo);
 * // => false
 *
 * _.isPlainObject([1, 2, 3]);
 * // => false
 *
 * _.isPlainObject({ 'x': 0, 'y': 0 });
 * // => true
 *
 * _.isPlainObject(Object.create(null));
 * // => true
 */
function isPlainObject$1(value) {
  if (!isObjectLike_1(value) || _baseGetTag(value) != objectTag$3) {
    return false;
  }
  var proto = _getPrototype(value);
  if (proto === null) {
    return true;
  }
  var Ctor = hasOwnProperty$b.call(proto, 'constructor') && proto.constructor;
  return typeof Ctor == 'function' && Ctor instanceof Ctor &&
    funcToString$2.call(Ctor) == objectCtorString;
}

var isPlainObject_1 = isPlainObject$1;

/**
 * Gets the value at `key`, unless `key` is "__proto__" or "constructor".
 *
 * @private
 * @param {Object} object The object to query.
 * @param {string} key The key of the property to get.
 * @returns {*} Returns the property value.
 */
function safeGet(object, key) {
  if (key === 'constructor' && typeof object[key] === 'function') {
    return;
  }

  if (key == '__proto__') {
    return;
  }

  return object[key];
}

var _safeGet = safeGet;

/**
 * Copies properties of `source` to `object`.
 *
 * @private
 * @param {Object} source The object to copy properties from.
 * @param {Array} props The property identifiers to copy.
 * @param {Object} [object={}] The object to copy properties to.
 * @param {Function} [customizer] The function to customize copied values.
 * @returns {Object} Returns `object`.
 */
function copyObject(source, props, object, customizer) {
  var isNew = !object;
  object || (object = {});

  var index = -1,
      length = props.length;

  while (++index < length) {
    var key = props[index];

    var newValue = customizer
      ? customizer(object[key], source[key], key, object, source)
      : undefined;

    if (newValue === undefined) {
      newValue = source[key];
    }
    if (isNew) {
      _baseAssignValue(object, key, newValue);
    } else {
      _assignValue(object, key, newValue);
    }
  }
  return object;
}

var _copyObject = copyObject;

/**
 * Converts `value` to a plain object flattening inherited enumerable string
 * keyed properties of `value` to own properties of the plain object.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {Object} Returns the converted plain object.
 * @example
 *
 * function Foo() {
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.assign({ 'a': 1 }, new Foo);
 * // => { 'a': 1, 'b': 2 }
 *
 * _.assign({ 'a': 1 }, _.toPlainObject(new Foo));
 * // => { 'a': 1, 'b': 2, 'c': 3 }
 */
function toPlainObject(value) {
  return _copyObject(value, keysIn_1(value));
}

var toPlainObject_1 = toPlainObject;

/**
 * A specialized version of `baseMerge` for arrays and objects which performs
 * deep merges and tracks traversed objects enabling objects with circular
 * references to be merged.
 *
 * @private
 * @param {Object} object The destination object.
 * @param {Object} source The source object.
 * @param {string} key The key of the value to merge.
 * @param {number} srcIndex The index of `source`.
 * @param {Function} mergeFunc The function to merge values.
 * @param {Function} [customizer] The function to customize assigned values.
 * @param {Object} [stack] Tracks traversed source values and their merged
 *  counterparts.
 */
function baseMergeDeep(object, source, key, srcIndex, mergeFunc, customizer, stack) {
  var objValue = _safeGet(object, key),
      srcValue = _safeGet(source, key),
      stacked = stack.get(srcValue);

  if (stacked) {
    _assignMergeValue(object, key, stacked);
    return;
  }
  var newValue = customizer
    ? customizer(objValue, srcValue, (key + ''), object, source, stack)
    : undefined;

  var isCommon = newValue === undefined;

  if (isCommon) {
    var isArr = isArray_1(srcValue),
        isBuff = !isArr && isBuffer_1(srcValue),
        isTyped = !isArr && !isBuff && isTypedArray_1(srcValue);

    newValue = srcValue;
    if (isArr || isBuff || isTyped) {
      if (isArray_1(objValue)) {
        newValue = objValue;
      }
      else if (isArrayLikeObject_1(objValue)) {
        newValue = _copyArray(objValue);
      }
      else if (isBuff) {
        isCommon = false;
        newValue = _cloneBuffer(srcValue, true);
      }
      else if (isTyped) {
        isCommon = false;
        newValue = _cloneTypedArray(srcValue, true);
      }
      else {
        newValue = [];
      }
    }
    else if (isPlainObject_1(srcValue) || isArguments_1(srcValue)) {
      newValue = objValue;
      if (isArguments_1(objValue)) {
        newValue = toPlainObject_1(objValue);
      }
      else if (!isObject_1(objValue) || isFunction_1(objValue)) {
        newValue = _initCloneObject(srcValue);
      }
    }
    else {
      isCommon = false;
    }
  }
  if (isCommon) {
    // Recursively merge objects and arrays (susceptible to call stack limits).
    stack.set(srcValue, newValue);
    mergeFunc(newValue, srcValue, srcIndex, customizer, stack);
    stack['delete'](srcValue);
  }
  _assignMergeValue(object, key, newValue);
}

var _baseMergeDeep = baseMergeDeep;

/**
 * The base implementation of `_.merge` without support for multiple sources.
 *
 * @private
 * @param {Object} object The destination object.
 * @param {Object} source The source object.
 * @param {number} srcIndex The index of `source`.
 * @param {Function} [customizer] The function to customize merged values.
 * @param {Object} [stack] Tracks traversed source values and their merged
 *  counterparts.
 */
function baseMerge(object, source, srcIndex, customizer, stack) {
  if (object === source) {
    return;
  }
  _baseFor(source, function(srcValue, key) {
    stack || (stack = new _Stack);
    if (isObject_1(srcValue)) {
      _baseMergeDeep(object, source, key, srcIndex, baseMerge, customizer, stack);
    }
    else {
      var newValue = customizer
        ? customizer(_safeGet(object, key), srcValue, (key + ''), object, source, stack)
        : undefined;

      if (newValue === undefined) {
        newValue = srcValue;
      }
      _assignMergeValue(object, key, newValue);
    }
  }, keysIn_1);
}

var _baseMerge = baseMerge;

/**
 * Used by `_.defaultsDeep` to customize its `_.merge` use to merge source
 * objects into destination objects that are passed thru.
 *
 * @private
 * @param {*} objValue The destination value.
 * @param {*} srcValue The source value.
 * @param {string} key The key of the property to merge.
 * @param {Object} object The parent object of `objValue`.
 * @param {Object} source The parent object of `srcValue`.
 * @param {Object} [stack] Tracks traversed source values and their merged
 *  counterparts.
 * @returns {*} Returns the value to assign.
 */
function customDefaultsMerge(objValue, srcValue, key, object, source, stack) {
  if (isObject_1(objValue) && isObject_1(srcValue)) {
    // Recursively merge objects and arrays (susceptible to call stack limits).
    stack.set(srcValue, objValue);
    _baseMerge(objValue, srcValue, undefined, customDefaultsMerge, stack);
    stack['delete'](srcValue);
  }
  return objValue;
}

var _customDefaultsMerge = customDefaultsMerge;

/**
 * Checks if the given arguments are from an iteratee call.
 *
 * @private
 * @param {*} value The potential iteratee value argument.
 * @param {*} index The potential iteratee index or key argument.
 * @param {*} object The potential iteratee object argument.
 * @returns {boolean} Returns `true` if the arguments are from an iteratee call,
 *  else `false`.
 */
function isIterateeCall(value, index, object) {
  if (!isObject_1(object)) {
    return false;
  }
  var type = typeof index;
  if (type == 'number'
        ? (isArrayLike_1(object) && _isIndex(index, object.length))
        : (type == 'string' && index in object)
      ) {
    return eq_1(object[index], value);
  }
  return false;
}

var _isIterateeCall = isIterateeCall;

/**
 * Creates a function like `_.assign`.
 *
 * @private
 * @param {Function} assigner The function to assign values.
 * @returns {Function} Returns the new assigner function.
 */
function createAssigner(assigner) {
  return _baseRest(function(object, sources) {
    var index = -1,
        length = sources.length,
        customizer = length > 1 ? sources[length - 1] : undefined,
        guard = length > 2 ? sources[2] : undefined;

    customizer = (assigner.length > 3 && typeof customizer == 'function')
      ? (length--, customizer)
      : undefined;

    if (guard && _isIterateeCall(sources[0], sources[1], guard)) {
      customizer = length < 3 ? undefined : customizer;
      length = 1;
    }
    object = Object(object);
    while (++index < length) {
      var source = sources[index];
      if (source) {
        assigner(object, source, index, customizer);
      }
    }
    return object;
  });
}

var _createAssigner = createAssigner;

/**
 * This method is like `_.merge` except that it accepts `customizer` which
 * is invoked to produce the merged values of the destination and source
 * properties. If `customizer` returns `undefined`, merging is handled by the
 * method instead. The `customizer` is invoked with six arguments:
 * (objValue, srcValue, key, object, source, stack).
 *
 * **Note:** This method mutates `object`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Object
 * @param {Object} object The destination object.
 * @param {...Object} sources The source objects.
 * @param {Function} customizer The function to customize assigned values.
 * @returns {Object} Returns `object`.
 * @example
 *
 * function customizer(objValue, srcValue) {
 *   if (_.isArray(objValue)) {
 *     return objValue.concat(srcValue);
 *   }
 * }
 *
 * var object = { 'a': [1], 'b': [2] };
 * var other = { 'a': [3], 'b': [4] };
 *
 * _.mergeWith(object, other, customizer);
 * // => { 'a': [1, 3], 'b': [2, 4] }
 */
var mergeWith = _createAssigner(function(object, source, srcIndex, customizer) {
  _baseMerge(object, source, srcIndex, customizer);
});

var mergeWith_1 = mergeWith;

/**
 * This method is like `_.defaults` except that it recursively assigns
 * default properties.
 *
 * **Note:** This method mutates `object`.
 *
 * @static
 * @memberOf _
 * @since 3.10.0
 * @category Object
 * @param {Object} object The destination object.
 * @param {...Object} [sources] The source objects.
 * @returns {Object} Returns `object`.
 * @see _.defaults
 * @example
 *
 * _.defaultsDeep({ 'a': { 'b': 2 } }, { 'a': { 'b': 1, 'c': 3 } });
 * // => { 'a': { 'b': 2, 'c': 3 } }
 */
var defaultsDeep = _baseRest(function(args) {
  args.push(undefined, _customDefaultsMerge);
  return _apply(mergeWith_1, undefined, args);
});

var defaultsDeep_1 = defaultsDeep;

/**
 * Deep merge the default object properties that are not defined in the destination object.
 * @param object  The destination object.
 * @param defaultObject   The default object.
 */

const setDefaults = (object, defaultObject) => defaultsDeep_1(object, defaultObject);

/**
 * Get a `renderLeaf` handler for a single type, with default options.
 */

const getRenderLeafDefault = ({
  key,
  defaultOptions,
  options
}) => {
  const keyOptions = setDefaults(options, defaultOptions)[key];
  return getRenderLeaf(keyOptions);
};

/**
 * RegExps.
 * A URL must match #1 and then at least one of #2/#3.
 * Use two levels of REs to avoid REDOS.
 */
const protocolAndDomainRE = /^(?:\w+:)?\/\/(\S+)$/;
const localhostDomainRE = /^localhost[:?\d]*(?:[^:?\d]\S*)?$/;
const nonLocalhostDomainRE = /^[^\s.]+\.\S{2,}$/;
/**
 * Loosely validate a URL `string`.
 */

const isUrl = string => {
  if (typeof string !== 'string') {
    return false;
  }

  const match = string.match(protocolAndDomainRE);

  if (!match) {
    return false;
  }

  const everythingAfterProtocol = match[1];

  if (!everythingAfterProtocol) {
    return false;
  }

  try {
    new URL(string);
  } catch (err) {
    return false;
  }

  return localhostDomainRE.test(everythingAfterProtocol) || nonLocalhostDomainRE.test(everythingAfterProtocol);
};

/**
 * Normalize the descendants to a valid document fragment.
 */

const normalizeDescendantsToDocumentFragment = descendants => {
  descendants.forEach(element => {
    if (Element.isElement(element)) {
      normalizeDescendantsToDocumentFragment(element.children);
    }
  });

  if (!descendants.length) {
    descendants.push({
      text: ''
    });
  }

  return descendants;
};

/**
 * Add/remove marks in the selection.
 * @param key mark to toggle
 * @param clear marks to clear when adding mark
 */

const toggleMark = (editor, key, clear = []) => {
  const isActive = isMarkActive(editor, key);

  if (isActive) {
    editor.removeMark(key);
    return;
  }

  const clears = castArray_1(clear);
  clears.forEach(item => {
    editor.removeMark(item);
  });
  editor.addMark(key, true);
};

/**
 * Get `onKeyDown` handler if there is a hotkey defined.
 */

const onKeyDownMark = ({
  type,
  hotkey,
  clear
}) => hotkey ? (e, editor) => {
  if (isHotkey(hotkey, e)) {
    e.preventDefault();
    toggleMark(editor, type, clear);
  }
} : undefined;

/**
 * Get `onKeyDown` handler for mark with default options.
 */

const onKeyDownMarkDefault = ({
  key,
  defaultOptions,
  options
}) => {
  const keyOptions = setDefaults(options, defaultOptions)[key];
  return onKeyDownMark(keyOptions);
};

function pipe(x, ...fns) {
  return fns.reduce((y, fn) => fn(y), x);
}

/**
 * Is the word at the point after a trigger (punctuation character)
 * https://github.com/ianstormtaylor/slate/blob/master/packages/slate/src/utils/string.ts#L6
 */

const isWordAfterTrigger = (editor, {
  at,
  trigger
}) => {
  // Point at the start of previous word (excluding punctuation)
  const wordBefore = Editor.before(editor, at, {
    unit: 'word'
  }); // Point before wordBefore

  const before = wordBefore && Editor.before(editor, wordBefore); // Range from before to start

  const beforeRange = before && Editor.range(editor, before, at); // Before text

  const beforeText = getText(editor, beforeRange); // Starts with char and ends with word characters

  const escapedTrigger = escapeRegExp(trigger);
  const beforeRegex = new RegExp(`^${escapedTrigger}(\\w+)$`); // Match regex on before text

  const match = !!beforeText && beforeText.match(beforeRegex);
  return {
    range: beforeRange,
    match
  };
};

/** Used for built-in method references. */
var objectProto$f = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$c = objectProto$f.hasOwnProperty;

/**
 * Assigns own and inherited enumerable string keyed properties of source
 * objects to the destination object for all destination properties that
 * resolve to `undefined`. Source objects are applied from left to right.
 * Once a property is set, additional values of the same property are ignored.
 *
 * **Note:** This method mutates `object`.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Object
 * @param {Object} object The destination object.
 * @param {...Object} [sources] The source objects.
 * @returns {Object} Returns `object`.
 * @see _.defaultsDeep
 * @example
 *
 * _.defaults({ 'a': 1 }, { 'b': 2 }, { 'a': 3 });
 * // => { 'a': 1, 'b': 2 }
 */
var defaults = _baseRest(function(object, sources) {
  object = Object(object);

  var index = -1;
  var length = sources.length;
  var guard = length > 2 ? sources[2] : undefined;

  if (guard && _isIterateeCall(sources[0], sources[1], guard)) {
    length = 1;
  }

  while (++index < length) {
    var source = sources[index];
    var props = keysIn_1(source);
    var propsIndex = -1;
    var propsLength = props.length;

    while (++propsIndex < propsLength) {
      var key = props[propsIndex];
      var value = object[key];

      if (value === undefined ||
          (eq_1(value, objectProto$f[key]) && !hasOwnProperty$c.call(object, key))) {
        object[key] = source[key];
      }
    }
  }

  return object;
});

var defaults_1 = defaults;

/**
 * Recursively apply an operation to children nodes with a query.
 */

const applyDeepToNodes = ({
  node,
  source,
  apply,
  query
}) => {
  const entry = [node, []];

  if (isNodeType(entry, query)) {
    if (source instanceof Function) {
      apply(node, source());
    } else {
      apply(node, source);
    }
  }

  if (!isAncestor(node)) return;
  node.children.forEach(child => {
    applyDeepToNodes({
      node: child,
      source,
      apply,
      query
    });
  });
};

/**
 * Recursively merge a source object to children nodes with a query.
 */

const defaultsDeepToNodes = options => {
  applyDeepToNodes(Object.assign(Object.assign({}, options), {
    apply: defaults_1
  }));
};

/**
 * This method is like `_.assign` except that it recursively merges own and
 * inherited enumerable string keyed properties of source objects into the
 * destination object. Source properties that resolve to `undefined` are
 * skipped if a destination value exists. Array and plain object properties
 * are merged recursively. Other objects and value types are overridden by
 * assignment. Source objects are applied from left to right. Subsequent
 * sources overwrite property assignments of previous sources.
 *
 * **Note:** This method mutates `object`.
 *
 * @static
 * @memberOf _
 * @since 0.5.0
 * @category Object
 * @param {Object} object The destination object.
 * @param {...Object} [sources] The source objects.
 * @returns {Object} Returns `object`.
 * @example
 *
 * var object = {
 *   'a': [{ 'b': 2 }, { 'd': 4 }]
 * };
 *
 * var other = {
 *   'a': [{ 'c': 3 }, { 'e': 5 }]
 * };
 *
 * _.merge(object, other);
 * // => { 'a': [{ 'b': 2, 'c': 3 }, { 'd': 4, 'e': 5 }] }
 */
var merge = _createAssigner(function(object, source, srcIndex) {
  _baseMerge(object, source, srcIndex);
});

var merge_1 = merge;

/**
 * Recursively merge a source object to children nodes with a query.
 */

const mergeDeepToNodes = options => {
  applyDeepToNodes(Object.assign(Object.assign({}, options), {
    apply: merge_1
  }));
};

/**
 * Enables support for inserting nodes with an id key.
 */

const withNodeID = ({
  idKey = 'id',
  idCreator = () => Date.now(),
  filterText = true,
  resetExistingID = false,
  filter = () => true,
  allow,
  exclude
} = {}) => e => {
  const editor = e;
  const {
    apply
  } = editor;

  const idPropsCreator = () => ({
    [idKey]: idCreator()
  });

  editor.removedIDs = new Set();

  editor.apply = operation => {
    if (operation.type === 'insert_node') {
      const newFilter = entry => {
        const [node] = entry;
        return filter(entry) && filterText ? Element.isElement(node) : isDescendant(node);
      };

      const node = cloneDeep(operation.node); // it will not overwrite ids once it's set as it's read-only

      const applyDeepToNodes = resetExistingID ? mergeDeepToNodes : defaultsDeepToNodes;
      applyDeepToNodes({
        node,
        source: idPropsCreator,
        query: {
          filter: newFilter,
          allow,
          exclude
        }
      });
      return apply(Object.assign(Object.assign({}, operation), {
        node
      }));
    }

    if (operation.type === 'split_node' && (!filterText || operation.properties.type)) {
      let id = operation.properties[idKey];

      if (editor.removedIDs.has(id)) {
        editor.removedIDs.delete(id);
      } else {
        id = idCreator();
      }

      return apply(Object.assign(Object.assign({}, operation), {
        properties: Object.assign(Object.assign({}, operation.properties), {
          [idKey]: id
        })
      }));
    }

    if (operation.type === 'merge_node' && (!filterText || operation.properties.type)) {
      editor.removedIDs.add(operation.properties.id);
    }

    return apply(operation);
  };

  return editor;
};

const DEFAULT_ELEMENT = 'p';

/**
 * Toggle the type of the selected node with a configurable default type.
 * @param defaultType
 */

const withToggleType = ({
  defaultType: type = DEFAULT_ELEMENT
} = {}) => e => {
  const editor = e;

  editor.toggleType = (activeType, defaultType = type) => {
    const isActive = isNodeTypeIn(editor, activeType);
    Transforms.setNodes(editor, {
      type: isActive ? defaultType : activeType
    });
  };

  return editor;
};

/**
 * Select the end point of the block above the selection.
 */

const selectEndOfBlockAboveSelection = editor => {
  const [, path] = getBlockAbove(editor);
  Transforms.select(editor, Editor.end(editor, path));
};

/**
 * Unwrap nodes by type
 */

const unwrapNodesByType = (editor, types, options = {}) => {
  types = castArray_1(types);
  Transforms.unwrapNodes(editor, Object.assign({
    match: n => types.includes(n.type)
  }, options));
};

/**
 * {@link Transforms.wrapNodes} with more options.
 */

const wrapNodes = (editor, element, options = {}) => {
  unhangRange(editor, options);
  Transforms.wrapNodes(editor, element, options);
};

/**
 * Unwrap if the node type is in selection.
 * Wrap otherwise.
 */

const toggleWrapNodes = (editor, type) => {
  if (isNodeTypeIn(editor, type)) {
    unwrapNodesByType(editor, type);
  } else {
    wrapNodes(editor, {
      type,
      children: []
    });
  }
};

/**
 * Extends `editor` with transforms:
 * - `editor.insertNodes` to use `Transforms.insertNodes` as `editor.insertNode` does not accept `options`.
 */

const withTransforms = () => editor => {
  const e = editor;

  e.insertNodes = (nodes, options) => {
    try {
      Transforms.insertNodes(editor, nodes, options);
    } catch (err) {}
  };

  return e;
};

const PortalBody = ({
  children
}) => /*#__PURE__*/createPortal(children, document.body);

const getStyledComponentStyles = ({
  className
}) => {
  return {
    root: [className, {// Insert css properties
    }]
  };
};

const getClassNames$1 = classNamesFunction();
/**
 * StyledComponent with no default styles.
 * [Use the `styles` API to add your own styles.](https://github.com/OfficeDev/office-ui-fabric-react/wiki/Component-Styling)
 */

const StyledComponentBase = ({
  children,
  styles,
  className,
  as = 'div'
}) => {
  const classNames = getClassNames$1(styles, {
    className
  });
  const Tag = as;
  return /*#__PURE__*/createElement(Tag, {
    className: classNames.root
  }, children);
};
/**
 * StyledComponent
 */

const StyledComponent = styled$1(StyledComponentBase, getStyledComponentStyles, undefined, {
  scope: 'StyledComponent'
});

const getClassNames$2 = classNamesFunction();
/**
 * StyledElement with no default styles.
 * [Use the `styles` API to add your own styles.](https://github.com/OfficeDev/office-ui-fabric-react/wiki/Component-Styling)
 */

const StyledElementBase = ({
  attributes,
  children,
  className,
  styles,
  as = 'div'
}) => {
  const classNames = getClassNames$2(styles, {
    className
  });
  const Tag = as;
  return /*#__PURE__*/createElement(Tag, Object.assign({}, attributes, {
    className: classNames.root
  }), children);
};
const StyledElement = styled$1(StyledElementBase, getStyledComponentStyles, undefined, {
  scope: 'StyledElement'
});

const getClassNames$3 = classNamesFunction();
/**
 * StyledLeaf with no default styles.
 * [Use the `styles` API to add your own styles.](https://github.com/OfficeDev/office-ui-fabric-react/wiki/Component-Styling)
 */

const StyledLeafBase = ({
  attributes,
  children,
  className,
  styles,
  as = 'span'
}) => {
  const classNames = getClassNames$3(styles, {
    className
  });
  const Tag = as;
  return /*#__PURE__*/createElement(Tag, Object.assign({}, attributes, {
    className: classNames.root
  }), children);
};
const StyledLeaf = styled$1(StyledLeafBase, getStyledComponentStyles, undefined, {
  scope: 'StyledLeaf'
});

const classNames$1 = {
  root: 'slate-Toolbar'
};
const getToolbarStyles = memoizeFunction(styles => {
  return concatStyleSets({
    root: [classNames$1.root, {
      display: 'flex',
      alignItems: 'center',
      boxSizing: 'content-box',
      userSelect: 'none',
      minHeight: 40,
      color: 'rgb(68, 68, 68)'
    }]
  }, styles);
});

const getClassNames$4 = classNamesFunction();
const ToolbarBase = /*#__PURE__*/forwardRef(({
  className,
  styles,
  children
}, ref) => {
  const classNames = getClassNames$4(styles, {
    className
  });
  return /*#__PURE__*/createElement("div", {
    "data-testid": "Toolbar",
    className: classNames.root,
    ref: ref
  }, children);
});
const Toolbar = /*#__PURE__*/forwardRef((_a, ref) => {
  var {
    styles
  } = _a,
      props = __rest(_a, ["styles"]);

  return /*#__PURE__*/createElement(ToolbarBase, Object.assign({}, props, {
    ref: ref,
    styles: concatStyleSets(getToolbarStyles(styles))
  }));
});

const classNames$2 = {
  root: 'slate-BalloonToolbar'
};
const getBalloonToolbarStyles = memoizeFunction((className, styles, theme, hidden, hiddenDelay, direction, arrow) => {
  let color = 'rgb(157, 170, 182)';
  let colorActive = 'white';
  let background = 'rgb(36, 42, 49)';
  let borderColor = 'transparent';

  if (theme === 'light') {
    color = 'rgba(0, 0, 0, 0.50)';
    colorActive = 'black';
    background = 'rgb(250, 250, 250)';
    borderColor = 'rgb(196, 196, 196)';
  }

  let marginTop;
  let arrowStyle = {};
  let arrowBorderStyle = {};

  if (arrow) {
    arrowStyle = {
      left: '50%',
      content: '" "',
      position: 'absolute',
      marginTop: '-1px',
      transform: 'translateX(-50%)',
      borderColor: `${background} transparent`,
      borderStyle: 'solid'
    };

    if (direction === 'top') {
      arrowStyle = Object.assign(Object.assign({}, arrowStyle), {
        top: '100%',
        bottom: 'auto',
        borderWidth: '8px 8px 0px'
      });

      if (theme === 'light') {
        arrowBorderStyle = Object.assign(Object.assign({}, arrowStyle), {
          marginTop: 0,
          borderWidth: '9px 9px 0px',
          borderColor: `${borderColor} transparent`
        });
      }
    } else {
      arrowStyle = Object.assign(Object.assign({}, arrowStyle), {
        top: 'auto',
        bottom: '100%',
        borderWidth: '0px 8px 8px'
      });

      if (theme === 'light') {
        arrowBorderStyle = Object.assign(Object.assign({}, arrowStyle), {
          marginTop: 0,
          borderWidth: '0px 9px 9px',
          borderColor: `${borderColor} transparent`
        });
      }
    }
  }

  if (direction === 'top') {
    marginTop = -9;
  } else {
    marginTop = 9;
  }

  return concatStyleSets({
    root: [classNames$2.root, {
      position: 'absolute',
      zIndex: 500,
      background,
      color,
      whiteSpace: 'nowrap',
      visibility: 'hidden',
      border: 'solid #000',
      borderRadius: 4,
      borderWidth: 1,
      borderColor,
      padding: '0 4px',
      marginTop,
      transition: hiddenDelay ? '' : 'top 75ms ease-out,left 75ms ease-out',
      selectors: {
        '::before': arrowBorderStyle,
        '::after': arrowStyle,
        '.slate-ToolbarButton-active, .slate-ToolbarButton:hover': {
          color: colorActive
        }
      }
    }, !hidden && {
      visibility: 'visible'
    }, className]
  }, styles);
});

const setPositionAtSelection = (el, direction = 'top') => {
  const domSelection = window.getSelection();
  if (!domSelection || domSelection.rangeCount < 1) return;
  const domRange = domSelection.getRangeAt(0);
  const rect = domRange.getBoundingClientRect();

  if (direction === 'top') {
    el.style.top = `${rect.top + window.pageYOffset - el.offsetHeight}px`;
  } else {
    el.style.top = `${rect.bottom + window.pageYOffset}px`;
  }

  el.style.left = `${rect.left + window.pageXOffset - el.offsetWidth / 2 + rect.width / 2}px`;
};

/**
 * Move when the selection changes.
 */

const useBalloonMove = ({
  editor,
  ref,
  direction
}) => {
  const selectionExpanded = isSelectionExpanded(editor);
  const selectionText = getSelectionText(editor);
  useEffect(() => {
    ref.current && selectionExpanded && setPositionAtSelection(ref.current, direction);
  }, [direction, selectionText.length, selectionExpanded, ref]);
};

/**
 * Hide if not selecting.
 * If hiddenDelay = 0 and the selection changes: show.
 * If hiddenDelay > 0: hide when the selection length changes.
 */

const useBalloonShow = ({
  editor,
  ref,
  hiddenDelay
}) => {
  const [hidden, setHidden] = useState(true);
  const selectionExpanded = isSelectionExpanded(editor);
  const selectionText = getSelectionText(editor);
  const show = useCallback(() => {
    if (ref.current && hidden && selectionExpanded) {
      setHidden(false);
    }
  }, [hidden, ref, selectionExpanded]);
  const [,, reset] = useTimeoutFn(show, hiddenDelay);
  useEffect(() => {
    if (!hiddenDelay) {
      show();
    }
  }, [selectionText.length, reset, hiddenDelay, show]);
  /**
   * Hide if not selecting.
   */

  useEffect(() => {
    if (!hidden && !selectionExpanded) {
      setHidden(true);

      if (ref.current) {
        ref.current.removeAttribute('style');
      }
    }
  }, [hidden, hiddenDelay, reset, selectionExpanded, show, selectionText.length, ref]);
  /**
   * If hiddenDelay > 0:
   * Hide when the selection length changes.
   */

  useEffect(() => {
    if (!hiddenDelay) return;
    reset();
    setHidden(true);
  }, [hiddenDelay, selectionText.length, reset]);
  return [hidden];
};

const BalloonToolbar = ({
  className,
  styles,
  children,
  hiddenDelay = 0,
  direction = 'top',
  theme = 'dark',
  arrow = false
}) => {
  const ref = useRef(null);
  const editor = useSlate();
  const [hidden] = useBalloonShow({
    editor,
    ref,
    hiddenDelay
  });
  useBalloonMove({
    editor,
    ref,
    direction
  });
  return /*#__PURE__*/createElement(PortalBody, null, /*#__PURE__*/createElement(Toolbar, {
    ref: ref,
    styles: getBalloonToolbarStyles(className, styles, theme, hidden, hiddenDelay, direction, arrow)
  }, children));
};

const classNames$3 = {
  root: 'slate-HeadingToolbar'
};
const getHeadingToolbarStyles = ({
  className
} = {}) => {
  const color = '#06c';
  const styles = {
    root: [classNames$3.root, {
      position: 'relative',
      padding: '1px 18px 17px',
      margin: '0 -20px',
      borderBottom: '2px solid #eee',
      marginBottom: '20px',
      selectors: {
        '.slate-ToolbarButton-active, .slate-ToolbarButton:hover': {
          color
        }
      }
    }, className]
  };
  return concatStyleSets(getToolbarStyles(), styles);
};

const HeadingToolbar = styled$1(ToolbarBase, getHeadingToolbarStyles(), undefined, {
  scope: 'HeadingToolbar'
});

const classNames$4 = {
  root: 'slate-ToolbarButton',
  active: 'slate-ToolbarButton-active'
};
const getToolbarButtonStyles = ({
  className,
  active
} = {}) => ({
  root: [classNames$4.root, {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '28px',
    height: '24px',
    userSelect: 'none',
    cursor: 'pointer',
    verticalAlign: 'middle',
    selectors: {
      '> svg': {
        display: 'block',
        width: '20px',
        height: '20px'
      }
    }
  }, active && classNames$4.active, className]
});

const getClassNames$5 = classNamesFunction();
const ToolbarButtonBase = ({
  className,
  styles,
  icon,
  tooltip,
  active,
  onMouseDown
}) => {
  const spanProps = {
    onMouseDown
  };
  const classNames = getClassNames$5(styles, {
    className,
    active
  });
  const tooltipProps = Object.assign({
    content: '',
    arrow: true,
    offset: [0, 17],
    delay: 0,
    duration: [200, 0],
    hideOnClick: false
  }, tooltip);
  const button = /*#__PURE__*/createElement("span", Object.assign({
    "data-testid": "ToolbarButton",
    className: classNames.root
  }, spanProps), icon);
  return tooltip ? /*#__PURE__*/createElement(Tippy, Object.assign({}, tooltipProps), button) : button;
};
const ToolbarButton = styled$1(ToolbarButtonBase, getToolbarButtonStyles, undefined, {
  scope: 'ToolbarButton'
});

/**
 * Toolbar button to toggle the type of elements in selection.
 */

const ToolbarElement = _a => {
  var {
    type
  } = _a,
      props = __rest(_a, ["type"]);

  const editor = useSlate();
  return /*#__PURE__*/createElement(ToolbarButton, Object.assign({
    active: isNodeTypeIn(editor, type),
    onMouseDown: getPreventDefaultHandler(editor.toggleType, type)
  }, props));
};

/**
 * Toolbar button to toggle the mark of the leaves in selection.
 */

const ToolbarMark = _a => {
  var {
    type,
    clear
  } = _a,
      props = __rest(_a, ["type", "clear"]);

  const editor = useSlate();
  return /*#__PURE__*/createElement(ToolbarButton, Object.assign({
    active: isMarkActive(editor, type),
    onMouseDown: getPreventDefaultHandler(toggleMark, editor, type, clear)
  }, props));
};

const getPreviewLeafStyles = ({
  className,
  title,
  list,
  italic,
  hr,
  code,
  bold,
  blockquote
}) => {
  const boldStyle = {
    fontWeight: 'bold'
  };
  const italicStyle = {
    fontStyle: 'italic'
  };
  const titleStyle = {
    display: 'inline-block',
    fontWeight: 'bold',
    fontSize: '20px',
    margin: '20px 0 10px 0'
  };
  const listStyle = {
    paddingLeft: '10px',
    fontSize: '20px',
    lineHeight: '10px'
  };
  const hrStyle = {
    display: 'block',
    textAlign: 'center',
    borderBottom: '2px solid #ddd'
  };
  const blockquoteStyle = {
    display: 'inline-block',
    borderLeft: '2px solid #ddd',
    paddingLeft: '10px',
    color: '#aaa',
    fontStyle: 'italic'
  };
  const codeStyle = {
    fontFamily: 'monospace',
    backgroundColor: '#eee',
    padding: '3px'
  };
  let rootStyle = {};
  if (bold) rootStyle = Object.assign(Object.assign({}, rootStyle), boldStyle);
  if (italic) rootStyle = Object.assign(Object.assign({}, rootStyle), italicStyle);
  if (title) rootStyle = Object.assign(Object.assign({}, rootStyle), titleStyle);
  if (list) rootStyle = Object.assign(Object.assign({}, rootStyle), listStyle);
  if (hr) rootStyle = Object.assign(Object.assign({}, rootStyle), hrStyle);
  if (blockquote) rootStyle = Object.assign(Object.assign({}, rootStyle), blockquoteStyle);
  if (code) rootStyle = Object.assign(Object.assign({}, rootStyle), codeStyle);
  return {
    root: [rootStyle, className]
  };
};

const getClassNames$6 = classNamesFunction();
/**
 * PreviewLeaf with no default styles.
 * [Use the `styles` API to add your own styles.](https://github.com/OfficeDev/office-ui-fabric-react/wiki/Component-Styling)
 */

const PreviewLeafBase = ({
  children,
  attributes,
  styles,
  className,
  leaf
}) => {
  const classNames = getClassNames$6(styles, Object.assign({
    className
  }, leaf));
  return /*#__PURE__*/createElement("span", Object.assign({}, attributes, {
    className: classNames.root
  }), children);
};
/**
 * PreviewLeaf
 */

const PreviewLeaf = styled$1(PreviewLeafBase, getPreviewLeafStyles, undefined, {
  scope: 'PreviewLeaf'
});

(function (Prism) {

	// Allow only one line break
	var inner = /(?:\\.|[^\\\n\r]|(?:\n|\r\n?)(?!\n|\r\n?))/.source;

	/**
	 * This function is intended for the creation of the bold or italic pattern.
	 *
	 * This also adds a lookbehind group to the given pattern to ensure that the pattern is not backslash-escaped.
	 *
	 * _Note:_ Keep in mind that this adds a capturing group.
	 *
	 * @param {string} pattern
	 * @param {boolean} starAlternative Whether to also add an alternative where all `_`s are replaced with `*`s.
	 * @returns {RegExp}
	 */
	function createInline(pattern, starAlternative) {
		pattern = pattern.replace(/<inner>/g, function () { return inner; });
		if (starAlternative) {
			pattern = pattern + '|' + pattern.replace(/_/g, '\\*');
		}
		return RegExp(/((?:^|[^\\])(?:\\{2})*)/.source + '(?:' + pattern + ')');
	}


	var tableCell = /(?:\\.|``.+?``|`[^`\r\n]+`|[^\\|\r\n`])+/.source;
	var tableRow = /\|?__(?:\|__)+\|?(?:(?:\n|\r\n?)|$)/.source.replace(/__/g, function () { return tableCell; });
	var tableLine = /\|?[ \t]*:?-{3,}:?[ \t]*(?:\|[ \t]*:?-{3,}:?[ \t]*)+\|?(?:\n|\r\n?)/.source;


	Prism.languages.markdown = Prism.languages.extend('markup', {});
	Prism.languages.insertBefore('markdown', 'prolog', {
		'blockquote': {
			// > ...
			pattern: /^>(?:[\t ]*>)*/m,
			alias: 'punctuation'
		},
		'table': {
			pattern: RegExp('^' + tableRow + tableLine + '(?:' + tableRow + ')*', 'm'),
			inside: {
				'table-data-rows': {
					pattern: RegExp('^(' + tableRow + tableLine + ')(?:' + tableRow + ')*$'),
					lookbehind: true,
					inside: {
						'table-data': {
							pattern: RegExp(tableCell),
							inside: Prism.languages.markdown
						},
						'punctuation': /\|/
					}
				},
				'table-line': {
					pattern: RegExp('^(' + tableRow + ')' + tableLine + '$'),
					lookbehind: true,
					inside: {
						'punctuation': /\||:?-{3,}:?/
					}
				},
				'table-header-row': {
					pattern: RegExp('^' + tableRow + '$'),
					inside: {
						'table-header': {
							pattern: RegExp(tableCell),
							alias: 'important',
							inside: Prism.languages.markdown
						},
						'punctuation': /\|/
					}
				}
			}
		},
		'code': [
			{
				// Prefixed by 4 spaces or 1 tab and preceded by an empty line
				pattern: /((?:^|\n)[ \t]*\n|(?:^|\r\n?)[ \t]*\r\n?)(?: {4}|\t).+(?:(?:\n|\r\n?)(?: {4}|\t).+)*/,
				lookbehind: true,
				alias: 'keyword'
			},
			{
				// `code`
				// ``code``
				pattern: /``.+?``|`[^`\r\n]+`/,
				alias: 'keyword'
			},
			{
				// ```optional language
				// code block
				// ```
				pattern: /^```[\s\S]*?^```$/m,
				greedy: true,
				inside: {
					'code-block': {
						pattern: /^(```.*(?:\n|\r\n?))[\s\S]+?(?=(?:\n|\r\n?)^```$)/m,
						lookbehind: true
					},
					'code-language': {
						pattern: /^(```).+/,
						lookbehind: true
					},
					'punctuation': /```/
				}
			}
		],
		'title': [
			{
				// title 1
				// =======

				// title 2
				// -------
				pattern: /\S.*(?:\n|\r\n?)(?:==+|--+)(?=[ \t]*$)/m,
				alias: 'important',
				inside: {
					punctuation: /==+$|--+$/
				}
			},
			{
				// # title 1
				// ###### title 6
				pattern: /(^\s*)#+.+/m,
				lookbehind: true,
				alias: 'important',
				inside: {
					punctuation: /^#+|#+$/
				}
			}
		],
		'hr': {
			// ***
			// ---
			// * * *
			// -----------
			pattern: /(^\s*)([*-])(?:[\t ]*\2){2,}(?=\s*$)/m,
			lookbehind: true,
			alias: 'punctuation'
		},
		'list': {
			// * item
			// + item
			// - item
			// 1. item
			pattern: /(^\s*)(?:[*+-]|\d+\.)(?=[\t ].)/m,
			lookbehind: true,
			alias: 'punctuation'
		},
		'url-reference': {
			// [id]: http://example.com "Optional title"
			// [id]: http://example.com 'Optional title'
			// [id]: http://example.com (Optional title)
			// [id]: <http://example.com> "Optional title"
			pattern: /!?\[[^\]]+\]:[\t ]+(?:\S+|<(?:\\.|[^>\\])+>)(?:[\t ]+(?:"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|\((?:\\.|[^)\\])*\)))?/,
			inside: {
				'variable': {
					pattern: /^(!?\[)[^\]]+/,
					lookbehind: true
				},
				'string': /(?:"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|\((?:\\.|[^)\\])*\))$/,
				'punctuation': /^[\[\]!:]|[<>]/
			},
			alias: 'url'
		},
		'bold': {
			// **strong**
			// __strong__

			// allow one nested instance of italic text using the same delimiter
			pattern: createInline(/__(?:(?!_)<inner>|_(?:(?!_)<inner>)+_)+__/.source, true),
			lookbehind: true,
			greedy: true,
			inside: {
				'content': {
					pattern: /(^..)[\s\S]+(?=..$)/,
					lookbehind: true,
					inside: {} // see below
				},
				'punctuation': /\*\*|__/
			}
		},
		'italic': {
			// *em*
			// _em_

			// allow one nested instance of bold text using the same delimiter
			pattern: createInline(/_(?:(?!_)<inner>|__(?:(?!_)<inner>)+__)+_/.source, true),
			lookbehind: true,
			greedy: true,
			inside: {
				'content': {
					pattern: /(^.)[\s\S]+(?=.$)/,
					lookbehind: true,
					inside: {} // see below
				},
				'punctuation': /[*_]/
			}
		},
		'strike': {
			// ~~strike through~~
			// ~strike~
			pattern: createInline(/(~~?)(?:(?!~)<inner>)+?\2/.source, false),
			lookbehind: true,
			greedy: true,
			inside: {
				'content': {
					pattern: /(^~~?)[\s\S]+(?=\1$)/,
					lookbehind: true,
					inside: {} // see below
				},
				'punctuation': /~~?/
			}
		},
		'url': {
			// [example](http://example.com "Optional title")
			// [example][id]
			// [example] [id]
			pattern: createInline(/!?\[(?:(?!\])<inner>)+\](?:\([^\s)]+(?:[\t ]+"(?:\\.|[^"\\])*")?\)| ?\[(?:(?!\])<inner>)+\])/.source, false),
			lookbehind: true,
			greedy: true,
			inside: {
				'variable': {
					pattern: /(\[)[^\]]+(?=\]$)/,
					lookbehind: true
				},
				'content': {
					pattern: /(^!?\[)[^\]]+(?=\])/,
					lookbehind: true,
					inside: {} // see below
				},
				'string': {
					pattern: /"(?:\\.|[^"\\])*"(?=\)$)/
				}
			}
		}
	});

	['url', 'bold', 'italic', 'strike'].forEach(function (token) {
		['url', 'bold', 'italic', 'strike'].forEach(function (inside) {
			if (token !== inside) {
				Prism.languages.markdown[token].inside.content.inside[inside] = Prism.languages.markdown[inside];
			}
		});
	});

	Prism.hooks.add('after-tokenize', function (env) {
		if (env.language !== 'markdown' && env.language !== 'md') {
			return;
		}

		function walkTokens(tokens) {
			if (!tokens || typeof tokens === 'string') {
				return;
			}

			for (var i = 0, l = tokens.length; i < l; i++) {
				var token = tokens[i];

				if (token.type !== 'code') {
					walkTokens(token.content);
					continue;
				}

				/*
				 * Add the correct `language-xxxx` class to this code block. Keep in mind that the `code-language` token
				 * is optional. But the grammar is defined so that there is only one case we have to handle:
				 *
				 * token.content = [
				 *     <span class="punctuation">```</span>,
				 *     <span class="code-language">xxxx</span>,
				 *     '\n', // exactly one new lines (\r or \n or \r\n)
				 *     <span class="code-block">...</span>,
				 *     '\n', // exactly one new lines again
				 *     <span class="punctuation">```</span>
				 * ];
				 */

				var codeLang = token.content[1];
				var codeBlock = token.content[3];

				if (codeLang && codeBlock &&
					codeLang.type === 'code-language' && codeBlock.type === 'code-block' &&
					typeof codeLang.content === 'string') {

					// this might be a language that Prism does not support

					// do some replacements to support C++, C#, and F#
					var lang = codeLang.content.replace(/\b#/g, 'sharp').replace(/\b\+\+/g, 'pp');
					// only use the first word
					lang = (/[a-z][\w-]*/i.exec(lang) || [''])[0].toLowerCase();
					var alias = 'language-' + lang;

					// add alias
					if (!codeBlock.alias) {
						codeBlock.alias = [alias];
					} else if (typeof codeBlock.alias === 'string') {
						codeBlock.alias = [codeBlock.alias, alias];
					} else {
						codeBlock.alias.push(alias);
					}
				}
			}
		}

		walkTokens(env.tokens);
	});

	Prism.hooks.add('wrap', function (env) {
		if (env.type !== 'code-block') {
			return;
		}

		var codeLang = '';
		for (var i = 0, l = env.classes.length; i < l; i++) {
			var cls = env.classes[i];
			var match = /language-(.+)/.exec(cls);
			if (match) {
				codeLang = match[1];
				break;
			}
		}

		var grammar = Prism.languages[codeLang];

		if (!grammar) {
			if (codeLang && codeLang !== 'none' && Prism.plugins.autoloader) {
				var id = 'md-' + new Date().valueOf() + '-' + Math.floor(Math.random() * 1e16);
				env.attributes['id'] = id;

				Prism.plugins.autoloader.loadLanguages(codeLang, function () {
					var ele = document.getElementById(id);
					if (ele) {
						ele.innerHTML = Prism.highlight(ele.textContent, Prism.languages[codeLang], codeLang);
					}
				});
			}
		} else {
			// reverse Prism.util.encode
			var code = env.content.replace(/&lt;/g, '<').replace(/&amp;/g, '&');

			env.content = Prism.highlight(code, grammar, codeLang);
		}
	});

	Prism.languages.md = Prism.languages.markdown;

}(Prism));

/* eslint-disable simple-import-sort/sort */

/**
 * Decorate texts with markdown preview.
 */

const decoratePreview = () => ([node, path]) => {
  const ranges = [];

  if (!Text.isText(node)) {
    return ranges;
  }

  const getLength = token => {
    if (typeof token === 'string') {
      return token.length;
    }

    if (typeof token.content === 'string') {
      return token.content.length;
    }

    return token.content.reduce((l, t) => l + getLength(t), 0);
  };

  const tokens = Prism$1.tokenize(node.text, Prism$1.languages.markdown);
  let start = 0;

  for (const token of tokens) {
    const length = getLength(token);
    const end = start + length;

    if (typeof token !== 'string') {
      ranges.push({
        [token.type]: true,
        anchor: {
          path,
          offset: start
        },
        focus: {
          path,
          offset: end
        }
      });
    }

    start = end;
  }

  return ranges;
};

const renderLeafPreview = () => props => /*#__PURE__*/createElement(PreviewLeaf, Object.assign({}, props));

const PreviewPlugin = () => ({
  decorate: decoratePreview(),
  renderLeaf: renderLeafPreview()
});

const htmlStringToDOMNode = rawHtml => {
  const node = document.createElement('body');
  node.innerHTML = rawHtml.replace(/(\r\n|\n|\r|\t)/gm, '');
  return node;
};

const trimWhitespace = rawHtml => rawHtml.replace(/(\r\n|\n|\r|\t)/gm, ''); // Remove redundant data attributes


const stripSlateDataAttributes = rawHtml => rawHtml.replace(/( data-slate)(-node|-type)="[^"]+"/gm, '').replace(/( data-testid)="[^"]+"/gm, '');
/**
 * Remove all class names that are not starting with `slate-`
 */


const stripClassNames = html => {
  const allClasses = html.split(/(class="[^"]*")/g);
  let filteredHtml = '';
  allClasses.forEach((item, index) => {
    if (index % 2 === 0) {
      return filteredHtml += item;
    }

    const slateClassNames = item.match(/(slate-[^"\s]*)/g);

    if (slateClassNames) {
      filteredHtml += `class="${slateClassNames.join(' ')}"`;
    }
  });
  return filteredHtml;
};

const getNode$1 = (elementProps, plugins) => {
  // If no type provided we wrap children with div tag
  if (!elementProps.element.type) {
    return `<div>${elementProps.children}</div>`;
  }

  let html; // Search for matching plugin based on element type

  plugins.some(plugin => {
    var _a, _b;

    if (!plugin.renderElement) return false;

    if (!((_b = (_a = plugin.deserialize) === null || _a === void 0 ? void 0 : _a.element) === null || _b === void 0 ? void 0 : _b.some(item => item.type === String(elementProps.element.type)))) {
      html = `<div>${elementProps.children}</div>`;
      return false;
    } // Render element using picked plugins renderElement function and ReactDOM


    html = renderToStaticMarkup(plugin.renderElement(elementProps));
    html = stripClassNames(html);
    return true;
  });
  return html;
};

const getLeaf = (leafProps, plugins) => {
  const {
    children
  } = leafProps;
  return plugins.reduce((result, plugin) => {
    if (!plugin.renderLeaf) return result;
    if (plugin.renderLeaf(leafProps) === children) return result;
    const newLeafProps = Object.assign(Object.assign({}, leafProps), {
      children: encodeURIComponent(result)
    });
    let html = decodeURIComponent(renderToStaticMarkup(plugin.renderLeaf(newLeafProps)));
    html = stripClassNames(html);
    return html;
  }, children);
};

const isEncoded = (str = '') => {
  try {
    return str !== decodeURIComponent(str);
  } catch (error) {
    return false;
  }
};
/**
 *
 * @param plugins
 */


const serializeHTMLFromNodes = plugins => nodes => {
  const result = nodes.map(node => {
    if (Text.isText(node)) {
      return getLeaf({
        leaf: node,
        text: node,
        children: isEncoded(node.text) ? node.text : encodeURIComponent(node.text),
        attributes: {
          'data-slate-leaf': true
        }
      }, plugins);
    }

    return getNode$1({
      element: node,
      children: encodeURIComponent(serializeHTMLFromNodes(plugins)(node.children)),
      attributes: {
        'data-slate-node': 'element',
        ref: null
      }
    }, plugins);
  }).join('');
  return stripSlateDataAttributes(trimWhitespace(decodeURIComponent(result)));
};

/**
 * Deserialize HTML to break line.
 */
const deserializeHTMLToBreak = node => {
  if (node.nodeName === 'BR') return '\n';
};

/**
 * Deserialize HTML to Element.
 */

const deserializeHTMLToElement = ({
  plugins,
  el,
  children
}) => {
  let element;
  plugins.some(({
    deserialize: pluginDeserializers
  }) => {
    if (!(pluginDeserializers === null || pluginDeserializers === void 0 ? void 0 : pluginDeserializers.element)) return;
    return pluginDeserializers.element.some(deserializer => {
      const deserialized = deserializer.deserialize(el);
      if (!deserialized) return;
      element = deserialized;
      return true;
    });
  });

  if (element) {
    let descendants = children;

    if (!descendants.length) {
      descendants = [{
        text: ''
      }];
    }

    return jsx('element', element, descendants);
  }
};

/**
 * Deserialize HTML body element to Fragment.
 */

const deserializeHTMLToFragment = ({
  el,
  children
}) => {
  if (el.nodeName === 'BODY') {
    return jsx('fragment', {}, children);
  }
};

/**
 * Deserialize HTML to Descendant[] with marks on Text.
 * Build the leaf from the leaf deserializers of each plugin.
 */

const deserializeHTMLToMarks = ({
  plugins,
  el,
  children
}) => {
  let leaf = {};
  plugins.forEach(({
    deserialize: pluginDeserializers
  }) => {
    if (!(pluginDeserializers === null || pluginDeserializers === void 0 ? void 0 : pluginDeserializers.leaf)) return;
    pluginDeserializers.leaf.forEach(deserializer => {
      const leafPart = deserializer.deserialize(el);
      if (!leafPart) return;
      leaf = Object.assign(Object.assign({}, leaf), leafPart);
    });
  });
  return children.reduce((arr, child) => {
    if (!child) return arr;

    if (Element.isElement(child)) {
      if (Object.keys(leaf).length) {
        mergeDeepToNodes({
          node: child,
          source: leaf,
          query: {
            filter: ([n]) => Text.isText(n)
          }
        });
      }

      arr.push(child);
    } else {
      arr.push(jsx('text', leaf, child));
    }

    return arr;
  }, []);
};

/**
 * Deserialize HTML text node to text.
 */
const deserializeHTMLToText = node => {
  if (node.nodeType === Node.TEXT_NODE) return node.nodeValue === '\n' ? null : node.textContent;
};

/**
 * Deserialize HTML element or child node.
 */

const deserializeHTMLNode = plugins => node => {
  var _a; // text node


  const textNode = deserializeHTMLToText(node);
  if (textNode) return textNode; // if not an element node

  if (node.nodeType !== Node.ELEMENT_NODE) return null;
  const htmlElement = node; // break line

  const breakLine = deserializeHTMLToBreak(node);
  if (breakLine) return breakLine;
  const {
    nodeName
  } = node;
  let parent = node; // blockquote

  if (nodeName === 'PRE' && ((_a = node.childNodes[0]) === null || _a === void 0 ? void 0 : _a.nodeName) === 'CODE') {
    [parent] = node.childNodes;
  }

  const children = Array.from(parent.childNodes).map(deserializeHTMLNode(plugins)).flat(); // body

  const fragment = deserializeHTMLToFragment({
    el: htmlElement,
    children
  });
  if (fragment) return fragment; // element

  const element = deserializeHTMLToElement({
    plugins,
    el: htmlElement,
    children
  });
  if (element) return element; // mark

  return deserializeHTMLToMarks({
    plugins,
    el: htmlElement,
    children
  });
};

/**
 * Deserialize HTML element.
 */

const deserializeHTMLElement = plugins => element => {
  return deserializeHTMLNode(plugins)(element);
};

/**
 * Deserialize HTML element to a valid document fragment.
 */

const deserializeHTMLToDocumentFragment = plugins => element => {
  const fragment = deserializeHTMLElement(plugins)(element);
  return normalizeDescendantsToDocumentFragment(fragment);
};

/**
 * Deserialize HTML to a valid Slate value.
 */

const deserializeHTMLToDocument = plugins => body => {
  const nodes = deserializeHTMLToDocumentFragment(plugins)(body);
  return [{
    children: nodes
  }];
};

/**
 * Enables support for deserializing inserted content from HTML format to Slate format.
 */

const withDeserializeHTML = ({
  plugins = []
} = {}) => editor => {
  const {
    insertData
  } = editor;
  const inlineTypes = plugins.reduce((arr, plugin) => {
    const types = plugin.inlineTypes || [];
    return arr.concat(types);
  }, []);

  editor.insertData = data => {
    const html = data.getData('text/html');

    if (html) {
      const {
        body
      } = new DOMParser().parseFromString(html, 'text/html');
      const fragment = deserializeHTMLToDocumentFragment(plugins)(body);
      const firstNodeType = fragment[0].type; // replace the selected node type by the first block type

      if (firstNodeType && !inlineTypes.includes(firstNodeType)) {
        Transforms.setNodes(editor, {
          type: fragment[0].type
        });
      }

      Transforms.insertFragment(editor, fragment);
      return;
    }

    insertData(data);
  };

  return editor;
};

function filterBreaklines(item) {
  return !item.text;
}

const ELEMENT_H1 = 'h1';
const ELEMENT_H2 = 'h2';
const ELEMENT_H3 = 'h3';
const ELEMENT_H4 = 'h4';
const ELEMENT_H5 = 'h5';
const ELEMENT_H6 = 'h6';
const baseMargin = 4.8;
const baseFontSize = 16;
const DEFAULTS_HEADING = {
  h1: {
    component: StyledElement,
    type: ELEMENT_H1,
    rootProps: {
      className: `slate-${ELEMENT_H1}`,
      as: 'h1',
      styles: {
        root: {
          fontWeight: '400',
          marginTop: 0,
          marginBottom: `${baseMargin * 2.5}px`,
          fontSize: `${baseFontSize * 20 / 11}px`,
          lineHeight: '36px',
          selectors: {
            ':not(:first-child)': {
              marginTop: '30px'
            }
          }
        }
      }
    }
  },
  h2: {
    component: StyledElement,
    type: ELEMENT_H2,
    rootProps: {
      className: `slate-${ELEMENT_H2}`,
      as: 'h2',
      styles: {
        root: {
          fontWeight: '400',
          marginTop: 0,
          marginBottom: `${baseMargin * 1.5}px`,
          fontSize: `${baseFontSize * 16 / 11}px`,
          lineHeight: '28px',
          selectors: {
            ':not(:first-child)': {
              marginTop: '18px'
            }
          }
        }
      }
    }
  },
  h3: {
    component: StyledElement,
    type: ELEMENT_H3,
    rootProps: {
      className: `slate-${ELEMENT_H3}`,
      as: 'h3',
      styles: {
        root: {
          color: '#434343',
          fontWeight: '400',
          marginTop: 0,
          marginBottom: `${baseMargin * 1.25}px`,
          fontSize: `${baseFontSize * 14 / 11}px`,
          selectors: {
            ':not(:first-child)': {
              marginTop: '8px'
            }
          }
        }
      }
    }
  },
  h4: {
    component: StyledElement,
    type: ELEMENT_H4,
    rootProps: {
      className: `slate-${ELEMENT_H4}`,
      as: 'h4',
      styles: {
        root: {
          color: '#666666',
          fontWeight: '400',
          marginTop: 0,
          marginBottom: `${baseMargin}px`,
          fontSize: `${baseFontSize * 12 / 11}px`,
          selectors: {
            ':not(:first-child)': {
              marginTop: '8px'
            }
          }
        }
      }
    }
  },
  h5: {
    component: StyledElement,
    type: ELEMENT_H5,
    rootProps: {
      className: `slate-${ELEMENT_H5}`,
      as: 'h5',
      styles: {
        root: {
          color: '#666666',
          fontWeight: '400',
          marginTop: 0,
          marginBottom: `${baseMargin}px`,
          fontSize: `${baseFontSize}px`,
          selectors: {
            ':not(:first-child)': {
              marginTop: '8px'
            }
          }
        }
      }
    }
  },
  h6: {
    component: StyledElement,
    type: ELEMENT_H6,
    rootProps: {
      className: `slate-${ELEMENT_H6}`,
      as: 'h6',
      styles: {
        root: {
          color: '#666666',
          fontWeight: '400',
          fontStyle: 'italic',
          marginTop: 0,
          marginBottom: `${baseMargin}px`,
          fontSize: `${baseFontSize}px`,
          selectors: {
            ':not(:first-child)': {
              marginTop: '8px'
            }
          }
        }
      }
    }
  },
  levels: 6
};

const getLinkElementStyles = ({
  className
}) => {
  return {
    root: [{
      // Insert css properties
      textDecoration: 'initial',
      color: '#0078d4',
      selectors: {
        ':hover, :visited:hover': {
          color: '#004578',
          textDecoration: 'underline'
        },
        ':visited': {
          color: '#0078d4'
        }
      }
    }, className]
  };
};

const getClassNames$7 = classNamesFunction();
/**
 * LinkElement with no default styles.
 * [Use the `styles` API to add your own styles.](https://github.com/OfficeDev/office-ui-fabric-react/wiki/Component-Styling)
 */

const LinkElementBase = ({
  attributes,
  children,
  element,
  styles,
  className
}) => {
  const classNames = getClassNames$7(styles, {
    className
  });
  return /*#__PURE__*/createElement("a", Object.assign({}, attributes, {
    href: element.url,
    className: classNames.root
  }), children);
};
/**
 * LinkElement
 */

const LinkElement = styled$1(LinkElementBase, getLinkElementStyles, undefined, {
  scope: 'LinkElement'
});

const ELEMENT_LINK = 'a';
const DEFAULTS_LINK = {
  link: {
    component: LinkElement,
    type: ELEMENT_LINK,
    isUrl,
    rootProps: {
      className: 'slate-link'
    }
  }
};

const ELEMENT_PARAGRAPH = 'p';
const DEFAULTS_PARAGRAPH = {
  p: {
    component: StyledElement,
    type: ELEMENT_PARAGRAPH,
    rootProps: {
      className: `slate-${ELEMENT_PARAGRAPH}`,
      as: 'p'
    }
  }
};

const ELEMENT_UL = 'ul';
const ELEMENT_OL = 'ol';
const ELEMENT_LI = 'li';
const DEFAULTS_LIST = Object.assign({
  ul: {
    component: StyledElement,
    type: ELEMENT_UL,
    rootProps: {
      className: 'slate-ul',
      as: 'ul',
      styles: {
        root: {
          paddingInlineStart: '24px',
          marginBlockStart: '0',
          marginBlockEnd: '0'
        }
      }
    }
  },
  ol: {
    component: StyledElement,
    type: ELEMENT_OL,
    rootProps: {
      className: 'slate-ol',
      as: 'ol',
      styles: {
        root: {
          paddingInlineStart: '24px',
          marginBlockStart: '0',
          marginBlockEnd: '0'
        }
      }
    }
  },
  li: {
    component: StyledElement,
    type: ELEMENT_LI,
    rootProps: {
      className: 'slate-li',
      as: 'li'
    }
  }
}, DEFAULTS_PARAGRAPH);

const parseMD = options => content => {
  const {
    p,
    blockquote,
    link,
    ul,
    ol,
    li,
    h1,
    h2,
    h3,
    h4,
    h5,
    h6
  } = setDefaults(options, {
    p: {
      type: ELEMENT_PARAGRAPH
    },
    blockquote: {
      type: ELEMENT_PARAGRAPH
    },
    link: {
      type: ELEMENT_LINK
    },
    ul: {
      type: ELEMENT_UL
    },
    ol: {
      type: ELEMENT_OL
    },
    li: {
      type: ELEMENT_LI
    },
    h1: {
      type: ELEMENT_H1
    },
    h2: {
      type: ELEMENT_H2
    },
    h3: {
      type: ELEMENT_H3
    },
    h4: {
      type: ELEMENT_H4
    },
    h5: {
      type: ELEMENT_H5
    },
    h6: {
      type: ELEMENT_H6
    }
  });
  const tree = unified().use(markdown).use(slate$1, {
    nodeTypes: {
      paragraph: p.type,
      block_quote: blockquote.type,
      link: link.type,
      ul_list: ul.type,
      ol_list: ol.type,
      listItem: li.type,
      heading: {
        1: h1.type,
        2: h2.type,
        3: h3.type,
        4: h4.type,
        5: h5.type,
        6: h6.type
      }
    }
  }).processSync(content);
  return tree.result;
};

/**
 * Enables support for deserializing content
 * from Markdown format to Slate format.
 */

const withDeserializeMd = options => editor => {
  const {
    insertData
  } = editor;

  editor.insertData = data => {
    const content = data.getData('text/plain');

    if (content) {
      const fragment = parseMD(options)(content);
      if (!fragment.length) return;

      if (fragment[0].type) {
        Transforms.setNodes(editor, {
          type: fragment[0].type
        });
      }

      Transforms.insertFragment(editor, fragment);
      return;
    }

    insertData(data);
  };

  return editor;
};

const CLASS_TODO_LIST = 'slate-todo-list';
const CLASS_TODO_LIST_CHECKED = 'slate-todo-list-checked';

const getTodoListElementStyles = ({
  className,
  checked
}) => {
  let rootClassName = className;
  if (checked) rootClassName += ` ${CLASS_TODO_LIST_CHECKED}`;
  return {
    root: [{
      display: 'flex',
      flexDirection: 'row',
      padding: '3px 0'
    }, rootClassName],
    checkboxWrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '24px',
      height: '24px',
      marginRight: '6px'
    },
    checkbox: {
      width: '16px',
      height: '16px',
      margin: '0'
    },
    text: {
      flex: 1,
      opacity: checked ? 0.666 : 1,
      textDecoration: checked ? 'line-through' : 'none',
      selectors: {
        ':focus': {
          outline: 'none'
        }
      }
    }
  };
};

const getClassNames$8 = classNamesFunction();
/**
 * TodoListElement with no default styles.
 * [Use the `styles` API to add your own styles.](https://github.com/OfficeDev/office-ui-fabric-react/wiki/Component-Styling)
 */

const TodoListElementBase = ({
  attributes,
  children,
  element,
  className,
  styles
}) => {
  const editor = useEditor();
  const readOnly = useReadOnly();
  const {
    checked
  } = element;
  const classNames = getClassNames$8(styles, {
    className,
    // Other style props
    checked
  });
  return /*#__PURE__*/createElement("div", Object.assign({}, attributes, {
    className: classNames.root
  }), /*#__PURE__*/createElement("div", {
    contentEditable: false,
    className: classNames.checkboxWrapper
  }, /*#__PURE__*/createElement("input", {
    "data-testid": "TodoListElementCheckbox",
    className: classNames.checkbox,
    type: "checkbox",
    checked: checked,
    onChange: e => {
      const path = ReactEditor.findPath(editor, element);
      Transforms.setNodes(editor, {
        checked: e.target.checked
      }, {
        at: path
      });
    }
  })), /*#__PURE__*/createElement("span", {
    className: classNames.text,
    contentEditable: !readOnly,
    suppressContentEditableWarning: true
  }, children));
};
/**
 * TodoListElement
 */

const TodoListElement = styled$1(TodoListElementBase, getTodoListElementStyles, undefined, {
  scope: 'TodoListElement'
});

const ELEMENT_TODO_LI = 'action_item';
const DEFAULTS_TODO_LIST = {
  todo_li: {
    component: TodoListElement,
    type: ELEMENT_TODO_LI,
    rootProps: {
      className: CLASS_TODO_LIST
    }
  }
};

const deserializeTodoList = options => {
  const {
    todo_li
  } = setDefaults(options, DEFAULTS_TODO_LIST);
  return {
    element: getNodeDeserializer({
      type: todo_li.type,
      node: el => ({
        type: todo_li.type,
        checked: el.classList.contains(CLASS_TODO_LIST_CHECKED)
      }),
      rules: [{
        className: todo_li.rootProps.className
      }]
    })
  };
};

const renderElementTodoList = options => {
  const {
    todo_li
  } = setDefaults(options, DEFAULTS_TODO_LIST);
  return getRenderElement(todo_li);
};

const TodoListPlugin = options => ({
  renderElement: renderElementTodoList(options),
  deserialize: deserializeTodoList(options)
});

const getBlockquoteElementStyles = ({
  className
}) => {
  return {
    root: [{
      // Insert css properties
      borderLeft: '2px solid #ddd',
      padding: '10px 20px 10px 16px',
      color: '#aaa',
      margin: '8px 0'
    }, className]
  };
};

const getClassNames$9 = classNamesFunction();
/**
 * BlockquoteElement with no default styles.
 * [Use the `styles` API to add your own styles.](https://github.com/OfficeDev/office-ui-fabric-react/wiki/Component-Styling)
 */

const BlockquoteElementBase = ({
  attributes,
  children,
  className,
  styles
}) => {
  const classNames = getClassNames$9(styles, {
    className
  });
  return /*#__PURE__*/createElement("blockquote", Object.assign({}, attributes, {
    className: classNames.root
  }), children);
};
/**
 * BlockquoteElement
 */

const BlockquoteElement = styled$1(BlockquoteElementBase, getBlockquoteElementStyles, undefined, {
  scope: 'BlockquoteElement'
});

const ELEMENT_BLOCKQUOTE = 'blockquote';
const DEFAULTS_BLOCKQUOTE = {
  blockquote: {
    component: BlockquoteElement,
    type: ELEMENT_BLOCKQUOTE,
    rootProps: {
      className: 'slate-blockquote',
      as: 'blockquote'
    }
  }
};

const deserializeBlockquote = options => {
  const {
    blockquote
  } = setDefaults(options, DEFAULTS_BLOCKQUOTE);
  return {
    element: getElementDeserializer({
      type: blockquote.type,
      rules: [{
        nodeNames: 'BLOCKQUOTE'
      }]
    })
  };
};

const renderElementBlockquote = options => {
  const {
    blockquote
  } = setDefaults(options, DEFAULTS_BLOCKQUOTE);
  return getRenderElement(blockquote);
};

/**
 * Enables support for block quotes, useful for
 * quotations and passages.
 */

const BlockquotePlugin = options => ({
  renderElement: renderElementBlockquote(options),
  deserialize: deserializeBlockquote(options)
});

const getCodeBlockElementStyles = ({
  className
}) => {
  return {
    root: [{
      // Insert css properties
      fontSize: '16px',
      padding: '12px 16px',
      backgroundColor: 'rgb(247, 246, 243)',
      borderRadius: '3px',
      whiteSpace: 'pre-wrap',
      fontFamily: 'SFMono-Regular, Consolas, Monaco, "Liberation Mono", Menlo, Courier, monospace;',
      tabSize: '2',
      lineHeight: 'normal'
    }, className]
  };
};

const getClassNames$a = classNamesFunction();
/**
 *   CodeBlockElement with no default styles.
 * [Use the `styles` API to add your own styles.](https://github.com/OfficeDev/office-ui-fabric-react/wiki/Component-Styling)
 */

const CodeBlockElementBase = ({
  attributes,
  children,
  className,
  styles
}) => {
  const classNames = getClassNames$a(styles, {
    className
  });
  return /*#__PURE__*/createElement("pre", Object.assign({}, attributes, {
    className: classNames.root
  }), /*#__PURE__*/createElement("code", null, children));
};
/**
 * CodeBlockElement
 */

const CodeBlockElement = styled$1(CodeBlockElementBase, getCodeBlockElementStyles, undefined, {
  scope: 'CodeBlockElement'
});

const MARK_PRISM = 'prism';
const ELEMENT_CODE_BLOCK = 'code_block';
const DEFAULTS_CODE_BLOCK = {
  code_block: {
    component: CodeBlockElement,
    type: ELEMENT_CODE_BLOCK,
    rootProps: {
      className: 'slate-code-block'
    }
  }
}; // `
// javascript:
// abap: ABAP
// arduino: Arduino
// bash: Bash
// basic: BASIC
// c: C
// clojure: Clojure
// coffeescript: CoffeeScript
// cpp: C++
// csharp: C#
// css: CSS
// dart: Dart
// diff: Diff
// docker: Docker
// elixir: Elixir
// elm: Elm
// erlang: Erlang
// flow: Flow
// fortran: Fortran
// fsharp: F#
// gherkin: Gherkin
// glsl: GLSL
// go: Go
// graphql: GraphQL
// groovy: Groovy
// haskell
// less
// livescript
// lua
// makefile
// markup
// matlab
// nix
// objectivec
// ocaml
// pascal
// perl
// prolog
// purebasic
// r
// reason
// scss
// scala
// scheme
// sql
// swift
// vbnet
// verilog
// vhdl
// visual-basic
// wasm
// `;

const CODE_BLOCK_LANGUAGES = {
  antlr4: 'ANTLR4',
  bash: 'Bash',
  c: 'C',
  csharp: 'C#',
  css: 'CSS',
  coffeescript: 'CoffeeScript',
  cmake: 'CMake',
  django: 'Django',
  docker: 'Docker',
  ejs: 'EJS',
  erlang: 'Erlang',
  git: 'Git',
  go: 'Go',
  graphql: 'GraphQL',
  groovy: 'Groovy',
  html: 'HTML',
  java: 'Java',
  javascript: 'JavaScript',
  json: 'JSON',
  kotlin: 'Kotlin',
  latex: 'LaTeX',
  less: 'Less',
  lua: 'Lua',
  makefile: 'Makefile',
  markdown: 'Markdown',
  matlab: 'MATLAB',
  markup: 'Markup',
  objectivec: 'Objective-C',
  perl: 'Perl',
  php: 'PHP',
  powershell: 'PowerShell',
  properties: '.properties',
  protobuf: 'Protocol Buffers',
  python: 'Python',
  r: 'R',
  jsx: 'React JSX',
  tsx: 'React TSX',
  ruby: 'Ruby',
  sass: 'Sass (Sass)',
  scss: 'Sass (Scss)',
  scala: 'Scala',
  scheme: 'Scheme',
  sql: 'SQL',
  shell: 'Shell',
  swift: 'Swift',
  svg: 'SVG',
  typescript: 'TypeScript',
  wasm: 'WebAssembly',
  yaml: 'YAML',
  xml: 'XML'
};

// import 'prismjs/components/prism-antlr4';
const decorateCodeBlock = () => entry => {
  const ranges = [];
  const [node, path] = entry;

  if (node.type === ELEMENT_CODE_BLOCK) {
    const text = Node$1.string(node); // const [parent] = Editor.parent(editor, path);
    // const langName: any = parent.lang || 'markup';

    const langName = 'javascript';
    const lang = languages[langName]; // if (lang) {

    const tokens = tokenize(text, lang);
    let offset = 0;

    for (const element of tokens) {
      if (typeof element === 'string') {
        offset += element.length;
      } else {
        const token = element;
        ranges.push({
          anchor: {
            path,
            offset
          },
          focus: {
            path,
            offset: offset + token.length
          },
          className: `prism-token token ${token.type} `,
          prism: true
        });
        offset += token.length;
      }
    } // }

  }

  return ranges;
};

const deserializeCodeBlock = options => {
  const {
    code_block
  } = setDefaults(options, DEFAULTS_CODE_BLOCK);
  return {
    element: getElementDeserializer({
      type: code_block.type,
      rules: [{
        nodeNames: 'PRE'
      }]
    })
  };
};

const renderElementCodeBlock = options => {
  const {
    code_block
  } = setDefaults(options, DEFAULTS_CODE_BLOCK);
  return getRenderElement(code_block);
};

const renderLeafCodeBlock = () => ({
  leaf,
  children
}) => {
  if (leaf[MARK_PRISM] && !!leaf.text) {
    return /*#__PURE__*/createElement("span", {
      className: leaf === null || leaf === void 0 ? void 0 : leaf.className
    }, children);
  }

  return children;
};

const CodeBlockPlugin = options => ({
  renderElement: renderElementCodeBlock(options),
  renderLeaf: renderLeafCodeBlock(),
  deserialize: deserializeCodeBlock(options),
  decorate: decorateCodeBlock()
});

const deserializeHeading = options => {
  const {
    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    levels
  } = setDefaults(options, DEFAULTS_HEADING);
  let deserializers = getElementDeserializer({
    type: h1.type,
    rules: [{
      nodeNames: 'H1'
    }]
  });
  if (levels >= 2) deserializers = [...deserializers, ...getElementDeserializer({
    type: h2.type,
    rules: [{
      nodeNames: 'H2'
    }]
  })];
  if (levels >= 3) deserializers = [...deserializers, ...getElementDeserializer({
    type: h3.type,
    rules: [{
      nodeNames: 'H3'
    }]
  })];
  if (levels >= 4) deserializers = [...deserializers, ...getElementDeserializer({
    type: h4.type,
    rules: [{
      nodeNames: 'H4'
    }]
  })];
  if (levels >= 5) deserializers = [...deserializers, ...getElementDeserializer({
    type: h5.type,
    rules: [{
      nodeNames: 'H5'
    }]
  })];
  if (levels >= 6) deserializers = [...deserializers, ...getElementDeserializer({
    type: h6.type,
    rules: [{
      nodeNames: 'H6'
    }]
  })];
  return {
    element: deserializers
  };
};

/**
 * Font sizes are relative to the base font size
 * H1 - fs * 20/11
 * H2 - fs * 16/11
 * H3 - fs * 14/11
 * H4 - fs * 12/11
 * H5 - fs * 1
 * H6 - fs * 1
 */

const renderElementHeading = options => props => {
  const {
    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    levels
  } = setDefaults(options, DEFAULTS_HEADING);
  const renderElementsOptions = [];

  const checkRenderElement = (level, optionsValues) => {
    if (levels >= level) renderElementsOptions.push(optionsValues);
  };

  checkRenderElement(1, h1);
  checkRenderElement(2, h2);
  checkRenderElement(3, h3);
  checkRenderElement(4, h4);
  checkRenderElement(5, h5);
  checkRenderElement(6, h6);
  return getRenderElements(renderElementsOptions)(props);
};

/**
 * Enables support for headings with configurable levels
 * (from 1 to 6).
 */

const HeadingPlugin = options => ({
  renderElement: renderElementHeading(options),
  deserialize: deserializeHeading(options)
});

const deserializeParagraph = options => {
  const {
    p
  } = setDefaults(options, DEFAULTS_PARAGRAPH);
  return {
    element: getElementDeserializer({
      type: p.type,
      rules: [{
        nodeNames: 'P'
      }]
    })
  };
};

const renderElementParagraph = options => {
  const {
    p
  } = setDefaults(options, DEFAULTS_PARAGRAPH);
  return getRenderElement(p);
};

/**
 * Enables support for paragraphs.
 */

const ParagraphPlugin = options => ({
  renderElement: renderElementParagraph(options),
  deserialize: deserializeParagraph(options)
});

const BasicElementPlugins = options => [BlockquotePlugin(options), CodeBlockPlugin(options), HeadingPlugin(options), ParagraphPlugin(options)];

const getImageElementStyles = ({
  className,
  focused,
  selected
}) => {
  return {
    root: [{// Insert css properties
    }, className],
    img: {
      display: 'block',
      maxWidth: '100%',
      maxHeight: '20em',
      padding: '10px 0',
      boxShadow: focused && selected ? '0 0 0 3px #B4D5FF' : 'none'
    }
  };
};

const getClassNames$b = classNamesFunction();
/**
 * ImageElement with no default styles.
 * [Use the `styles` API to add your own styles.](https://github.com/OfficeDev/office-ui-fabric-react/wiki/Component-Styling)
 */

const ImageElementBase = ({
  attributes,
  children,
  element,
  className,
  styles
}) => {
  const {
    url
  } = element;
  const focused = useFocused();
  const selected = useSelected();
  const classNames = getClassNames$b(styles, {
    className,
    // Other style props
    focused,
    selected
  });
  return /*#__PURE__*/createElement("div", Object.assign({}, attributes, {
    className: classNames.root
  }), /*#__PURE__*/createElement("div", {
    contentEditable: false
  }, /*#__PURE__*/createElement("img", {
    "data-testid": "ImageElementImage",
    className: classNames.img,
    src: url,
    alt: ""
  })), children);
};
/**
 * ImageElement
 */

const ImageElement = styled$1(ImageElementBase, getImageElementStyles, undefined, {
  scope: 'ImageElement'
});

const ELEMENT_IMAGE = 'img';
const DEFAULTS_IMAGE = {
  img: {
    component: ImageElement,
    type: ELEMENT_IMAGE,
    rootProps: {
      className: 'slate-img'
    }
  }
};

const insertImage = (editor, url, options) => {
  const {
    img
  } = setDefaults(options, DEFAULTS_IMAGE);
  const text = {
    text: ''
  };
  const image = {
    type: img.type,
    url,
    children: [text]
  };
  Transforms.insertNodes(editor, image);
};

const ToolbarImage = _a => {
  var {
    img
  } = _a,
      props = __rest(_a, ["img"]);

  const editor = useEditor();
  return /*#__PURE__*/createElement(ToolbarButton, Object.assign({
    onMouseDown: event => {
      event.preventDefault();
      const url = window.prompt('Enter the URL of the image:');
      if (!url) return;
      insertImage(editor, url, {
        img
      });
    }
  }, props));
};

const deserializeImage = options => {
  const {
    img
  } = setDefaults(options, DEFAULTS_IMAGE);
  return {
    element: getNodeDeserializer({
      type: img.type,
      node: el => ({
        type: img.type,
        url: el.getAttribute('src')
      }),
      rules: [{
        nodeNames: 'IMG'
      }]
    })
  };
};

const renderElementImage = options => {
  const {
    img
  } = setDefaults(options, DEFAULTS_IMAGE);
  return getRenderElement(img);
};

/**
 * Enables support for images.
 */

const ImagePlugin = options => {
  const {
    img
  } = setDefaults(options, DEFAULTS_IMAGE);
  return {
    renderElement: renderElementImage(options),
    deserialize: deserializeImage(options),
    voidTypes: [img.type]
  };
};

const isImageUrl = url => {
  if (!isUrl(url)) return false;
  const ext = new URL(url).pathname.split('.').pop();
  return imageExtensions.includes(ext);
};

const onImageLoad = (editor, reader) => () => {
  const url = reader.result;
  if (url) insertImage(editor, url);
};

/**
 * Allows for pasting images from clipboard.
 * Not yet: dragging and dropping images, selecting them through a file system dialog.
 * @param img.type
 */

const withImageUpload = options => editor => {
  const {
    insertData
  } = editor;

  editor.insertData = data => {
    const text = data.getData('text/plain');
    const {
      files
    } = data;

    if (files && files.length > 0) {
      for (const file of files) {
        const reader = new FileReader();
        const [mime] = file.type.split('/');

        if (mime === 'image') {
          reader.addEventListener('load', onImageLoad(editor, reader));
          reader.readAsDataURL(file);
        }
      }
    } else if (isImageUrl(text)) {
      insertImage(editor, text, options);
    } else {
      insertData(data);
    }
  };

  return editor;
};

/**
 * Wrap selected nodes with a link and collapse at the end.
 */

const wrapLink = (editor, url, options) => {
  const {
    at,
    link
  } = setDefaults(options, DEFAULTS_LINK);
  wrapNodes(editor, {
    type: link.type,
    url,
    children: []
  }, {
    at,
    split: true
  });
};

/**
 * Unwrap link at a location (default: selection).
 * Then, the focus of the location is set to selection focus.
 * Then, wrap the link at the location.
 */

const upsertLinkAtSelection = (editor, url, options) => {
  if (!editor.selection) return;
  const {
    link,
    wrap
  } = setDefaults(options, DEFAULTS_LINK);

  if (!wrap && isCollapsed(editor.selection)) {
    return Transforms.insertNodes(editor, {
      type: link.type,
      url,
      children: [{
        text: url
      }]
    });
  }

  unwrapNodesByType(editor, link.type, {
    at: editor.selection
  });
  wrapLink(editor, url, {
    link,
    at: editor.selection
  });
  Transforms.collapse(editor, {
    edge: 'end'
  });
};

const ToolbarLink = _a => {
  var {
    link
  } = _a,
      props = __rest(_a, ["link"]);

  const options = setDefaults({
    link
  }, DEFAULTS_LINK);
  const editor = useSlate();
  return /*#__PURE__*/createElement(ToolbarButton, Object.assign({
    active: isNodeTypeIn(editor, options.link.type),
    onMouseDown: event => {
      event.preventDefault();
      const url = window.prompt('Enter the URL of the link:');
      if (!url) return;
      upsertLinkAtSelection(editor, url, options);
    }
  }, props));
};

const deserializeLink = options => {
  const {
    link
  } = setDefaults(options, DEFAULTS_LINK);
  return {
    element: getNodeDeserializer({
      type: link.type,
      node: el => ({
        type: link.type,
        url: el.getAttribute('href')
      }),
      rules: [{
        nodeNames: 'A'
      }]
    })
  };
};

const renderElementLink = options => {
  const {
    link
  } = setDefaults(options, DEFAULTS_LINK);
  return getRenderElement(link);
};

/**
 * Enables support for hyperlinks.
 */

const LinkPlugin = options => {
  const {
    link
  } = setDefaults(options, DEFAULTS_LINK);
  return {
    renderElement: renderElementLink(options),
    deserialize: deserializeLink(options),
    inlineTypes: [link.type]
  };
};

/**
 * Remove nodes with empty text.
 */

const withRemoveEmptyNodes = options => editor => {
  const types = castArray_1(options.type);
  const {
    normalizeNode
  } = editor;

  editor.normalizeNode = ([node, path]) => {
    if (node.type && types.includes(node.type) && Node$1.string(node) === '') {
      Transforms.removeNodes(editor, {
        at: path
      });
    }

    normalizeNode([node, path]);
  };

  return editor;
};

const upsertLink = (editor, url, _a) => {
  var {
    at
  } = _a,
      options = __rest(_a, ["at"]);

  const {
    link
  } = setDefaults(options, DEFAULTS_LINK);
  unwrapNodesByType(editor, link.type, {
    at
  });
  const newSelection = editor.selection;
  wrapLink(editor, url, {
    link,
    at: Object.assign(Object.assign({}, at), {
      focus: newSelection.focus
    })
  });
};
/**
 * Insert space after a url to wrap a link.
 * Lookup from the block start to the cursor to check if there is an url.
 * If not found, lookup before the cursor for a space character to check the url.
 *
 * On insert data:
 * Paste a string inside a link element will edit its children text but not its url.
 *
 */


const withLink = options => editor => {
  const {
    link,
    isUrl: isUrl$1
  } = setDefaults(options, Object.assign(Object.assign({}, DEFAULTS_LINK), {
    isUrl: isUrl
  }));
  const {
    insertData,
    insertText
  } = editor;

  editor.insertText = text => {
    const DEFAULT_RANGE_BEFORE_OPTIONS = {
      matchString: ' ',
      skipInvalid: true,
      afterMatch: true,
      multiPaths: true
    };

    if (text === ' ' && isCollapsed(editor.selection)) {
      const selection = editor.selection;
      const rangeFromBlockStart = getRangeFromBlockStart(editor);
      const textFromBlockStart = getText(editor, rangeFromBlockStart);

      if (rangeFromBlockStart && isUrl$1(textFromBlockStart)) {
        upsertLink(editor, textFromBlockStart, {
          at: rangeFromBlockStart,
          link
        });
        return insertText(text);
      }

      const rangeOptions = Object.assign(Object.assign({}, DEFAULT_RANGE_BEFORE_OPTIONS), get_1(options, 'rangeBeforeOptions', {}));
      const beforeWordRange = getRangeBefore(editor, selection, rangeOptions);

      if (beforeWordRange) {
        const beforeWordText = getText(editor, beforeWordRange);

        if (isUrl$1(beforeWordText)) {
          upsertLink(editor, beforeWordText, {
            at: beforeWordRange,
            link
          });
        }
      }
    }

    insertText(text);
  };

  editor.insertData = data => {
    const text = data.getData('text/plain');

    if (text) {
      if (isNodeTypeIn(editor, link.type)) {
        return insertText(text);
      }

      if (isUrl$1(text)) {
        return upsertLinkAtSelection(editor, text, {
          link
        });
      }
    }

    insertData(data);
  };

  editor = withRemoveEmptyNodes({
    type: link.type
  })(editor);
  return editor;
};

const unwrapList = (editor, options) => {
  const {
    li,
    ul,
    ol
  } = setDefaults(options, DEFAULTS_LIST);
  unwrapNodesByType(editor, li.type);
  unwrapNodesByType(editor, [ul.type, ol.type], {
    split: true
  });
};

const toggleList = (editor, _a) => {
  var {
    typeList
  } = _a,
      options = __rest(_a, ["typeList"]);

  if (!editor.selection) return;
  const {
    p,
    li
  } = setDefaults(options, DEFAULTS_LIST);
  const isActive = isNodeTypeIn(editor, typeList);
  unwrapList(editor, options);
  Transforms.setNodes(editor, {
    type: p.type
  });

  if (!isActive) {
    const list = {
      type: typeList,
      children: []
    };
    wrapNodes(editor, list);
    const nodes = [...getNodesByType(editor, p.type)];
    const listItem = {
      type: li.type,
      children: []
    };

    for (const [, path] of nodes) {
      Transforms.wrapNodes(editor, listItem, {
        at: path
      });
    }
  }
};

const ToolbarList = _a => {
  var {
    typeList = ELEMENT_UL
  } = _a,
      props = __rest(_a, ["typeList"]);

  const editor = useSlate();
  return /*#__PURE__*/createElement(ToolbarElement, Object.assign({
    type: typeList,
    onMouseDown: getPreventDefaultHandler(toggleList, editor, Object.assign(Object.assign({}, props), {
      typeList
    }))
  }, props));
};

const deserializeList = options => {
  const {
    li,
    ul,
    ol
  } = setDefaults(options, DEFAULTS_LIST);
  return {
    element: [...getElementDeserializer({
      type: ul.type,
      rules: [{
        nodeNames: 'UL'
      }]
    }), ...getElementDeserializer({
      type: ol.type,
      rules: [{
        nodeNames: 'OL'
      }]
    }), ...getElementDeserializer({
      type: li.type,
      rules: [{
        nodeNames: 'LI'
      }]
    })]
  };
};

/**
 * Is the selection in li>p.
 * If true, return the node entries.
 */

const isSelectionInListItem = (editor, options) => {
  const {
    li
  } = setDefaults(options, DEFAULTS_LIST);

  if (editor.selection && isNodeTypeIn(editor, li.type) && !isRangeAtRoot(editor.selection)) {
    const [, paragraphPath] = Editor.parent(editor, editor.selection);
    const [listItemNode, listItemPath] = getAboveByType(editor, li.type) || Editor.parent(editor, paragraphPath);
    if (listItemNode.type !== li.type) return;
    const [listNode, listPath] = Editor.parent(editor, listItemPath);
    return {
      listNode,
      listPath,
      listItemNode,
      listItemPath
    };
  }
};

const isList = options => n => {
  const {
    ul,
    ol
  } = setDefaults(options, DEFAULTS_LIST);
  return [ol.type, ul.type].includes(n.type);
};

const moveListItemDown = (editor, listNode, listItemPath, options) => {
  // Previous sibling is the new parent
  const previousSiblingItem = Editor.node(editor, Path.previous(listItemPath));

  if (previousSiblingItem) {
    const [previousNode, previousPath] = previousSiblingItem;
    const sublist = previousNode.children.find(isList(options));
    const newPath = previousPath.concat(sublist ? [1, sublist.children.length] : [1]);

    if (!sublist) {
      // Create new sublist
      Transforms.wrapNodes(editor, {
        type: listNode.type,
        children: []
      }, {
        at: listItemPath
      });
    } // Move the current item to the sublist


    Transforms.moveNodes(editor, {
      at: listItemPath,
      to: newPath
    });
  }
};

/**
 * Move a list item next to its parent.
 * The parent should be a list item.
 */

const moveListItemUp = (editor, listNode, listPath, listItemPath, options) => {
  const {
    li
  } = setDefaults(options, DEFAULTS_LIST);
  const [listParentNode, listParentPath] = Editor.parent(editor, listPath);
  if (listParentNode.type !== li.type) return;
  const newListItemPath = Path.next(listParentPath); // Move item one level up

  Transforms.moveNodes(editor, {
    at: listItemPath,
    to: newListItemPath
  });
  /**
   * Move the next siblings to a new list
   */

  const listItemIdx = listItemPath[listItemPath.length - 1];
  const siblingPath = [...listItemPath];
  const newListPath = newListItemPath.concat(1);
  let siblingFound = false;
  let newSiblingIdx = 0;
  listNode.children.forEach((n, idx) => {
    if (listItemIdx < idx) {
      if (!siblingFound) {
        siblingFound = true;
        Transforms.insertNodes(editor, {
          type: listNode.type,
          children: []
        }, {
          at: newListPath
        });
      }

      siblingPath[siblingPath.length - 1] = listItemIdx;
      const newSiblingsPath = newListPath.concat(newSiblingIdx);
      newSiblingIdx++;
      Transforms.moveNodes(editor, {
        at: siblingPath,
        to: newSiblingsPath
      });
    }
  }); // Remove sublist if it was the first list item

  if (!listItemIdx) {
    Transforms.removeNodes(editor, {
      at: listPath
    });
  }

  return true;
};

const onKeyDownList = options => (e, editor) => {
  let moved = false;

  if (e.key === 'Tab') {
    const res = isSelectionInListItem(editor, options);
    if (!res) return;
    const {
      listNode,
      listPath,
      listItemPath
    } = res;
    e.preventDefault(); // move up with shift+tab

    const shiftTab = e.shiftKey;

    if (shiftTab) {
      moved = moveListItemUp(editor, listNode, listPath, listItemPath, options);
      if (moved) e.preventDefault();
    } // move down with tab


    const tab = !e.shiftKey;

    if (tab && !isFirstChild(listItemPath)) {
      moveListItemDown(editor, listNode, listItemPath, options);
    }
  }
};

const renderElementList = options => {
  const {
    ul,
    ol,
    li
  } = setDefaults(options, DEFAULTS_LIST);
  return getRenderElements([ul, ol, li]);
};

/**
 * Enables support for bulleted, numbered and to-do lists.
 */

const ListPlugin = options => ({
  renderElement: renderElementList(options),
  deserialize: deserializeList(options),
  onKeyDown: onKeyDownList(options)
});

/**
 * Insert list item if selection in li>p.
 */

const insertListItem = (editor, options) => {
  const {
    p,
    li
  } = setDefaults(options, DEFAULTS_LIST);

  if (editor.selection && !isRangeAtRoot(editor.selection)) {
    const paragraphEntry = getAboveByType(editor, p.type);
    if (!paragraphEntry) return;
    const [, paragraphPath] = paragraphEntry;
    const [listItemNode, listItemPath] = Editor.parent(editor, paragraphPath);
    if (listItemNode.type !== li.type) return;

    if (!Range.isCollapsed(editor.selection)) {
      Transforms.delete(editor);
    }

    const isStart = Editor.isStart(editor, editor.selection.focus, paragraphPath);
    const isEnd = isBlockTextEmptyAfterSelection(editor);
    const nextParagraphPath = Path.next(paragraphPath);
    const nextListItemPath = Path.next(listItemPath);
    /**
     * If start, insert a list item before
     */

    if (isStart) {
      Transforms.insertNodes(editor, {
        type: li.type,
        children: [{
          type: p.type,
          children: [{
            text: ''
          }]
        }]
      }, {
        at: listItemPath
      });
      return true;
    }
    /**
     * If not end, split nodes, wrap a list item on the new paragraph and move it to the next list item
     */


    if (!isEnd) {
      Transforms.splitNodes(editor, {
        at: editor.selection
      });
      Transforms.wrapNodes(editor, {
        type: li.type,
        children: []
      }, {
        at: nextParagraphPath
      });
      Transforms.moveNodes(editor, {
        at: nextParagraphPath,
        to: nextListItemPath
      });
    } else {
      /**
       * If end, insert a list item after and select it
       */
      Transforms.insertNodes(editor, {
        type: li.type,
        children: [{
          type: p.type,
          children: [{
            text: ''
          }]
        }]
      }, {
        at: nextListItemPath
      });
      Transforms.select(editor, nextListItemPath);
    }
    /**
     * If there is a list in the list item, move it to the next list item
     */


    if (listItemNode.children.length > 1) {
      Transforms.moveNodes(editor, {
        at: nextParagraphPath,
        to: nextListItemPath.concat(1)
      });
    }

    return true;
  }
};

const ListHotkey = {
  TAB: 'Tab',
  ENTER: 'Enter',
  DELETE_BACKWARD: 'Backspace'
};

const onKeyDownResetBlockType = ({
  rules
}) => (event, editor) => {
  let reset;

  if (editor.selection && isCollapsed(editor.selection)) {
    rules.forEach(({
      types,
      defaultType,
      hotkey,
      predicate,
      onReset
    }) => {
      if (!event || hotkey && isHotkey(hotkey, event)) {
        if (predicate(editor) && isNodeTypeIn(editor, types)) {
          event === null || event === void 0 ? void 0 : event.preventDefault();
          Transforms.setNodes(editor, {
            type: defaultType
          });
          onReset === null || onReset === void 0 ? void 0 : onReset(editor);
          reset = true;
        }
      }
    });
  }

  return reset;
};

const withList = options => editor => {
  const {
    p,
    li
  } = setDefaults(options, DEFAULTS_LIST);
  const {
    insertBreak,
    deleteBackward
  } = editor;
  const resetBlockTypesListRule = {
    types: [li.type],
    defaultType: p.type,
    onReset: _editor => unwrapList(_editor, options)
  };

  editor.insertBreak = () => {
    const res = isSelectionInListItem(editor, options);
    let moved;

    if (res) {
      const {
        listItemNode,
        listItemPath
      } = res;

      if (listItemNode.children.length > 1) {
        return Transforms.insertNodes(editor, {
          type: li.type,
          children: [{
            type: p.type,
            children: [{
              text: ''
            }]
          }]
        }, {
          at: Path.next(listItemPath),
          select: true
        });
      }
    }

    if (res && isBlockAboveEmpty(editor)) {
      const {
        listNode,
        listPath,
        listItemPath
      } = res;
      moved = moveListItemUp(editor, listNode, listPath, listItemPath, options);
      if (moved) return;
    }

    const didReset = onKeyDownResetBlockType({
      rules: [Object.assign(Object.assign({}, resetBlockTypesListRule), {
        predicate: () => !moved && isBlockAboveEmpty(editor)
      })]
    })(null, editor);
    if (didReset) return;
    /**
     * Add a new list item if selection is in a LIST_ITEM > p.type.
     */

    if (!moved) {
      const inserted = insertListItem(editor, options);
      if (inserted) return;
    }

    insertBreak();
  };

  editor.deleteBackward = unit => {
    const res = isSelectionInListItem(editor, options);
    let moved;

    if (res && isSelectionAtBlockStart(editor)) {
      const {
        listNode,
        listPath,
        listItemPath
      } = res;
      moved = moveListItemUp(editor, listNode, listPath, listItemPath, options);
      if (moved) return;
    }

    if (res) {
      const {
        listItemNode
      } = res;

      if (listItemNode.children.length > 1 && Range.isCollapsed(editor.selection)) {
        return deleteBackward(unit);
      }
    }

    const didReset = onKeyDownResetBlockType({
      rules: [Object.assign(Object.assign({}, resetBlockTypesListRule), {
        predicate: () => !moved && isSelectionAtBlockStart(editor)
      })]
    })(null, editor);
    if (didReset) return;
    deleteBackward(unit);
  };

  return editor;
};

const getMentionElementStyles = ({
  className,
  focused,
  selected
}) => {
  return {
    root: [{
      // Insert css properties
      padding: '3px 3px 2px',
      margin: '0 1px',
      verticalAlign: 'baseline',
      display: 'inline-block',
      borderRadius: '4px',
      backgroundColor: '#eee',
      fontSize: '0.9em',
      boxShadow: selected && focused ? '0 0 0 2px #B4D5FF' : 'none'
    }, className]
  };
};

const getClassNames$c = classNamesFunction();
/**
 *   MentionElement with no default styles.
 * [Use the `styles` API to add your own styles.](https://github.com/OfficeDev/office-ui-fabric-react/wiki/Component-Styling)
 */

const MentionElementBase = ({
  attributes,
  children,
  element,
  prefix,
  className,
  styles,
  as: Tag = 'span',
  onClick
}) => {
  const selected = useSelected();
  const focused = useFocused();
  const classNames = getClassNames$c(styles, {
    className,
    // Other style props
    selected,
    focused
  });
  return /*#__PURE__*/createElement(Tag, Object.assign({}, attributes, {
    "data-slate-value": element.value,
    className: classNames.root,
    contentEditable: false,
    onClick: getHandler(onClick, {
      value: element.value
    })
  }), prefix, element.value, children);
};
/**
 * MentionElement
 */

const MentionElement = styled$1(MentionElementBase, getMentionElementStyles, undefined, {
  scope: 'MentionElement'
});

const classNames$5 = {
  root: 'slate-MentionSelect',
  mentionItem: 'slate-MentionSelect-mentionItem',
  mentionItemSelected: 'slate-MentionSelect-mentionItemSelected'
};
const getMentionSelectStyles = ({
  className
} = {}) => {
  const mentionItem = [classNames$5.mentionItem, {
    padding: '1px 3px',
    borderRadius: '3px',
    background: 'transparent',
    cursor: 'pointer'
  }];
  const mentionItemSelected = [classNames$5.mentionItemSelected, ...mentionItem, {
    background: '#B4D5FF'
  }];
  return {
    root: [classNames$5.root, {
      top: '-9999px',
      left: '-9999px',
      position: 'absolute',
      zIndex: 1,
      padding: '3px',
      background: 'white',
      borderRadius: '4px',
      boxShadow: '0 1px 5px rgba(0,0,0,.2)'
    }, className],
    mentionItem,
    mentionItemSelected
  };
};

const getClassNames$d = classNamesFunction();
const MentionSelectBase = _a => {
  var {
    className,
    styles,
    at,
    options,
    valueIndex,
    onClickMention
  } = _a,
      props = __rest(_a, ["className", "styles", "at", "options", "valueIndex", "onClickMention"]);

  const classNames = getClassNames$d(styles, {
    className
  });
  const ref = useRef();
  const editor = useSlate();
  useEffect(() => {
    if (at && options.length > 0) {
      const el = ref.current;
      const domRange = ReactEditor.toDOMRange(editor, at);
      const rect = domRange.getBoundingClientRect();

      if (el) {
        el.style.top = `${rect.top + window.pageYOffset + 24}px`;
        el.style.left = `${rect.left + window.pageXOffset}px`;
      }
    }
  }, [options.length, editor, at]);

  if (!at || !options.length) {
    return null;
  }

  return /*#__PURE__*/createElement(PortalBody, null, /*#__PURE__*/createElement("div", Object.assign({
    ref: ref,
    className: classNames.root
  }, props), options.map((option, i) => /*#__PURE__*/createElement("div", {
    key: `${i}${option.value}`,
    className: i === valueIndex ? classNames.mentionItemSelected : classNames.mentionItem,
    onMouseDown: getPreventDefaultHandler(onClickMention, editor, option)
  }, option.value))));
};
const MentionSelect = styled$1(MentionSelectBase, getMentionSelectStyles, undefined, {
  scope: 'MentionSelect'
});

const ELEMENT_MENTION = 'mention';
const DEFAULTS_MENTION = {
  mention: {
    component: MentionElement,
    type: ELEMENT_MENTION,
    rootProps: {
      className: 'slate-mention',
      prefix: '@'
    }
  }
};

const deserializeMention = options => {
  const {
    mention
  } = setDefaults(options, DEFAULTS_MENTION);
  return {
    element: getNodeDeserializer({
      type: mention.type,
      node: el => ({
        type: mention.type,
        value: el.getAttribute('data-slate-value')
      }),
      rules: [{
        className: mention.rootProps.className
      }]
    })
  };
};

const renderElementMention = options => {
  const {
    mention
  } = setDefaults(options, DEFAULTS_MENTION);
  return getRenderElement(mention);
};

/**
 * Enables support for autocompleting @mentions and #tags.
 * When typing a configurable marker, such as @ or #, a select
 * component appears with autocompleted suggestions.
 */

const MentionPlugin = options => {
  const {
    mention
  } = setDefaults(options, DEFAULTS_MENTION);
  return {
    renderElement: renderElementMention(options),
    deserialize: deserializeMention(options),
    inlineTypes: [mention.type],
    voidTypes: [mention.type]
  };
};

const insertMention = (editor, mentionable, options) => {
  const {
    mention
  } = setDefaults(options, DEFAULTS_MENTION);
  const mentionNode = Object.assign({
    type: mention.type,
    children: [{
      text: ''
    }]
  }, mentionable);
  Transforms.insertNodes(editor, mentionNode);
  Transforms.move(editor);
};

/**
 * Get next index from 0 to max.
 * If index is max, get to 0.
 */
const getNextIndex = (i, max) => i >= max ? 0 : i + 1;

/**
 * Get previous index from 0 to max.
 * If index is 0, get to max.
 */
const getPreviousIndex = (i, max) => i <= 0 ? max : i - 1;

const useMention = (mentionables = [], _a = {}) => {
  var {
    maxSuggestions = 10,
    trigger = '@'
  } = _a,
      options = __rest(_a, ["maxSuggestions", "trigger"]);

  const [targetRange, setTargetRange] = useState(null);
  const [valueIndex, setValueIndex] = useState(0);
  const [search, setSearch] = useState('');
  const values = mentionables.filter(c => c.value.toLowerCase().includes(search.toLowerCase())).slice(0, maxSuggestions);
  const onAddMention = useCallback((editor, data) => {
    if (targetRange !== null) {
      Transforms.select(editor, targetRange);
      insertMention(editor, data, options);
      return setTargetRange(null);
    }
  }, [options, targetRange]);
  const onKeyDownMention = useCallback((e, editor) => {
    if (targetRange) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        return setValueIndex(getNextIndex(valueIndex, values.length - 1));
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        return setValueIndex(getPreviousIndex(valueIndex, values.length - 1));
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        return setTargetRange(null);
      }

      if (['Tab', 'Enter'].includes(e.key)) {
        e.preventDefault();
        onAddMention(editor, values[valueIndex]);
        return false;
      }
    }
  }, [values, valueIndex, setValueIndex, targetRange, setTargetRange, onAddMention]);
  const onChangeMention = useCallback(editor => {
    const {
      selection
    } = editor;

    if (selection && isCollapsed(selection)) {
      const cursor = Range.start(selection);
      const {
        range,
        match: beforeMatch
      } = isWordAfterTrigger(editor, {
        at: cursor,
        trigger
      });

      if (beforeMatch && isPointAtWordEnd(editor, {
        at: cursor
      })) {
        setTargetRange(range);
        const [, word] = beforeMatch;
        setSearch(word);
        setValueIndex(0);
        return;
      }
    }

    setTargetRange(null);
  }, [setTargetRange, setSearch, setValueIndex, trigger]);
  return {
    search,
    index: valueIndex,
    target: targetRange,
    values,
    onChangeMention,
    onKeyDownMention,
    onAddMention
  };
};

const getTableElementStyles = ({
  className
}) => {
  return {
    root: [{
      // Insert css properties
      margin: '10px 0',
      borderCollapse: 'collapse',
      width: '100%'
    }, className]
  };
};

const getClassNames$e = classNamesFunction();
/**
 * TableElement with no default styles.
 * [Use the `styles` API to add your own styles.](https://github.com/OfficeDev/office-ui-fabric-react/wiki/Component-Styling)
 */

const TableElementBase = ({
  attributes,
  children,
  className,
  styles
}) => {
  const classNames = getClassNames$e(styles, {
    className
  });
  return /*#__PURE__*/createElement("table", Object.assign({}, attributes, {
    className: classNames.root
  }), /*#__PURE__*/createElement("tbody", null, children));
};
/**
 * TableElement
 */

const TableElement = styled$1(TableElementBase, getTableElementStyles, undefined, {
  scope: 'TableElement'
});

const ELEMENT_TABLE = 'table';
const ELEMENT_TH = 'th';
const ELEMENT_TR = 'tr';
const ELEMENT_TD = 'td';
const DEFAULTS_TABLE = {
  table: {
    component: TableElement,
    type: ELEMENT_TABLE,
    rootProps: {
      className: 'slate-table',
      as: 'table'
    }
  },
  tr: {
    component: StyledElement,
    type: ELEMENT_TR,
    rootProps: {
      className: 'slate-tr',
      as: 'tr'
    }
  },
  th: {
    component: StyledElement,
    type: ELEMENT_TH,
    rootProps: {
      className: 'slate-th',
      as: 'th',
      styles: {
        root: {
          backgroundColor: 'rgb(244, 245, 247)',
          border: '1px solid rgb(193, 199, 208)',
          padding: '8px',
          minWidth: '48px',
          textAlign: 'left',
          selectors: {
            '> *': {
              margin: 0
            }
          }
        }
      }
    }
  },
  td: {
    component: StyledElement,
    type: ELEMENT_TD,
    rootProps: {
      className: 'slate-td',
      as: 'td',
      styles: {
        root: {
          backgroundColor: 'rgb(255, 255, 255)',
          border: '1px solid rgb(193, 199, 208)',
          padding: '8px',
          minWidth: '48px',
          selectors: {
            '> *': {
              margin: 0
            }
          }
        }
      }
    }
  }
};

const ToolbarTable = _a => {
  var {
    transform
  } = _a,
      props = __rest(_a, ["transform"]);

  const {
    table
  } = setDefaults(props, DEFAULTS_TABLE);
  const editor = useSlate();
  return /*#__PURE__*/createElement(ToolbarButton, Object.assign({
    active: isNodeTypeIn(editor, table.type),
    onMouseDown: getPreventDefaultHandler(transform, editor, props)
  }, props));
};

const deserializeTable = options => {
  const {
    table,
    td,
    th,
    tr
  } = setDefaults(options, DEFAULTS_TABLE);
  return {
    element: [...getElementDeserializer({
      type: table.type,
      rules: [{
        nodeNames: 'TABLE'
      }]
    }), ...getElementDeserializer({
      type: tr.type,
      rules: [{
        nodeNames: 'TR'
      }]
    }), ...getElementDeserializer({
      type: td.type,
      rules: [{
        nodeNames: 'TD'
      }]
    }), ...getElementDeserializer({
      type: th.type,
      rules: [{
        nodeNames: 'TH'
      }]
    })]
  };
};

const renderElementTable = options => {
  const {
    table,
    td,
    th,
    tr
  } = setDefaults(options, DEFAULTS_TABLE);
  return getRenderElements([table, th, tr, td]);
};

/**
 * Enables support for tables.
 */

const TablePlugin = options => ({
  renderElement: renderElementTable(options),
  deserialize: deserializeTable(options)
});

const getEmptyCellNode = options => {
  const {
    th,
    td,
    header
  } = setDefaults(options, DEFAULTS_TABLE);
  return {
    type: header ? th.type : td.type,
    children: [{
      text: ''
    }]
  };
};

const getEmptyRowNode = (colCount, options) => {
  const {
    tr
  } = setDefaults(options, DEFAULTS_TABLE);
  return {
    type: tr.type,
    children: Array(colCount).fill(colCount).map(() => getEmptyCellNode(options))
  };
};

const getEmptyTableNode = options => {
  const {
    table
  } = setDefaults(options, DEFAULTS_TABLE);
  return {
    type: table.type,
    children: [getEmptyRowNode(2, options), getEmptyRowNode(2, options)]
  };
};

const addColumn = (editor, options) => {
  const {
    table,
    td,
    th
  } = setDefaults(options, DEFAULTS_TABLE);

  if (isNodeTypeIn(editor, table.type)) {
    const currentCellItem = getAboveByType(editor, [td.type, th.type]);
    const currentTableItem = getAboveByType(editor, table.type);

    if (currentCellItem && currentTableItem) {
      const nextCellPath = Path.next(currentCellItem[1]);
      const newCellPath = nextCellPath.slice();
      const replacePathPos = newCellPath.length - 2;
      const currentRowIdx = nextCellPath[replacePathPos];
      currentTableItem[0].children.forEach((row, rowIdx) => {
        newCellPath[replacePathPos] = rowIdx;
        Transforms.insertNodes(editor, getEmptyCellNode(Object.assign({}, options)), {
          at: newCellPath,
          select: rowIdx === currentRowIdx
        });
      });
    }
  }
};

const addRow = (editor, options) => {
  const {
    table,
    tr
  } = setDefaults(options, DEFAULTS_TABLE);

  if (isNodeTypeIn(editor, table.type)) {
    const currentRowItem = getAboveByType(editor, tr.type);

    if (currentRowItem) {
      const [currentRowElem, currentRowPath] = currentRowItem;
      Transforms.insertNodes(editor, getEmptyRowNode(currentRowElem.children.length, options), {
        at: Path.next(currentRowPath),
        select: true
      });
    }
  }
};

const deleteColumn = (editor, options) => {
  const {
    table,
    tr,
    td,
    th
  } = setDefaults(options, DEFAULTS_TABLE);

  if (isNodeTypeIn(editor, table.type)) {
    const currentCellItem = getAboveByType(editor, [td.type, th.type]);
    const currentRowItem = getAboveByType(editor, tr.type);
    const currentTableItem = getAboveByType(editor, table.type);

    if (currentCellItem && currentRowItem && currentTableItem && // Cannot delete the last cell
    currentRowItem[0].children.length > 1) {
      const currentCellPath = currentCellItem[1];
      const pathToDelete = currentCellPath.slice();
      const replacePathPos = pathToDelete.length - 2;
      currentTableItem[0].children.forEach((row, rowIdx) => {
        pathToDelete[replacePathPos] = rowIdx;
        Transforms.removeNodes(editor, {
          at: pathToDelete
        });
      });
    }
  }
};

const deleteRow = (editor, options) => {
  const {
    table,
    tr
  } = setDefaults(options, DEFAULTS_TABLE);

  if (isNodeTypeIn(editor, table.type)) {
    const currentTableItem = getAboveByType(editor, table.type);
    const currentRowItem = getAboveByType(editor, tr.type);

    if (currentRowItem && currentTableItem && // Cannot delete the last row
    currentTableItem[0].children.length > 1) {
      Transforms.removeNodes(editor, {
        at: currentRowItem[1]
      });
    }
  }
};

const deleteTable = (editor, options) => {
  const {
    table
  } = setDefaults(options, DEFAULTS_TABLE);

  if (isNodeTypeIn(editor, table.type)) {
    const tableItem = getAboveByType(editor, table.type);

    if (tableItem) {
      Transforms.removeNodes(editor, {
        at: tableItem[1]
      });
    }
  }
};

const insertTable = (editor, options) => {
  const {
    table
  } = setDefaults(options, DEFAULTS_TABLE);

  if (!isNodeTypeIn(editor, table.type)) {
    Transforms.insertNodes(editor, getEmptyTableNode(options));
  }
};

const withTable = options => editor => {
  const {
    td,
    th
  } = setDefaults(options, DEFAULTS_TABLE);
  const {
    deleteBackward,
    deleteForward
  } = editor;

  const preventDeleteCell = (operation, pointCallback) => unit => {
    const {
      selection
    } = editor;

    if (isCollapsed(selection)) {
      const [cell] = Editor.nodes(editor, {
        match: n => n.type === td.type || n.type === th.type
      });

      if (cell) {
        const [, cellPath] = cell;
        const start = pointCallback(editor, cellPath);

        if (selection && Point.equals(selection.anchor, start)) {
          return;
        }
      }
    }

    operation(unit);
  }; // prevent deleting cells with deleteBackward


  editor.deleteBackward = preventDeleteCell(deleteBackward, Editor.start); // prevent deleting cells with deleteForward

  editor.deleteForward = preventDeleteCell(deleteForward, Editor.end);
  return editor;
};

const getMediaEmbedElementStyles = ({
  className
}) => {
  return {
    root: [{
      // Insert css properties
      position: 'relative'
    }, className],
    iframeWrapper: {
      padding: '75% 0 0 0',
      position: 'relative'
    },
    iframe: {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%'
    },
    input: {
      fontSize: '0.85em',
      width: '100%',
      padding: '0.5em',
      border: '2px solid #ddd',
      background: '#fafafa',
      marginTop: '5px'
    }
  };
};

const MediaEmbedUrlInput = _a => {
  var {
    className,
    url,
    onChange
  } = _a,
      props = __rest(_a, ["className", "url", "onChange"]);

  const [value, setValue] = useState(url);
  return /*#__PURE__*/createElement("input", Object.assign({
    className: className,
    value: value,
    onClick: e => e.stopPropagation(),
    onChange: e => {
      const newUrl = e.target.value;
      setValue(newUrl);
      onChange(newUrl);
    }
  }, props));
};

const getClassNames$f = classNamesFunction();
/**
 * MediaEmbedElement with no default styles.
 * [Use the `styles` API to add your own styles.](https://github.com/OfficeDev/office-ui-fabric-react/wiki/Component-Styling)
 */

const MediaEmbedElementBase = ({
  attributes,
  children,
  element,
  className,
  styles
}) => {
  const classNames = getClassNames$f(styles, {
    className
  });
  const editor = useEditor();
  const {
    url
  } = element;
  return /*#__PURE__*/createElement("div", Object.assign({}, attributes, {
    className: classNames.root
  }), /*#__PURE__*/createElement("div", {
    contentEditable: false
  }, /*#__PURE__*/createElement("div", {
    className: classNames.iframeWrapper
  }, /*#__PURE__*/createElement("iframe", {
    className: classNames.iframe,
    title: "embed",
    src: `${url}?title=0&byline=0&portrait=0`,
    frameBorder: "0"
  })), /*#__PURE__*/createElement(MediaEmbedUrlInput, {
    "data-testid": "MediaEmbedUrlInput",
    className: classNames.input,
    url: url,
    onChange: val => {
      const path = ReactEditor.findPath(editor, element);
      Transforms.setNodes(editor, {
        url: val
      }, {
        at: path
      });
    }
  })), children);
};
/**
 * MediaEmbedElement
 */

const MediaEmbedElement = styled$1(MediaEmbedElementBase, getMediaEmbedElementStyles, undefined, {
  scope: 'MediaEmbedElement'
});

const ELEMENT_MEDIA_EMBED = 'media_embed';
const DEFAULTS_MEDIA_EMBED = {
  media_embed: {
    component: MediaEmbedElement,
    type: ELEMENT_MEDIA_EMBED,
    rootProps: {
      className: 'slate-media-embed'
    }
  }
};

const deserializeIframe = options => {
  const {
    media_embed
  } = setDefaults(options, DEFAULTS_MEDIA_EMBED);
  return {
    element: getNodeDeserializer({
      type: media_embed.type,
      node: el => {
        let url = el.getAttribute('src');

        if (url) {
          [url] = url.split('?');
          return {
            type: media_embed.type,
            url
          };
        }
      },
      rules: [{
        nodeNames: 'IFRAME'
      }, {
        className: media_embed.rootProps.className
      }]
    })
  };
};

const renderElementMediaEmbed = options => {
  const {
    media_embed
  } = setDefaults(options, DEFAULTS_MEDIA_EMBED);
  return getRenderElement(media_embed);
};

/**
 * Enables support for embeddable media such as YouTube
 * or Vimeo videos, Instagram posts and tweets or Google Maps.
 */

const MediaEmbedPlugin = options => {
  const {
    media_embed
  } = setDefaults(options, DEFAULTS_MEDIA_EMBED);
  return {
    renderElement: renderElementMediaEmbed(options),
    deserialize: deserializeIframe(options),
    voidTypes: [media_embed.type]
  };
};

const ELEMENT_ALIGN_LEFT = 'align_left';
const ELEMENT_ALIGN_CENTER = 'align_center';
const ELEMENT_ALIGN_RIGHT = 'align_right';
const DEFAULTS_ALIGN = {
  align_left: {
    component: StyledElement,
    type: ELEMENT_ALIGN_LEFT,
    rootProps: {
      className: 'slate-align-left',
      styles: {
        root: {
          textAlign: 'left'
        }
      }
    }
  },
  align_center: {
    component: StyledElement,
    type: ELEMENT_ALIGN_CENTER,
    rootProps: {
      className: 'slate-align-center',
      styles: {
        root: {
          textAlign: 'center'
        }
      }
    }
  },
  align_right: {
    component: StyledElement,
    type: ELEMENT_ALIGN_RIGHT,
    rootProps: {
      className: 'slate-align-right',
      styles: {
        root: {
          textAlign: 'right'
        }
      }
    }
  }
};

const upsertAlign = (editor, {
  type,
  unwrapTypes = [ELEMENT_ALIGN_LEFT, ELEMENT_ALIGN_CENTER, ELEMENT_ALIGN_RIGHT]
}) => {
  if (!editor.selection) return;
  unwrapNodesByType(editor, unwrapTypes);
  if (!type) return;
  wrapNodes(editor, {
    type,
    children: []
  }, {
    mode: 'lowest'
  });
};

const ToolbarAlign = _a => {
  var {
    type,
    unwrapTypes = [ELEMENT_ALIGN_LEFT, ELEMENT_ALIGN_CENTER, ELEMENT_ALIGN_RIGHT]
  } = _a,
      props = __rest(_a, ["type", "unwrapTypes"]);

  const editor = useSlate();
  return /*#__PURE__*/createElement(ToolbarButton, Object.assign({
    active: !!type && isNodeTypeIn(editor, type),
    onMouseDown: getPreventDefaultHandler(upsertAlign, editor, {
      type,
      unwrapTypes
    })
  }, props));
};

const deserializeAlign = options => {
  const {
    align_center,
    align_right
  } = setDefaults(options, DEFAULTS_ALIGN);
  return {
    element: [...getElementDeserializer({
      type: align_center.type,
      rules: [{
        className: align_center.rootProps.className
      }, {
        nodeNames: 'DIV',
        style: {
          textAlign: 'center'
        }
      }]
    }), ...getElementDeserializer({
      type: align_right.type,
      rules: [{
        className: align_right.rootProps.className
      }, {
        nodeNames: 'DIV',
        style: {
          textAlign: 'right'
        }
      }]
    })]
  };
};

const renderElementAlign = options => {
  const {
    align_left,
    align_center,
    align_right
  } = setDefaults(options, DEFAULTS_ALIGN);
  return getRenderElements([Object.assign({}, align_left), align_center, align_right]);
};

/**
 * Enables support for text alignment, useful to align your content
 * to left, right and center it.
 */

const AlignPlugin = options => ({
  renderElement: renderElementAlign(options),
  deserialize: deserializeAlign(options)
});

const autoformatBlock = (editor, type, at, {
  preFormat,
  format
}) => {
  Transforms.delete(editor, {
    at
  });
  preFormat === null || preFormat === void 0 ? void 0 : preFormat(editor);

  if (!format) {
    Transforms.setNodes(editor, {
      type
    }, {
      match: n => Editor.isBlock(editor, n)
    });
  } else {
    format(editor);
  }
};

const autoformatInline = (editor, {
  type,
  between,
  markup,
  ignoreTrim
}) => {
  const selection = editor.selection;
  const startMarkup = between ? between[0] : markup;
  const endMarkup = between ? between[1] : '';
  let endMarkupPointBefore = selection.anchor;

  if (endMarkup) {
    endMarkupPointBefore = getPointBefore(editor, selection, {
      matchString: endMarkup
    });
    if (!endMarkupPointBefore) return false;
  }

  const startMarkupPointAfter = getPointBefore(editor, endMarkupPointBefore, {
    matchString: startMarkup,
    skipInvalid: true,
    afterMatch: true
  });
  if (!startMarkupPointAfter) return false; // found

  const markupRange = {
    anchor: startMarkupPointAfter,
    focus: endMarkupPointBefore
  };

  if (!ignoreTrim) {
    const markupText = getText(editor, markupRange);
    if (markupText.trim() !== markupText) return false;
  } // delete end markup


  if (endMarkup) {
    endMarkupPointBefore = getPointBefore(editor, selection, {
      matchString: endMarkup
    });
    Transforms.delete(editor, {
      at: {
        anchor: endMarkupPointBefore,
        focus: selection.anchor
      }
    });
  } // add mark to the text between the markups


  Transforms.select(editor, markupRange);
  editor.addMark(type, true);
  Transforms.collapse(editor, {
    edge: 'end'
  });
  editor.removeMark(type, false); // delete start markup

  const startMarkupPointBefore = getPointBefore(editor, selection, {
    matchString: startMarkup,
    skipInvalid: true
  });
  Transforms.delete(editor, {
    at: {
      anchor: startMarkupPointBefore,
      focus: startMarkupPointAfter
    }
  });
  return true;
};

/**
 * Autoformat in the middle of a block
 */

const autoformatInlineBlock = (editor, {
  type,
  markup,
  preFormat,
  format
}) => {
  const markupRange = getRangeBefore(editor, editor.selection, {
    matchString: markup,
    skipInvalid: true
  });

  if (markupRange) {
    autoformatBlock(editor, type, markupRange, {
      preFormat: () => {
        editor.insertBreak();
        preFormat === null || preFormat === void 0 ? void 0 : preFormat(editor);
      },
      format
    });
    return true;
  }
};

/**
 * Enables support for autoformatting actions.
 * Once a markup rule is validated, it does not check the following rules.
 */

const withAutoformat = ({
  rules
}) => editor => {
  const {
    insertText
  } = editor;

  editor.insertText = text => {
    if (!isCollapsed(editor.selection)) return insertText(text);

    for (const {
      trigger = ' ',
      type,
      markup,
      preFormat,
      format,
      mode,
      between,
      ignoreTrim,
      insertTrigger
    } of rules) {
      const triggers = castArray_1(trigger); // Check trigger

      if (!triggers.includes(text)) continue;
      const markups = castArray_1(markup);
      const rangeFromBlockStart = getRangeFromBlockStart(editor);
      const textFromBlockStart = getText(editor, rangeFromBlockStart);

      const valid = () => insertTrigger && insertText(text);

      if (markups.includes(textFromBlockStart)) {
        // Start of the block
        autoformatBlock(editor, type, rangeFromBlockStart, {
          preFormat,
          format
        });
        return valid();
      }

      if (mode === 'inline-block') {
        if (autoformatInlineBlock(editor, {
          preFormat,
          markup,
          format,
          type
        })) {
          return valid();
        }
      }

      if (mode === 'inline') {
        if (autoformatInline(editor, {
          type,
          between,
          ignoreTrim,
          markup: Array.isArray(markup) ? markup[0] : markup
        })) {
          return valid();
        }
      }
    }

    insertText(text);
  };

  return editor;
};

/**
 * Check if the selection is at the edge of its parent block.
 * If it is and if the selection is expanded, delete its content.
 */

const exitBreakAtEdges = (editor, {
  start,
  end
}) => {
  let queryEdge = false;
  let isEdge = false;
  let isStart = false;

  if (start || end) {
    queryEdge = true;

    if (start && isSelectionAtBlockStart(editor)) {
      isEdge = true;
      isStart = true;
    }

    if (end && isSelectionAtBlockEnd(editor)) {
      isEdge = true;
    }

    if (isEdge && isExpanded(editor.selection)) {
      editor.deleteFragment();
    }
  }

  return {
    queryEdge,
    isEdge,
    isStart
  };
};
const onKeyDownExitBreak = ({
  rules = [{
    hotkey: 'mod+enter'
  }, {
    hotkey: 'mod+shift+enter',
    before: true
  }]
} = {}) => (event, editor) => {
  const entry = getBlockAbove(editor);
  rules.forEach(_a => {
    var {
      hotkey
    } = _a,
        _b = _a.query,
        _c = _b === void 0 ? {} : _b,
        {
      start,
      end
    } = _c,
        query = __rest(_c, ["start", "end"]),
        {
      level = 1,
      before,
      defaultType = DEFAULT_ELEMENT
    } = _a;

    if (isHotkey(hotkey, event) && isNodeType(entry, query)) {
      if (!editor.selection) return;
      const {
        queryEdge,
        isEdge,
        isStart
      } = exitBreakAtEdges(editor, {
        start,
        end
      });
      if (isStart) before = true;
      if (queryEdge && !isEdge) return;
      event.preventDefault();
      const selectionPath = Editor.path(editor, editor.selection);
      let insertPath;

      if (before) {
        insertPath = selectionPath.slice(0, level + 1);
      } else {
        insertPath = Path.next(selectionPath.slice(0, level + 1));
      }

      Transforms.insertNodes(editor, {
        type: defaultType,
        children: [{
          text: ''
        }]
      }, {
        at: insertPath,
        select: !isStart
      });
    }
  });
};

/**
 * Insert soft break following configurable rules.
 * Each rule specifies a hotkey and query options.
 */

const ExitBreakPlugin = (options = {}) => ({
  onKeyDown: onKeyDownExitBreak(options)
});

/**
 * Enables support for resetting block type from rules.
 */

const ResetBlockTypePlugin = options => ({
  onKeyDown: onKeyDownResetBlockType(options)
});

const onKeyDownSoftBreak = ({
  rules = [{
    hotkey: 'shift+enter'
  }]
} = {}) => (event, editor) => {
  const entry = getBlockAbove(editor);
  rules.forEach(({
    hotkey,
    query
  }) => {
    if (isHotkey(hotkey, event) && isNodeType(entry, query)) {
      event.preventDefault();
      editor.insertText('\n');
    }
  });
};

/**
 * Insert soft break following configurable rules.
 * Each rule specifies a hotkey and query options.
 */

const SoftBreakPlugin = (options = {}) => ({
  onKeyDown: onKeyDownSoftBreak(options)
});

const MARK_BOLD = 'bold';
const DEFAULTS_BOLD = {
  bold: {
    component: StyledLeaf,
    type: MARK_BOLD,
    hotkey: 'mod+b',
    rootProps: {
      className: `slate-bold`,
      as: 'strong'
    }
  }
};

const deserializeBold = options => {
  const {
    bold
  } = setDefaults(options, DEFAULTS_BOLD);
  return {
    leaf: getLeafDeserializer({
      type: bold.type,
      rules: [{
        nodeNames: ['STRONG']
      }, {
        style: {
          fontWeight: ['600', '700', 'bold']
        }
      }]
    })
  };
};

const renderLeafBold = options => getRenderLeafDefault({
  key: 'bold',
  defaultOptions: DEFAULTS_BOLD,
  options
});

/**
 * Enables support for bold formatting
 */

const BoldPlugin = options => ({
  renderLeaf: renderLeafBold(options),
  deserialize: deserializeBold(options),
  onKeyDown: onKeyDownMarkDefault({
    key: 'bold',
    defaultOptions: DEFAULTS_BOLD,
    options
  })
});

const MARK_CODE = 'code';
const DEFAULTS_CODE = {
  code: {
    component: StyledLeaf,
    type: MARK_CODE,
    hotkey: 'mod+e',
    rootProps: {
      className: `slate-code`,
      as: 'code',
      styles: {
        root: {
          whiteSpace: 'pre-wrap',
          fontSize: '85%',
          fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, Courier, monospace;',
          backgroundColor: 'rgba(135,131,120,0.15)',
          borderRadius: '3px',
          padding: '0.2em 0.4em',
          lineHeight: 'normal'
        }
      }
    }
  }
};

const deserializeCode = options => {
  const {
    code
  } = setDefaults(options, DEFAULTS_CODE);
  return {
    leaf: getLeafDeserializer({
      type: code.type,
      rules: [{
        nodeNames: ['CODE']
      }, {
        style: {
          wordWrap: 'break-word'
        }
      }]
    })
  };
};

const renderLeafCode = options => getRenderLeafDefault({
  key: 'code',
  defaultOptions: DEFAULTS_CODE,
  options
});

/**
 * Enables support for code formatting
 */

const CodePlugin = options => ({
  renderLeaf: renderLeafCode(options),
  deserialize: deserializeCode(options),
  onKeyDown: onKeyDownMarkDefault({
    key: 'code',
    defaultOptions: DEFAULTS_CODE,
    options
  })
});

const MARK_ITALIC = 'italic';
const DEFAULTS_ITALIC = {
  italic: {
    component: StyledLeaf,
    type: MARK_ITALIC,
    hotkey: 'mod+i',
    rootProps: {
      className: `slate-italic`,
      as: 'em'
    }
  }
};

const deserializeItalic = options => {
  const {
    italic
  } = setDefaults(options, DEFAULTS_ITALIC);
  return {
    leaf: getLeafDeserializer({
      type: italic.type,
      rules: [{
        nodeNames: ['EM', 'I']
      }, {
        style: {
          fontStyle: 'italic'
        }
      }]
    })
  };
};

const renderLeafItalic = options => getRenderLeafDefault({
  key: 'italic',
  defaultOptions: DEFAULTS_ITALIC,
  options
});

/**
 * Enables support for italic formatting.
 */

const ItalicPlugin = options => ({
  renderLeaf: renderLeafItalic(options),
  deserialize: deserializeItalic(options),
  onKeyDown: onKeyDownMarkDefault({
    key: 'italic',
    defaultOptions: DEFAULTS_ITALIC,
    options
  })
});

const MARK_STRIKETHROUGH = 'strikethrough';
const DEFAULTS_STRIKETHROUGH = {
  strikethrough: {
    component: StyledLeaf,
    type: MARK_STRIKETHROUGH,
    hotkey: 'mod+shift+s',
    rootProps: {
      className: `slate-strikethrough`,
      as: 's'
    }
  }
};

const deserializeStrikethrough = options => {
  const {
    strikethrough
  } = setDefaults(options, DEFAULTS_STRIKETHROUGH);
  return {
    leaf: getLeafDeserializer({
      type: strikethrough.type,
      rules: [{
        nodeNames: ['S', 'DEL', 'STRIKE']
      }, {
        style: {
          textDecoration: 'line-through'
        }
      }]
    })
  };
};

const renderLeafStrikethrough = options => getRenderLeafDefault({
  key: 'strikethrough',
  defaultOptions: DEFAULTS_STRIKETHROUGH,
  options
});

/**
 * Enables support for strikethrough formatting.
 */

const StrikethroughPlugin = options => ({
  renderLeaf: renderLeafStrikethrough(options),
  deserialize: deserializeStrikethrough(options),
  onKeyDown: onKeyDownMarkDefault({
    key: 'strikethrough',
    defaultOptions: DEFAULTS_STRIKETHROUGH,
    options
  })
});

const MARK_SUPERSCRIPT = 'superscript';
const MARK_SUBSCRIPT = 'subscript';
const DEFAULTS_SUBSUPSCRIPT = {
  subscript: {
    component: StyledLeaf,
    type: MARK_SUBSCRIPT,
    hotkey: 'mod+,',
    clear: MARK_SUPERSCRIPT,
    rootProps: {
      className: `slate-subscript`,
      as: 'sub'
    }
  },
  superscript: {
    component: StyledLeaf,
    type: MARK_SUPERSCRIPT,
    hotkey: 'mod+.',
    clear: MARK_SUBSCRIPT,
    rootProps: {
      className: `slate-superscript`,
      as: 'sup'
    }
  }
};

const deserializeSubscript = options => {
  const {
    subscript
  } = setDefaults(options, DEFAULTS_SUBSUPSCRIPT);
  return {
    leaf: getLeafDeserializer({
      type: subscript.type,
      rules: [{
        nodeNames: ['SUB']
      }, {
        style: {
          verticalAlign: 'sub'
        }
      }]
    })
  };
};

const renderLeafSubscript = options => getRenderLeafDefault({
  key: 'subscript',
  defaultOptions: DEFAULTS_SUBSUPSCRIPT,
  options
});

/**
 * Enables support for subscript formatting.
 */

const SubscriptPlugin = options => ({
  renderLeaf: renderLeafSubscript(options),
  deserialize: deserializeSubscript(options),
  onKeyDown: onKeyDownMarkDefault({
    key: 'subscript',
    defaultOptions: DEFAULTS_SUBSUPSCRIPT,
    options
  })
});

const deserializeSuperscript = options => {
  const {
    superscript
  } = setDefaults(options, DEFAULTS_SUBSUPSCRIPT);
  return {
    leaf: getLeafDeserializer({
      type: superscript.type,
      rules: [{
        nodeNames: ['SUP']
      }, {
        style: {
          verticalAlign: 'super'
        }
      }]
    })
  };
};

const renderLeafSuperscript = options => getRenderLeafDefault({
  key: 'superscript',
  defaultOptions: DEFAULTS_SUBSUPSCRIPT,
  options
});

/**
 * Enables support for superscript formatting.
 */

const SuperscriptPlugin = options => ({
  renderLeaf: renderLeafSuperscript(options),
  deserialize: deserializeSuperscript(options),
  onKeyDown: onKeyDownMarkDefault({
    key: 'superscript',
    defaultOptions: DEFAULTS_SUBSUPSCRIPT,
    options
  })
});

const MARK_UNDERLINE = 'underline';
const DEFAULTS_UNDERLINE = {
  underline: {
    component: StyledLeaf,
    type: MARK_UNDERLINE,
    hotkey: 'mod+u',
    rootProps: {
      className: `slate-underline`,
      as: 'u'
    }
  }
};

const deserializeUnderline = options => {
  const {
    underline
  } = setDefaults(options, DEFAULTS_UNDERLINE);
  return {
    leaf: getLeafDeserializer({
      type: underline.type,
      rules: [{
        nodeNames: ['U']
      }, {
        style: {
          textDecoration: 'underline'
        }
      }]
    })
  };
};

const renderLeafUnderline = options => getRenderLeafDefault({
  key: 'underline',
  defaultOptions: DEFAULTS_UNDERLINE,
  options
});

/**
 * Enables support for underline formatting.
 */

const UnderlinePlugin = options => ({
  renderLeaf: renderLeafUnderline(options),
  deserialize: deserializeUnderline(options),
  onKeyDown: onKeyDownMarkDefault({
    key: 'underline',
    defaultOptions: DEFAULTS_UNDERLINE,
    options
  })
});

/**
 * Enables support for basic marks:
 * - Bold
 * - Code
 * - Italic
 * - Strikethrough
 * - Subscript
 * - Superscript
 * - Underline
 */

const BasicMarkPlugins = options => [BoldPlugin(options), CodePlugin(options), ItalicPlugin(options), StrikethroughPlugin(options), SubscriptPlugin(options), SuperscriptPlugin(options), UnderlinePlugin(options)];

const MARK_HIGHLIGHT = 'highlight';
const DEFAULTS_HIGHLIGHT = {
  highlight: {
    component: StyledLeaf,
    type: MARK_HIGHLIGHT,
    hotkey: 'mod+shift+h',
    rootProps: {
      className: 'slate-highlight',
      as: 'mark',
      styles: {
        root: {
          backgroundColor: '#FEF3B7'
        }
      }
    }
  }
};

const deserializeHighlight = options => {
  const {
    highlight
  } = setDefaults(options, DEFAULTS_HIGHLIGHT);
  return {
    leaf: getLeafDeserializer({
      type: highlight.type,
      rules: [{
        nodeNames: ['MARK']
      }]
    })
  };
};

const renderLeafHighlight = options => getRenderLeafDefault({
  key: 'highlight',
  defaultOptions: DEFAULTS_HIGHLIGHT,
  options
});

/**
 * Enables support for highlights, useful when reviewing
 * content or highlighting it for future reference.
 */

const HighlightPlugin = options => ({
  renderLeaf: renderLeafHighlight(options),
  deserialize: deserializeHighlight(options),
  onKeyDown: onKeyDownMarkDefault({
    key: 'highlight',
    defaultOptions: DEFAULTS_HIGHLIGHT,
    options
  })
});

const MARK_KBD = 'kbd';
const DEFAULTS_KBD = {
  kbd: {
    component: StyledLeaf,
    type: MARK_KBD,
    // hotkey: "mod+e", TODO: What hotkey for keyboard shortcut?
    rootProps: {
      className: `slate-kbd`,
      as: 'kbd',
      styles: {
        root: {
          whiteSpace: 'pre-wrap',
          fontSize: '75%',
          fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, Courier, monospace;',
          backgroundColor: 'white',
          border: '1px solid black',
          borderRadius: '3px',
          padding: '0.2em 0.4em',
          marginRight: '0.2em',
          lineHeight: 'normal',
          boxShadow: '2px 2px 3px 0px rgba(0,0,0,0.75)'
        }
      }
    }
  }
};

const deserializeKbd = options => {
  const {
    kbd
  } = setDefaults(options, DEFAULTS_KBD);
  return {
    leaf: getLeafDeserializer({
      type: kbd.type,
      rules: [{
        nodeNames: ['KBD']
      }, {
        style: {
          wordWrap: 'break-word'
        }
      }]
    })
  };
};

const renderLeafKbd = options => getRenderLeafDefault({
  key: 'kbd',
  defaultOptions: DEFAULTS_KBD,
  options
});

/**
 * Enables support for code formatting
 */

const KbdPlugin = options => ({
  renderLeaf: renderLeafKbd(options),
  deserialize: deserializeKbd(options),
  onKeyDown: onKeyDownMarkDefault({
    key: 'kbd',
    defaultOptions: DEFAULTS_KBD,
    options
  })
});

const withMarks = () => editor => {
  const e = editor;

  e.removeMark = (key, shouldChange = true) => {
    const {
      selection
    } = editor;

    if (selection) {
      if (Range.isExpanded(selection)) {
        Transforms.unsetNodes(editor, key, {
          match: Text.isText,
          split: true
        });
      } else {
        const marks = Object.assign({}, Editor.marks(editor) || {});
        delete marks[key];
        editor.marks = marks;
        shouldChange && editor.onChange();
      }
    }
  };

  return e;
};

const withNormalizeTypes = ({
  rules
}) => editor => {
  const {
    normalizeNode
  } = editor;

  editor.normalizeNode = ([currentNode, currentPath]) => {
    if (!currentPath.length) {
      rules.forEach(({
        strictType,
        type,
        path
      }) => {
        const node = getNode(editor, path);

        if (node) {
          if (strictType && node.type !== strictType) {
            Transforms.setNodes(editor, {
              type: strictType
            }, {
              at: path
            });
          }
        } else {
          editor.insertNodes({
            type: strictType !== null && strictType !== void 0 ? strictType : type,
            children: [{
              text: ''
            }]
          }, {
            at: path
          });
        }
      });
    }

    return normalizeNode([currentNode, currentPath]);
  };

  return editor;
};

/**
 * Add a trailing block when the last node type is not `type`
 */

const withTrailingNode = (_a = {}) => {
  var {
    type = ELEMENT_PARAGRAPH,
    level = 1
  } = _a,
      query = __rest(_a, ["type", "level"]);

  return editor => {
    const {
      normalizeNode
    } = editor;

    editor.normalizeNode = ([currentNode, currentPath]) => {
      if (!currentPath.length) {
        const entry = getLastNode(editor, level);
        const [lastNode, lastPath] = entry;

        if (lastNode.type !== type && isNodeType(entry, query)) {
          editor.insertNodes({
            type,
            children: [{
              text: ''
            }]
          }, {
            at: Path.next(lastPath)
          });
        }
      }

      return normalizeNode([currentNode, currentPath]);
    };

    return editor;
  };
};

const ToolbarSearchHighlight = ({
  icon: Icon,
  setSearch
}) => /*#__PURE__*/createElement(HeadingToolbar, {
  styles: {
    root: {
      height: '38px'
    }
  }
}, /*#__PURE__*/createElement("div", {
  style: {
    position: 'relative',
    paddingBottom: '10px',
    marginBottom: '10px'
  }
}, /*#__PURE__*/createElement(Icon, {
  size: 18,
  style: {
    position: 'absolute',
    top: '0.5em',
    left: '0.5em',
    color: '#ccc'
  }
}), /*#__PURE__*/createElement("input", {
  "data-testid": "ToolbarSearchHighlightInput",
  type: "search",
  placeholder: "Search the text...",
  onChange: e => setSearch(e.target.value),
  style: {
    boxSizing: 'border-box',
    fontSize: '0.85em',
    width: '100%',
    padding: '0.5em',
    paddingLeft: '2em',
    border: '2px solid #ddd',
    background: '#fafafa'
  }
})));

const MARK_SEARCH_HIGHLIGHT = 'search_highlight';
const DEFAULTS_SEARCH_HIGHLIGHT = {
  search_highlight: {
    type: MARK_SEARCH_HIGHLIGHT,
    rootProps: {
      as: 'span',
      className: 'slate-search-highlight',
      styles: {
        root: {
          backgroundColor: '#fff59d'
        }
      }
    }
  }
};

const decorateSearchHighlight = options => ([node, path]) => {
  const {
    search_highlight,
    search
  } = setDefaults(options, DEFAULTS_SEARCH_HIGHLIGHT);
  const ranges = [];

  if (search && Text.isText(node)) {
    const {
      text
    } = node;
    const parts = text.split(search);
    let offset = 0;
    parts.forEach((part, i) => {
      if (i !== 0) {
        ranges.push({
          anchor: {
            path,
            offset: offset - search.length
          },
          focus: {
            path,
            offset
          },
          [search_highlight.type]: true
        });
      }

      offset = offset + part.length + search.length;
    });
  }

  return ranges;
};

const SearchHighlightPlugin = options => {
  const {
    search_highlight
  } = setDefaults(options, DEFAULTS_SEARCH_HIGHLIGHT);
  return Object.assign({}, HighlightPlugin({
    highlight: search_highlight
  }));
};

export { AlignPlugin, BalloonToolbar, BasicElementPlugins, BasicMarkPlugins, BlockquoteElement, BlockquoteElementBase, BlockquotePlugin, BoldPlugin, CLASS_TODO_LIST, CLASS_TODO_LIST_CHECKED, CODE_BLOCK_LANGUAGES, CodeBlockElement, CodeBlockElementBase, CodeBlockPlugin, CodePlugin, DEFAULTS_ALIGN, DEFAULTS_BLOCKQUOTE, DEFAULTS_BOLD, DEFAULTS_CODE, DEFAULTS_CODE_BLOCK, DEFAULTS_HEADING, DEFAULTS_HIGHLIGHT, DEFAULTS_IMAGE, DEFAULTS_ITALIC, DEFAULTS_KBD, DEFAULTS_LINK, DEFAULTS_LIST, DEFAULTS_MEDIA_EMBED, DEFAULTS_MENTION, DEFAULTS_PARAGRAPH, DEFAULTS_SEARCH_HIGHLIGHT, DEFAULTS_STRIKETHROUGH, DEFAULTS_SUBSUPSCRIPT, DEFAULTS_TABLE, DEFAULTS_TODO_LIST, DEFAULTS_UNDERLINE, DEFAULT_ELEMENT, ELEMENT_ALIGN_CENTER, ELEMENT_ALIGN_LEFT, ELEMENT_ALIGN_RIGHT, ELEMENT_BLOCKQUOTE, ELEMENT_CODE_BLOCK, ELEMENT_H1, ELEMENT_H2, ELEMENT_H3, ELEMENT_H4, ELEMENT_H5, ELEMENT_H6, ELEMENT_IMAGE, ELEMENT_LI, ELEMENT_LINK, ELEMENT_MEDIA_EMBED, ELEMENT_MENTION, ELEMENT_OL, ELEMENT_PARAGRAPH, ELEMENT_TABLE, ELEMENT_TD, ELEMENT_TH, ELEMENT_TODO_LI, ELEMENT_TR, ELEMENT_UL, ExitBreakPlugin, HeadingPlugin, HeadingToolbar, HighlightPlugin, ImageElement, ImageElementBase, ImagePlugin, ItalicPlugin, KbdPlugin, LinkElement, LinkElementBase, LinkPlugin, ListHotkey, ListPlugin, MARK_BOLD, MARK_CODE, MARK_HIGHLIGHT, MARK_ITALIC, MARK_KBD, MARK_PRISM, MARK_SEARCH_HIGHLIGHT, MARK_STRIKETHROUGH, MARK_SUBSCRIPT, MARK_SUPERSCRIPT, MARK_UNDERLINE, MediaEmbedElement, MediaEmbedElementBase, MediaEmbedPlugin, MentionElement, MentionElementBase, MentionPlugin, MentionSelect, MentionSelectBase, ParagraphPlugin, PortalBody, PreviewLeaf, PreviewLeafBase, PreviewPlugin, ResetBlockTypePlugin, SearchHighlightPlugin, Selectable, SoftBreakPlugin, StrikethroughPlugin, StyledComponent, StyledComponentBase, StyledElement, StyledElementBase, StyledLeaf, StyledLeafBase, SubscriptPlugin, SuperscriptPlugin, TableElement, TableElementBase, TablePlugin, TodoListElement, TodoListElementBase, TodoListPlugin, Toolbar, ToolbarAlign, ToolbarBase, ToolbarButton, ToolbarButtonBase, ToolbarElement, ToolbarImage, ToolbarLink, ToolbarList, ToolbarMark, ToolbarSearchHighlight, ToolbarTable, UnderlinePlugin, addColumn, addRow, applyDeepToNodes, autoformatBlock, autoformatInline, autoformatInlineBlock, createDocumentNode, createNode, decorateCodeBlock, decoratePreview, decorateSearchHighlight, defaultsDeepToNodes, deleteColumn, deleteRow, deleteTable, deserializeAlign, deserializeBlockquote, deserializeBold, deserializeCode, deserializeCodeBlock, deserializeHTMLElement, deserializeHTMLToBreak, deserializeHTMLToDocument, deserializeHTMLToDocumentFragment, deserializeHTMLToElement, deserializeHTMLToFragment, deserializeHTMLToMarks, deserializeHTMLToText, deserializeHeading, deserializeHighlight, deserializeIframe, deserializeItalic, deserializeKbd, deserializeLink, deserializeList, deserializeMention, deserializeParagraph, deserializeStrikethrough, deserializeSubscript, deserializeSuperscript, deserializeTable, deserializeTodoList, deserializeUnderline, escapeRegExp, exitBreakAtEdges, filterBreaklines, focusBlockStartById, getAboveByType, getBalloonToolbarStyles, getBlockAbove, getBlockPathById, getBlockquoteElementStyles, getBlocks, getCodeBlockElementStyles, getElementDeserializer, getEmptyCellNode, getEmptyRowNode, getEmptyTableNode, getHandler, getHeadingToolbarStyles, getHoverDirection, getImageElementStyles, getLastNode, getLeafDeserializer, getLinkElementStyles, getMediaEmbedElementStyles, getMentionElementStyles, getMentionSelectStyles, getNewDirection, getNextIndex, getNextSiblingNodes, getNode, getNodeDeserializer, getNodes, getNodesById, getNodesByType, getNodesRange, getPointBefore, getPointFromLocation, getPreventDefaultHandler, getPreviewLeafStyles, getPreviousBlockById, getPreviousIndex, getRangeBefore, getRangeFromBlockStart, getRenderElement, getRenderElements, getRenderLeaf, getRenderLeafDefault, getSelectableElement, getSelectableStyles, getSelectionText, getStyledComponentStyles, getTableElementStyles, getText, getTodoListElementStyles, getToolbarButtonStyles, getToolbarStyles, grabberTooltipProps, htmlStringToDOMNode, insertImage, insertListItem, insertMention, insertTable, isAncestor, isAncestorEmpty, isBlockAboveEmpty, isBlockTextEmptyAfterSelection, isCollapsed, isDescendant, isEnd, isExpanded, isFirstChild, isImageUrl, isList, isMarkActive, isNodeType, isNodeTypeIn, isPointAtRoot, isPointAtWordEnd, isRangeAtRoot, isSelectionAtBlockEnd, isSelectionAtBlockStart, isSelectionExpanded, isSelectionInListItem, isStart, isTextByPath, isUrl, isWordAfterTrigger, mergeDeepToNodes, moveListItemDown, moveListItemUp, normalizeDescendantsToDocumentFragment, onImageLoad, onKeyDownExitBreak, onKeyDownList, onKeyDownMark, onKeyDownMarkDefault, onKeyDownResetBlockType, onKeyDownSoftBreak, parseMD, pipe, removeBlocksAndFocus, renderElementAlign, renderElementBlockquote, renderElementCodeBlock, renderElementHeading, renderElementImage, renderElementLink, renderElementList, renderElementMediaEmbed, renderElementMention, renderElementParagraph, renderElementTable, renderElementTodoList, renderLeafBold, renderLeafCode, renderLeafCodeBlock, renderLeafHighlight, renderLeafItalic, renderLeafKbd, renderLeafPreview, renderLeafStrikethrough, renderLeafSubscript, renderLeafSuperscript, renderLeafUnderline, selectBlockById, selectBlocksBySelectionOrId, selectEndOfBlockAboveSelection, serializeHTMLFromNodes, setDefaults, setPositionAtSelection, toggleList, toggleMark, toggleWrapNodes, unhangRange, unwrapList, unwrapNodesByType, upsertAlign, upsertLinkAtSelection, useBalloonMove, useBalloonShow, useDndBlock, useDragBlock, useDropBlockOnEditor, useMention, withAutoformat, withDeserializeHTML, withDeserializeMd, withImageUpload, withInlineVoid, withLink, withList, withMarks, withNodeID, withNormalizeTypes, withRemoveEmptyNodes, withTable, withToggleType, withTrailingNode, withTransforms, wrapLink, wrapNodes };
//# sourceMappingURL=index.es.js.map
